// Catalogue source canonique de l'interface ExpertLoop (anglais).
// Structuré par catégorie : les clés restent des chemins pointés stables
// (`common.save`), l'imbrication ne sert qu'à la lisibilité et à la recherche.
// Toute évolution de ce catalogue est détectée automatiquement par
// computeSourceVersion + computeCatalogDiff (retraduction incrémentale).
//
// `as const` est indispensable : il conserve les littéraux, dont MessageKey
// dérive l'union des clés valides. Sans lui, t() reprend n'importe quelle chaîne.
import { flattenMessages } from "./flatten.js";
export const I18N_SOURCE_LOCALE = "en";
export const I18N_FALLBACK_LOCALE = "en";
export const messages = {
    // ─── Génériques ─────────────────────────────────────────────────────────
    common: {
        uploadFailed: "The file could not be uploaded.",
        uploadRetry: "The upload did not finish. Please retry.",
        uploadProgress: "Uploading: {percent}%",
        save: "Save",
        cancel: "Cancel",
        close: "Close",
        confirm: "Confirm",
        submit: "Submit",
        delete: "Delete",
        back: "Back",
        send: "Send",
        retry: "Retry",
        remove: "Remove",
        loading: "Loading...",
        error: "Something went wrong. Please try again.",
        showMore: "Show more",
        showLess: "Show less",
        previous: "Previous",
        next: "Next",
        pageOf: "Page {page} of {totalPages}",
        dismiss: "Dismiss",
        all: "All",
        yes: "Yes",
        no: "No",
        decline: "Decline",
    },
    listOptions: {
        open: "List options",
        title: "List options",
        pageSize: "Rows per page",
        done: "Show results",
    },
    // ─── Erreurs génériques du client HTTP ──────────────────────────────────
    // Derniers recours : n'apparaissent que si le serveur répond en erreur sans
    // champ `error` exploitable.
    errors: {
        requestFailed: "Request failed",
        uploadFailed: "Upload failed",
    },
    // ─── Tailles de fichier ─────────────────────────────────────────────────
    // L'unité est traduisible : toutes les langues n'écrivent pas "B/KB/MB".
    fileSize: {
        bytes: "{size} B",
        kilobytes: "{size} KB",
        megabytes: "{size} MB",
    },
    // ─── Composants partagés (web) ──────────────────────────────────────────
    trust: {
        paymentHeld: "Payment held securely until expert delivers",
    },
    aiAssist: {
        hint: "Write in your language — AI can help rephrase your request",
        notOffered: "This expert prefers requests written in your own words — simply describe your need.",
        // État fermé de l'interrupteur : l'emplacement reste réservé et l'annonce
        // honnête. Le badge et ce libellé disparaissent quand l'assistance est ouverte,
        // et seulement alors.
        comingSoon: "Coming soon",
        try: "Try AI",
        trigger: "Help me phrase my request",
        regenerate: "Ask again",
        generating: "Preparing a suggestion...",
        panelTitle: "ExpertLoop suggestion",
        // Contrepartie d'interface de la règle qui gouverne toute la gate : aucun libellé
        // ne doit laisser entendre qu'une information a été vérifiée ou validée.
        panelNote: "A suggestion, not a check: nothing here has been verified.",
        editHint: "You can edit this text before and after using it.",
        use: "Use this version",
        dismiss: "Dismiss",
        restore: "Back to my text",
        accepted: "Added to your request. Nothing has been sent yet.",
        yourDraft: "Your request",
        // Signal de lisibilité dérivé, sans score chiffré : un pourcentage laisserait
        // croire à une certification que le contrat ne donne pas.
        readiness: {
            ready: "Ready to send",
            minor: "A few details could help",
            major: "An important piece of information is missing",
        },
        clarificationsTitle: "A few useful details",
        clarificationAnswer: "Your answer (optional)",
        fold: "Add these details to my request",
        // Affiché quand un repli a enrichi le brouillon APRÈS la génération : la
        // proposition ne contient pas ces réponses, et l'accepter les effacerait. Le
        // libellé dit ce qui se passe et ce qu'il faut faire — l'action reste refusée
        // tant que la proposition n'a pas été recalculée.
        stale: "Your answers were added to your request, but this suggestion was written before them. Ask again to get a version that includes them.",
        errors: {
            unavailable: "Request assistance is not available right now. You can still write and send your request.",
            rateLimited: "Too many attempts. Please try again in a few minutes.",
            provider: "The suggestion could not be prepared. Please try again.",
            draftEmpty: "Write a few words about your need first.",
            draftTooLong: "Your draft is too long to be assisted. Shorten it and try again.",
        },
    },
    impersonation: {
        mode: "Impersonation Mode:",
        viewingAs: "Viewing as {name} ({email})",
        by: "— by {adminEmail}",
        endSession: "End Session",
        noToken: "No impersonation token provided",
        failed: "Failed to impersonate user",
        failedTitle: "Impersonation Failed",
        expiredHint: "This link may have expired. Impersonation tokens are valid for 30 minutes.",
        settingUp: "Setting up impersonation session...",
    },
    prerequisites: {
        title: "To get a fast, accurate answer, cover these points:",
    },
    theme: {
        system: "System",
        light: "Light",
        dark: "Dark",
        ariaLabel: "Theme: {theme}. Click to change.",
        title: "Theme: {theme}",
    },
    editor: {
        bold: "Bold",
        italic: "Italic",
        strikethrough: "Strikethrough",
        heading: "Heading",
        bulletList: "Bullet List",
        numberedList: "Numbered List",
        codeBlock: "Code Block",
        quote: "Quote",
        addLink: "Add Link",
        enterUrl: "Enter the URL:",
        apply: "Apply",
        placeholder: "Start typing...",
        sourceMode: "HTML source mode",
    },
    guest: {
        requests: {
            title: "Your Requests",
            removeTooltip: "Remove from list",
            removeTitle: "Remove Request?",
            removeBody: "This will remove the request from your local list. You can still access the chat if you have the link.",
        },
    },
    reviews: {
        title: "Reviews for {name}",
        none: "No reviews yet",
        noneForRating: "No {rating}-star reviews",
        filterRating: "Rating:",
        filterService: "Service:",
        filterAll: "All ({count})",
        filterAllShort: "All",
        loading: "Loading reviews...",
        loadFailed: "Failed to load reviews",
        /* Deux clés plutôt qu'un token {plural} : « avis » est invariable en
           français. Le modèle avait conservé le token faute de mieux, et le
           catalogue affichait « sur 27 aviss ». */
        showingOne: "Showing 1 review",
        showingMany: "Showing {shown} of {total} reviews",
        noComment: "(No comment)",
    },
    // ─── Navigation (dashboard web) ─────────────────────────────────────────
    nav: {
        media: "Videos",
        dashboard: "Dashboard",
        chats: "Chats",
        billing: "Billing Info",
        products: "Products",
        transactions: "Transactions",
        earnings: "Earnings",
        invoices: "Invoices",
        subscription: "Subscription",
        // Entrée courte : la page s'intitule « Demandes envoyées » (UIUX-1)
        myChats: "My requests",
        myExperts: "My experts",
        profile: "Profile",
        expertProfile: "My expert profile",
        // Cette entrée mène à la page qui porte l'identité de la personne : nom,
        // nom affiché, adresse, mode expert et liens de boutique. Elle s'appelait
        // « Paramètres », ce qui faisait penser aux réglages de l'application
        // alors qu'il s'agit du compte de l'expert.
        account: "My account",
        // Sortie du tableau de bord vers la page d'accueil publique. Elle est
        // portée à la fois par le mot-symbole et par une entrée explicite : un
        // logo cliquable est une convention, pas une information.
        backToSite: "Back to the site",
        // Paramètres > Moyens de paiement (PAY-1) : adresse mémorisée et cartes
        // Stripe enregistrées. L'entrée a été renommée quand « Finances » est
        // arrivée, pour que les deux destinations ne se confondent plus.
        payment: "Payment methods",
        // Espace client > Finances (BL-F3) : historique des règlements et factures
        finances: "Finances",
        inviteExpert: "Invite an expert",
        receivedInvitations: "Received invitations",
        // Parrainage entre experts (lot E5)
        expertReferral: "Referrals",
        notifications: "Notifications",
        signOut: "Sign Out",
    },
    // ─── En-tête public (hors dashboard) ────────────────────────────────────
    // La navigation pointe vers des ancres de la landing. Aucune entrée
    // « parcourir les experts » : ExpertLoop n'est pas un annuaire, et la
    // boutique d'un expert ne renvoie pas son audience vers des concurrents.
    header: {
        home: "ExpertLoop, back to home",
        menu: "Menu",
        openMenu: "Open menu",
        closeMenu: "Close menu",
        features: "Features",
        howItWorks: "How it works",
        pricing: "Pricing",
        createStore: "Create my store",
    },
    // ─── Pied de page public ────────────────────────────────────────────────
    footer: {
        tagline: "Your expertise. Your store. Your clients.",
        navTitle: "Platform",
        accountTitle: "Your account",
        rights: "© 2026 ExpertLoop. All rights reserved.",
    },
    // ─── Authentification ───────────────────────────────────────────────────
    auth: {
        login: {
            title: "Welcome Back",
            subtitle: "Sign in to continue to your account",
            email: "Email",
            emailPlaceholder: "you@example.com",
            password: "Password",
            passwordPlaceholder: "Enter your password",
            submit: "Sign In",
            submitting: "Signing in...",
            failed: "Login failed",
            // Validation côté client avant tout appel API : plus jamais de
            // « Invalid request body » brut affiché à l'utilisateur
            emailRequired: "Please enter a valid email address.",
            passwordRequired: "Please enter your password.",
            // Compte existant détecté dans le tunnel de demande : explique pourquoi
            // l'utilisateur a atterri ici, son email étant déjà pré-rempli
            accountExistsHint: "This email already has an account. Sign in to continue your request.",
            emailAccountExists: "This email is registered with email/password. Please use the login form below.",
            forgotPassword: "Forgot password?",
            or: "or",
            google: "Continue with Google",
            noAccount: "Don't have an account?",
            signUp: "Sign up",
            terms: "By signing in, you agree to our Terms of Service and Privacy Policy.",
        },
        register: {
            title: "Create Account",
            subtitle: "Join ExpertLoop today",
            firstName: "First Name",
            firstNamePlaceholder: "John",
            // Seul le prénom est obligatoire à l'inscription (friction minimale)
            lastName: "Last Name (optional)",
            lastNamePlaceholder: "Doe",
            passwordPlaceholder: "At least 8 characters",
            confirmPassword: "Confirm Password",
            confirmPasswordPlaceholder: "Confirm your password",
            roleLabel: "I want to",
            roleClient: "Find an expert (Client)",
            roleExpert: "Offer my expertise (Expert)",
            submit: "Create Account",
            submitting: "Creating account...",
            failed: "Registration failed",
            passwordMismatch: "Passwords do not match",
            passwordTooShort: "Password must be at least 8 characters",
            haveAccount: "Already have an account?",
            signIn: "Sign in",
            terms: "By creating an account, you agree to our Terms of Service and Privacy Policy.",
            checkEmailTitle: "Check Your Email",
            checkEmailSentTo: "We've sent a verification link to",
            checkEmailHint: "Please check your inbox and click the verification link to activate your account.",
            resendSent: "Verification email sent! Please check your inbox.",
            resendFailed: "Failed to resend email",
            resendFailedRetry: "Failed to resend email. Please try again.",
            resending: "Sending...",
            noAttemptsRemaining: "No attempts remaining",
            resendWithCooldown: "Resend email ({cooldown})",
            resend: "Resend verification email",
            attemptsRemaining: "{count} resend attempt{plural} remaining",
            alreadyVerified: "Already verified?",
        },
        forgot: {
            title: "Forgot Password?",
            subtitle: "Enter your email address and we'll send you a link to reset your password.",
            submit: "Send Reset Link",
            sending: "Sending...",
            failed: "Failed to send reset email",
            checkEmailTitle: "Check Your Email",
            sentTo: "We've sent a password reset link to",
            hint: "If an account exists with this email, you'll receive instructions to reset your password. The link will expire in 1 hour.",
            backToSignIn: "Back to Sign In",
            remembered: "Remember your password?",
        },
        reset: {
            title: "Reset Password",
            subtitle: "Enter your new password below.",
            newPassword: "New Password",
            submit: "Reset Password",
            submitting: "Resetting...",
            failed: "Failed to reset password",
            invalidLink: "Invalid reset link. Please request a new one.",
            invalidLinkTitle: "Invalid Link",
            invalidLinkBody: "This password reset link is invalid. Please request a new one.",
            requestNewLink: "Request New Link",
            successTitle: "Password Reset!",
            successBody: "Your password has been reset successfully.",
        },
        verify: {
            verifyingTitle: "Verifying Email",
            verifyingBody: "Please wait while we verify your email address...",
            verifying: "Verifying...",
            successTitle: "Email Verified!",
            redirecting: "You will be redirected to the login page shortly.",
            signInNow: "Sign In Now",
            failedTitle: "Verification Failed",
            failed: "Verification failed",
            failedHint: "The verification link may have expired or already been used.",
            invalidLink: "Invalid verification link. Please check your email and try again.",
            needNewLink: "Need a new link?",
            needNewLinkSuffix: "and request a new verification email.",
        },
    },
    // ─── Durées ─────────────────────────────────────────────────────────────
    time: {
        day: "1 day",
        days: "{count} days",
        hour: "1 hour",
        hours: "{count} hours",
    },
    // ─── Page d'accueil ─────────────────────────────────────────────────────
    // La landing s'adresse d'abord aux experts : ExpertLoop n'est pas un
    // annuaire où l'on met des profils en concurrence, mais l'outil de vente
    // d'un expert qui apporte déjà sa propre audience. Ne pas réintroduire de
    // vocabulaire de place de marché ici.
    home: {
        // ─── Hero ────────────────────────────────────────────────────────────
        // Le titre s'adresse à l'expert, jamais au client en quête d'un
        // prestataire. La version mobile dit la même chose en trois souffles :
        // le montage téléphone pose le texte à gauche du visage, et une phrase
        // longue y passerait sur le sujet.
        heroTitle: "Your expertise deserves a sales experience to match.",
        heroTitleMobile: "Your expertise. Your clients. Your relationship.",
        heroSubtitle: "Present your services under your own name, receive requests that are already framed, and have the amount held before you start. The client relationship stays yours.",
        heroSubtitleMobile: "Sell under your own name, have the amount held before you start, and keep your clients.",
        heroPhotoAlt: "An expert working on his laptop in his office",
        ctaCreateStore: "Create my store",
        ctaHowItWorks: "See how it works",
        // Trois repères du hero. Ils annoncent des bénéfices développés plus bas
        // et ne les réexpliquent jamais mot pour mot.
        proofStoreTitle: "Your store",
        proofStoreBody: "Present your services and your offers under your own name.",
        proofHoldTitle: "Your work protected by a held amount",
        proofHoldBody: "The amount is authorised before you start, so you never work without cover.",
        proofClientsTitle: "You keep your clients",
        proofClientsBody: "The relationship and the exchanges stay between you and them.",
        // ─── Parcours client en quatre étapes ────────────────────────────────
        // Cette section remplace à elle seule les anciens piliers, l'inventaire
        // d'outils et la comparaison avant/après, qui racontaient trois fois le
        // même parcours commercial.
        journeyTitle: "Your whole client journey, in one place.",
        journeyLead: "From the offer you publish to the amount you actually collect, in four steps.",
        journeyPresentTitle: "Present your offers",
        journeyPresentBody: "Your store gathers your services, your prices and your terms, ready to share.",
        journeyRequestTitle: "Receive a framed request",
        journeyRequestBody: "The client describes what they need, and ExpertLoop helps them give the useful details without filling in an administrative form.",
        journeySecureTitle: "Secure the start",
        journeySecureBody: "The client authorises the amount before you begin. Nothing is charged at that point: the amount is simply held.",
        journeyDeliverTitle: "Work, deliver and get paid",
        journeyDeliverBody: "Messages, documents and deliverables stay in the same place, and the payment is collected on what you actually delivered.",
        // ─── Positionnement ──────────────────────────────────────────────────
        // Le message tient en deux temps, portés par deux clés : la seconde
        // ligne est mise en couleur, et une seule phrase coupée en CSS aurait
        // dépendu de la longueur de chaque traduction.
        directoryTitle: "This is not a marketplace.",
        directoryTitleFunnel: "It is your professional sales funnel.",
        directoryBody: "You keep your brand, your audience and your client relationship. ExpertLoop stays in the background and provides the infrastructure.",
        directoryPointBrand: "Your brand, your audience, your client relationship.",
        directoryPointRanking: "No imposed ranking, no forced competition.",
        directoryPointBackground: "ExpertLoop stays behind you and gives you the tools to sell and work freely.",
        // ─── IA au service de la relation ────────────────────────────────────
        // Aucun taux ni résultat chiffré n'est promis : le texte dit ce que
        // l'assistance change pour le client, pas ce qu'elle garantit.
        aiTitle: "AI assists you. You stay the expert.",
        aiBody: "A request that is better formulated is clearer for you and more reassuring for your client, and fewer misunderstandings can help more conversations become missions. A summary only covers what the assistant can actually see in the conversation.",
        aiUsePrepare: "Prepare an offer",
        aiUseRequest: "Assists the client's request",
        aiUseTranslate: "Translate the exchanges",
        aiUseSummarise: "Summarise a conversation",
        aiBoundary: "AI helps you understand, structure, translate and prepare. It never decides, never answers and never sends in your place.",
        // ─── Conditions et confiance ─────────────────────────────────────────
        // Toutes les informations financières vivent ici et nulle part ailleurs.
        // La fourchette reflète les formules réellement configurées (lot SUB-1) :
        // les taux sont interpolés depuis la grille servie par l'API, jamais écrits
        // ici. La version sans chiffres sert tant que la grille n'est pas chargée.
        pricingTitle: "Clear terms, from the start",
        pricingCommissionTitle: "Commission set by your plan",
        pricingCommissionBody: "The rate comes from your subscription plan, from {highestRate}% on the free plan down to {lowestRate}% on the highest plan beyond a monthly sales threshold. Stripe fees are not included.",
        pricingCommissionBodyGeneric: "The rate comes from your subscription plan, and a higher plan lowers it. Stripe fees are not included.",
        pricingHoldTitle: "The amount is held before you start",
        pricingHoldBody: "Your client authorises the amount before the work begins, with nothing charged at that point. You never start without knowing the money is covered.",
        pricingKnownTitle: "You know what you earn before you accept",
        pricingKnownBody: "The amount and the terms that apply are visible to you before you accept a request.",
        // ─── Relance finale ──────────────────────────────────────────────────
        finalCtaTitle: "A platform built for experts.",
        finalCtaBody: "ExpertLoop is launching, and it is being built with the experts who use it.",
        finalCtaPhotoAlt: "An expert smiling at her desk, a cup in her hands",
        // Conservés
        goToDashboard: "Go to Dashboard",
        browseExperts: "Browse Experts",
        signUp: "Sign Up",
        signIn: "Sign In",
        guestRequestsTitle: "Your Guest Requests",
        becomeExpert: "Become an Expert",
        footer: "© 2026 ExpertLoop. All rights reserved.",
    },
    // ─── Boutique expert ────────────────────────────────────────────────────
    expertStore: {
        disabled: "This expert's profile is currently unavailable",
        notFound: "Expert not found",
        tagline: "Expert on ExpertLoop",
        rating: "rating",
        ratings: "ratings",
        noRatings: "No ratings yet",
        respondsWithin: "Typically responds within {time}",
        guestRequestsWithExpert: "You have {count} request{plural} with this expert",
        services: "Services",
        noServices: "This expert hasn't added any services yet.",
        viewDetails: "View Details",
    },
    // ─── Boutique de l'expert (refonte) ─────────────────────────────────────
    // Formulations volontairement non contractuelles pour le délai de réponse :
    // seul le délai de restitution est un engagement porté par la commande.
    store: {
        // Bandeau de marque, desktop uniquement : en mobile le client doit voir
        // l'expert et ses prestations sans avoir à faire défiler un hero.
        bannerTitle: "Your expertise. Your store. Your clients.",
        bannerSubtitle: "Sell your services your own way.",
        selectService: "Choose a service",
        yourSelection: "Your selection",
        chooseService: "Choose this service",
        serviceDetails: "Service details",
        whatYouShare: "What you share to get started",
        deliveryWithin: "Delivered within {time} at the latest",
        responseWithin: "Usually replies within {time}",
        serviceCount: "{count} services",
        oneService: "1 service",
        allServices: "All services",
        otherServices: "See the other services",
        backToServices: "Back to services",
        // Catalogue et fil d'Ariane de la vitrine publique.
        servicesWithCount: "Services ({count})",
        servicesShort: "Services",
        viewService: "View service",
        // Dépliage de la bio complète : aucune page de profil public n'existe.
        viewProfile: "View profile",
        hideProfile: "Hide profile",
        terms: "Terms",
        // Conditions de la prestation en libellé + valeur : la fiche mobile les
        // aligne dans un panneau d'information au lieu de les réciter en phrases.
        // Le libellé porte la nuance (« max. », « habituel »), la valeur ne porte
        // que la durée formatée.
        deliveryLabel: "Max. delivery time",
        responseLabel: "Usual reply time",
        breadcrumb: "Breadcrumb",
        home: "Home",
    },
    // ─── Page introuvable (route inconnue) ──────────────────────────────────
    // Filet de sécurité du routeur : sans elle, une adresse erronée affichait
    // la page d'erreur nue de React Router, sans logo ni porte de sortie.
    notFoundPage: {
        title: "Page not found",
        body: "This address doesn't match any page. The link may be mistyped, or the page may have moved.",
        home: "Back to the home page",
        dashboard: "Go to my dashboard",
    },
    // ─── Session perdue (écran de secours des pages protégées) ──────────────
    // Même principe que notFoundPage : jamais d'écran sans issue.
    session: {
        expiredTitle: "Your session has expired",
        expiredBody: "Sign in again to pick up where you left off.",
        unreachableTitle: "Can't reach ExpertLoop",
        unreachableBody: "Check your internet connection, then try again.",
        retry: "Try again",
        signIn: "Sign in",
    },
    // ─── Écran d'erreur inattendue (errorElement du routeur) ────────────────
    // Dernier filet : un plantage de rendu n'affiche plus l'écran nu de React
    // Router, mais une sortie humaine — recharger, revenir chez soi, écrire.
    errorPage: {
        title: "Something went wrong",
        body: "This page failed to display. It's on our side, not yours — your requests and conversations are safe. Reloading usually fixes it.",
        reload: "Reload the page",
        support: "Contact support",
    },
    // ─── Page produit ───────────────────────────────────────────────────────
    product: {
        unavailable: "This service is currently unavailable",
        notFound: "Product not found",
        backToStore: "Back to {name}'s store",
        respondsWithin: "Responds within {time}",
        prerequisitesTitle: "To get a fast, accurate answer, prepare:",
        priceLabel: "Price",
        ownService: "This is your own service",
        requestService: "Request This Service",
    },
    // ─── Page de demande ────────────────────────────────────────────────────
    request: {
        title: "Describe Your Request",
        intro: "Take your time to explain what you need. The more details you provide, the better {name} can help you.",
        theExpert: "the expert",
        requestingAs: "Requesting as",
        yourEmail: "Your Email *",
        emailPlaceholder: "email@example.com",
        yourName: "Your Name (optional)",
        namePlaceholder: "John Doe",
        yourRequest: "Your Request *",
        prerequisitesTitle: "Cover these points so the expert can answer quickly:",
        messagePlaceholder: "Describe what you need help with in detail...",
        // L'envoi persiste la demande côté serveur ; elle n'est transmise à
        // l'expert qu'après vérification de l'email (UIUX-1B)
        send: "Send my request",
        sending: "Saving...",
        stepEyebrow: "Step 1 of 3 — Your request",
        sendFailed: "Failed to send request",
        emailMismatch: "You must be signed in with the same email to place this order.",
        fillRequired: "Please fill in all required fields",
        cannotRequestOwn: "You cannot request your own service",
        backToProduct: "Back to Product",
        backToService: "Back to service details",
        draftSaving: "Saving...",
        draftSaved: "Draft saved",
        // UIUX-1B — la rédaction précède l'identification : l'email n'est
        // demandé qu'au moment d'« Envoyer ma demande », dans un dialogue
        guestWriteFirst: "Write your request first — we'll only ask for your email when you send it.",
        contactDialog: {
            title: "Where should we reach you?",
            body: "Your email is where we'll confirm your request and let you know when {expertName} replies.",
            hint: "We'll send you a 6-digit code to confirm this address. No account needed.",
            submit: "Continue",
        },
    },
    // ─── Onboarding expert ──────────────────────────────────────────────────
    onboarding: {
        title: "Welcome to ExpertLoop!",
        subtitle: "Let's get your expert profile set up so you can start receiving clients.",
        step1Title: "Account Created",
        step1Body: "Your expert account has been created.",
        step2Title: "Set Up Payments",
        step2Body: "Connect your Stripe account to receive payments from clients.",
        connectStripe: "Connect Stripe",
        step3Title: "Create Products",
        step3Body: "Add products or services that clients can purchase.",
        goToProducts: "Go to Products",
        step4Title: "Share Your Store",
        step4Body: "Share your expert page link with potential clients.",
    },
    // ─── Onboarding Stripe ──────────────────────────────────────────────────
    stripeOnboarding: {
        checkingStatus: "Checking account status...",
        completedTitle: "Onboarding Completed!",
        completedBody: "Your Stripe account is fully set up and ready to receive payments.",
        backToDashboard: "Come back to my dashboard",
        almostThereTitle: "Almost There!",
        almostThereBody: "Your details have been submitted. Stripe is reviewing your account. This usually takes a few minutes.",
        checkStatus: "Check Status",
        formHint: "Complete the form below to start accepting payments from clients.",
        tryAgain: "Try again",
        goToDashboard: "Go to dashboard",
        initializing: "Initializing account...",
        skip: "Skip for now",
        initFailed: "Failed to initialize account",
        notComplete: "Onboarding not complete",
        loadFailed: "Failed to load onboarding",
        stripeLoadFailed: "Failed to load Stripe",
    },
    // ─── Dashboard : Earnings / Billing ─────────────────────────────────────
    earnings: {
        title: "Earnings",
        subtitle: "Understand what you earned and follow every bank payout",
        notSetupTitle: "Stripe Account Not Set Up",
        notSetupBody: "You need to complete your Stripe onboarding to withdraw earnings.",
        completeOnboarding: "Complete Stripe Onboarding",
        loading: "Loading your earnings",
        loadFailed: "Your earnings could not be loaded. Please try again later.",
        netSinceSignup: "Net earnings since you joined",
        alreadyPaid: "Already paid",
        toReceive: "To receive",
        breakdownTitle: "How your net earnings are calculated",
        grossRevenue: "Gross revenue",
        totalFees: "ExpertLoop + Stripe fees",
        feesDetail: "ExpertLoop: {platform} · Stripe: {stripe}",
        // Remboursements déduits des revenus nets, comme le fait Stripe
        refunds: "Refunds to clients",
        feesReturned: "ExpertLoop commission returned with refunds",
        refundsHint: "Stripe does not return its processing fees when a sale is refunded: they remain in the fees above.",
        stripeBalanceTitle: "Still with Stripe",
        inTransit: "In transit or scheduled",
        availableBalance: "Available balance",
        pendingBalance: "Pending balance",
        balanceEstimateHint: "Stripe has not created a payout for these balances yet. No transfer date is promised here.",
        payoutsTitle: "Payout timeline",
        payoutsHint: "Only payouts actually created by Stripe appear here.",
        noPayouts: "No bank payout has been created yet.",
        destination: "{label} ending in {last4}",
        bankAccount: "Bank account",
        estimatedArrival: "Estimated arrival provided by Stripe: {date}",
        // Écart restant entre notre calcul et ce que Stripe a réellement versé ou garde
        reconciliationLower: "Stripe counts {difference} less than this page. This usually comes from Stripe fees we could not record on older sales. For your bank payouts, Stripe's figures (already paid and to receive) are the ones that count.",
        reconciliationHigher: "Stripe counts {difference} more than this page. This usually comes from a sale or an adjustment Stripe recorded that is not yet reflected here. For your bank payouts, Stripe's figures (already paid and to receive) are the ones that count.",
        // Pied de la liste des virements
        payoutsTotalPaid: "Total paid to your bank account",
        payoutsTotalInProgress: "On its way",
        status: {
            paid: "Paid",
            inTransit: "In transit",
            pending: "Scheduled",
            failed: "Failed",
            canceled: "Canceled",
        },
    },
    billing: {
        subtitle: "Manage your Stripe account and payment settings",
        notSetupBody: "You need to complete your Stripe onboarding to access billing features.",
        loading: "Loading billing information...",
        loadFailed: "Failed to load billing information. Please try again later.",
    },
    // ─── Dashboard : Purchases (client) ─────────────────────────────────────
    orders: {
        title: "My Purchases",
        subtitle: "Orders from experts you've hired",
        loading: "Loading your orders...",
        loadFailed: "Failed to load orders",
        emptyTitle: "No purchases yet",
        emptyBody: "When you hire an expert and make a payment, your orders will appear here.",
        expertOrder: "Expert Order",
        completedOn: "Completed: {date}",
        viewChat: "View Chat",
        status: {
            pendingAuthorization: "Pending Hold",
            authorized: "Funds Held",
            captured: "Completed",
            refundPending: "Refund pending",
            refunded: "Refunded",
            canceled: "Canceled",
            expired: "Hold Expired",
            cancellationRequested: "Cancellation Requested",
            // Mission gratuite (REFERRAL-4)
            settledFree: "Completed (free)",
            freeInProgress: "Free mission",
        },
        statusDesc: {
            pendingAuthorization: "Waiting for you to hold funds",
            authorized: "Funds held on your card — expert working on your request",
            captured: "Service completed and payment processed",
            refundPending: "Your refund is being processed",
            refunded: "Your payment has been refunded",
            canceled: "Order was canceled",
            expired: "The hold on your funds expired",
            cancellationRequested: "Cancellation has been requested",
            // Mission gratuite (REFERRAL-4) : aucun montant en jeu
            settledFree: "Free mission completed — no payment was involved",
            freeInProgress: "Free mission in progress — no payment involved",
        },
        freeAmount: "Free",
    },
    // ─── Dashboard : Invoices ───────────────────────────────────────────────
    invoices: {
        subtitle: "View and download invoices for completed services",
        defaultClient: "Client",
        defaultProduct: "Expert Service",
        fee: "Fee: {fee}",
        pdf: "PDF",
        view: "View",
        // Colonnes du tableau, sur le modèle du centre client
        columnDate: "Date",
        columnClient: "Client",
        columnProduct: "Service",
        columnAmount: "Amount",
        columnFee: "Fee",
        columnInvoice: "Invoice",
        downloadFor: "Download the invoice for {client}",
        viewFor: "View the invoice for {client}",
        downloadFailed: "The invoice could not be downloaded.",
        retry: "Try again",
        loading: "Loading invoices...",
        loadFailed: "Failed to load invoices",
        emptyTitle: "No invoices yet",
        emptySearch: "No invoices match your search. Try a different search term.",
        emptyBody: "Invoices will appear here after you complete paid services.",
        found: "{count} invoice{plural} found",
        searchPlaceholder: "Search by client, service or chat reference...",
        show: "Show:",
    },
    // ─── Dashboard : Transactions ───────────────────────────────────────────
    transactions: {
        subtitle: "View your payments, disputes, and financial documents",
        connectTitle: "Connect Stripe to View Transactions",
        connectBody: "Set up your Stripe account to view your payment history and documents.",
        setupIncompleteTitle: "Complete Your Stripe Setup",
        setupIncompleteBody: "Your Stripe account setup is incomplete. Complete the onboarding process to start receiving payments.",
        completeSetup: "Complete Setup",
        verificationTitle: "Verification in Progress",
        verificationBody: "Your account is being verified by Stripe. This usually takes 1-2 business days. You'll be able to receive payments once verification is complete.",
        payments: "Payments",
        disputes: "Disputes",
        documents: "Documents",
        // Le tableau des paiements est rendu par Stripe : sa colonne « montant »
        // affiche la somme réservée, pas celle réellement encaissée après une
        // remise consentie à la livraison. Nous ne pouvons pas changer cette
        // colonne, seulement l'expliquer.
        reducedPriceTitle: "Amounts shown by Stripe",
        reducedPriceBody: "The amount column shows what the client reserved on their card. When you deliver at a reduced price, the amount you actually collected appears in the payment details.",
        reducedPriceEarningsLink: "See your earnings",
        notificationsLoadFailed: "Failed to load notifications",
        paymentsLoadFailed: "Failed to load payments",
        disputesLoadFailed: "Failed to load disputes",
        documentsLoadFailed: "Failed to load documents",
    },
    // ─── Dashboard : Profile ────────────────────────────────────────────────
    profile: {
        loading: "Loading profile...",
        editTitle: "Edit Profile",
        updated: "Profile updated successfully!",
        updateFailed: "Failed to update profile",
        namesRequired: "First name and last name are required",
        displayName: "Display Name",
        displayNamePlaceholder: "Your display name",
        displayNameHint: "This name will be shown to other users. Leave empty to use your full name.",
        emailLocked: "Email cannot be changed",
        saving: "Saving...",
        saveChanges: "Save Changes",
        expertMode: "Expert Mode",
        expertModeHint: "Enable expert mode to create products and receive payments from clients.",
        expertModeFailed: "Failed to update expert mode",
        expertSettings: "Expert Settings",
        storeUrl: "Your store address",
        storeUrlHint: "Share this link with clients so they can view your products and start chats.",
        expertId: "ExpertLoop ID",
        expertIdHint: "This is the permanent identifier of your expert profile.",
        permanentUrl: "Permanent address",
        permanentUrlHint: "This immutable technical address remains valid even if your public alias changes.",
        copyUrl: "Copy",
        copiedUrl: "Copied!",
        copyUrlFailed: "The address could not be copied.",
        responseTime: "Expected Response Time",
        responseTimeHint: "Let clients know how quickly you typically respond to new requests.",
        responseTimeFailed: "Failed to update response time",
        within1Hour: "Within 1 hour",
        withinHours: "Within {count} hours",
        withinDays: "Within {count} days",
        stripeSetupHint: "Complete your Stripe setup to receive payments.",
        onboardingIncomplete: "Onboarding Incomplete",
        onboardingIncompleteHint: "Please complete your Stripe onboarding to start receiving payments.",
        completeOnboarding: "Complete Onboarding",
        pendingVerification: "Pending Verification",
        pendingVerificationHint: "Your account is being verified by Stripe. This usually takes 1-2 business days.",
        paymentsConnected: "Payments Connected",
        paymentsConnectedHint: "Your Stripe account is connected and ready to receive payments.",
        disableExpertTitle: "Disable Expert Mode",
        disableExpertBody: "Disabling expert mode will hide your products from clients. Your Stripe account and existing chats will be preserved. Continue?",
        disable: "Disable",
    },
    // ─── Système de statuts des chats (source de vérité : features/chat/utils.ts) ──
    chatStatus: {
        pendingPayment: "awaiting payment",
        // UIUX-1B : email prouvé, fonds pas encore réservés — visible expert
        awaitingFunds: "email verified · funds to reserve",
        abandoned: "abandoned",
        pending: "pending",
        declined: "declined",
        completed: "completed",
        refundPending: "refund pending",
        refunded: "refunded",
        /* Après-vente : demande de remboursement sur une prestation déjà livrée
           et payée. La conversation cesse d'être présentée comme terminée. */
        refundRequested: "refund requested",
        mediation: "mediation",
        /* Le support a tranché : le litige est clos, la conversation est terminée
           et n'attend plus aucun geste. Sans cet état, elle restait « mediation »
           indéfiniment, et l'expert la voyait parmi ses litiges actifs. */
        mediationResolved: "mediation resolved",
        cancellation: "cancellation",
        canceled: "canceled",
        confirmed: "confirmed",
        delivered: "delivered",
        revision: "revision",
        inProgress: "in progress",
    },
    // ─── Bandeau d'état du fil (ChatStatusStrip, refonte UI v3) ─────────────
    chatStrip: {
        inProgress: "In progress",
        mediationResolved: "Mediation resolved",
        mediationResolvedNote: "The support team has settled this case.",
        toDeliver: "To deliver",
        awaitingReply: "Awaiting reply",
        awaitingClientReview: "Awaiting client review",
        awaitingPayment: "Awaiting payment",
        actionRequired: "Action required",
        reviewDelivery: "Review the delivery",
        capturePayment: "Capture the payment",
        acceptOrDecline: "Accept or decline the request",
        cancellationRequested: "Cancellation requested",
        underMediation: "Under review by support",
        completed: "Completed",
        declined: "Declined",
        expired: "Expired",
        abandoned: "Abandoned",
        closed: "Closed",
        canceled: "Canceled",
        refundPending: "Refund in progress",
        refunded: "Refunded",
        /* Après-vente : le client a demandé un remboursement sur une prestation
           déjà livrée et payée. La conversation cesse d'être « terminée ». */
        refundRequested: "Refund requested",
        deliverBefore: "Deliver before {date}",
        respondBefore: "Respond before {date}",
        reviewBefore: "Reply before {date}",
        timeLeft: "{time} left",
    },
    // ─── Remboursement après livraison (BL-F5) ──────────────────────────────
    /* La somme est décidée par l'expert, jamais proposée par le client : la
       conversation reste le lieu de la négociation. */
    /* Jalons d'après-vente dans la conversation. Le client doit lire ce qui lui
       est réellement rendu, et l'expert ce qu'il a réellement accordé. */
    timelineRefund: {
        requestedTitle: "Refund requested",
        requestedExpertBody: "Your client is asking for a refund on this service, for which you collected {amount}. You decide how much to give back.",
        requestedClientBody: "You asked for a refund on this service, for which you paid {amount}.",
        requestedWaiting: "Waiting for your expert's decision.",
        partialTitle: "Refund granted",
        partialExpertBody: "You refunded {refunded} of the {captured} collected. The ExpertLoop commission was returned in the same proportion.",
        partialClientBody: "Your expert refunded you {refunded} of the {captured} you paid.",
        bankDelay: "Depending on your bank, it can take a few days to appear on your statement.",
    },
    requestRefundModal: {
        title: "Ask for a refund",
        body: "You paid {amount} for this service. Your expert will decide how much to give back, and you can discuss it in the conversation.",
        explainPrompt: "Tell your expert what went wrong.",
        placeholder: "Explain briefly why you are asking for a refund…",
        confirm: "Send the request",
        requesting: "Sending…",
    },
    refundModal: {
        title: "Refund this client",
        body: "You collected {amount} for this service. Choose how much you want to give back.",
        amountLabel: "Amount to refund",
        invalidAmount: "Enter an amount above zero and no higher than {amount}.",
        commissionNotice: "The ExpertLoop commission is returned in the same proportion. Payment processing fees are not returned.",
        confirm: "Refund",
        refunding: "Refunding…",
    },
    // ─── Actions plateforme de l'expert (source de vérité : features/chat/utils.ts) ──
    expertAction: {
        startVideoSession: "Start a video session",
        declineRequest: "Decline Request",
        acceptRequest: "Accept Request",
        capturePayment: "Capture Payment",
        deliverService: "Deliver Service",
        deliverComplement: "Share an additional delivery",
        approveCancellation: "Approve Cancellation",
        denyCancellation: "Deny Cancellation",
        approveRefund: "Refund the client",
        denyRefund: "Decline the refund",
        unblockCancellations: "Unblock cancellations",
        blockCancellations: "Block cancellations",
        declineAndRelease: "Decline & Release",
        deliverAgain: "Deliver Again",
        cancelAndRepublish: "Cancel & Republish",
        offerSecondChance: "Offer Second Chance",
    },
    // ─── Vue d'ensemble du dashboard (expert) ────────────────────────────────
    overview: {
        seeStore: "See my store",
        copyLink: "Copy my link",
        copied: "Copied!",
        needsDelivery: "Needs Delivery",
        noOrdersAwaitingDelivery: "No orders awaiting delivery",
        readyToCapture: "Ready to Capture",
        noPaymentsToCapture: "No payments ready to capture",
        capture: "Capture",
        capturePayment: "Capture Payment",
        awaitingApproval: "Awaiting Client Approval",
        noDeliveriesAwaiting: "No deliveries awaiting approval",
        connectStripeTitle: "Connect Stripe to Accept Payments",
        connectStripeBody: "Set up your Stripe account to start receiving payments from clients.",
        earnedRevenue: "Earned Revenue",
        pendingRevenue: "Pending Revenue",
        overdue: "Overdue",
        hoursMinutesLeft: "{hours}h {minutes}m left",
        hoursLeft: "{hours}h left",
        daysHours: "{days}d {hours}h",
        autoAccepting: "Auto-accepting...",
        autoAcceptInHours: "Auto-accept in {hours}h {minutes}m",
        autoAcceptInMinutes: "Auto-accept in {minutes}m",
        revisionRequested: "Revision requested",
        toDeliver: "To deliver",
        allCaughtUp: "All caught up! No pending actions.",
        actionRequired: "Action Required",
        pendingRequestsOne: "1 new chat request awaiting your response",
        pendingRequestsMany: "{count} new chat requests awaiting your response",
        viewRequests: "View Requests",
        unreadChatsOne: "1 chat has unread messages",
        unreadChatsMany: "{count} chats have unread messages",
        viewChats: "View Chats",
        activeChatsOne: "1 active chat needs your attention",
        activeChatsMany: "{count} active chats need your attention",
    },
    // ─── Tableau de bord expert v2 ───────────────────────────────────────────
    // Branche distincte de `overview`, dont plusieurs clés sont déjà des
    // feuilles : y imbriquer romprait la règle « une clé ne peut pas être à la
    // fois une feuille et une branche ».
    dashboard: {
        greeting: "Hello {name}",
        /* Libellé de l'icône « i » pour les lecteurs d'écran, suivi de ce qu'elle explique. */
        aboutLabel: "About: {subject}",
        period: {
            label: "Period",
            d7: "7 days",
            d30: "30 days",
            month: "This month",
            m12: "12 months",
        },
        subtitle: {
            d7: "Here is your activity over the last 7 days.",
            d30: "Here is your activity over the last 30 days.",
            month: "Here is your activity this month.",
            m12: "Here is your activity over the last 12 months.",
        },
        revenue: {
            netLabel: {
                d7: "Captured over 7 days, net of commission",
                d30: "Captured over 30 days, net of commission",
                month: "Captured this month, net of commission",
                m12: "Captured over 12 months, net of commission",
            },
            firstPeriod: "First measured period",
            deltaUp: "Up {percent}% versus the previous period",
            deltaDown: "Down {percent}% versus the previous period",
            gross: "Gross invoiced",
            grossInfo: "What your clients paid in total over this period, before anything was deducted. Compare it with the amount above to see what actually reaches you.",
            platformFee: "ExpertLoop commission",
            platformFeeInfo: "What ExpertLoop keeps on the sales of this period. It is shown as a deduction because it is already subtracted from the amount above: that amount is what lands on your account, not an estimate.",
            stripeFee: "Stripe fees",
            stripeFeeInfo: "What the payment provider charges to move the money from your client to you. ExpertLoop does not keep any of it. It varies with the card used, which is why it is not a round number.",
            sparklineInfo: "Each bar is one captured payment period. Hover a bar to see its date and amount. The scale follows the period you selected above.",
            sparklineLabel: "Captured revenue over twelve months, from {first} to {last}",
            sparklineEmpty: "No revenue captured over the last twelve months",
            loading: "Loading your figures",
        },
        stats: {
            secured: "Secured",
            securedMeta: "{count} service{plural}, funds held on the client side",
            securedEmpty: "No funds held right now",
            securedInfo: "Money your clients have already committed: their card is charged on hold, and the amount is waiting for you to deliver. This is today's picture, so it does not change with the period you select. Deliver these and it becomes yours.",
            capturable: "To capture",
            capturableMeta: "{count} service{plural} approved by the client",
            capturableEmpty: "Nothing waiting to be captured",
            capturableInfo: "Your client approved the delivery, so this money is yours to take. Nothing happens until you capture it, and a payment hold does not last forever — this is the one number worth acting on today. Like the one above, it does not depend on the selected period.",
            averageOrder: "Average sale",
            averageOrderMeta: "{count} service{plural} sold over the period",
            averageOrderEmpty: "No sale over the period",
            averageOrderInfo: "What one sale brings you on average over this period. It is the figure you can move without finding a single extra client: a better framed offer, a higher priced service, or one add-on raises it.",
        },
        completeness: {
            title: "Store completeness",
            startTitle: "Before your first share",
            score: "{score} of {total} points",
            progressLabel: "Store completeness: {score} of {total} points",
            items: {
                stripeConnected: "Payment account connected",
                bio: "Introduction written",
                products: "Service online",
                responseTime: "Response time announced",
                displayName: "Display name set",
                avatar: "Profile photo",
                expertises: "At least 3 areas of expertise",
            },
            info: {
                stripeConnected: "Connect the account that will receive your money. Without it a client can start a request, but no payment can be held and nothing can reach you.",
                bio: "A few lines on who you help and what changes for them. This is what a visitor reads before deciding to write to you, and it is where most hesitation is settled.",
                products: "A service is what a client can actually order: a scope, a price, a delivery time. Without one, your page can be read but nothing can be bought.",
                responseTime: "The delay you commit to before answering a request. Announcing it reassures a client who has no idea whether you will reply today or next week — and you are measured on it.",
                displayName: "The name shown on your store and in your conversations. Your own name or your studio name, whichever your audience already knows.",
                avatar: "A face makes a stranger far easier to write to. It is the cheapest thing on this list and one of the most persuasive.",
                expertises: "The subjects you take on. They tell a visitor in two seconds whether they are in the right place, and they set the expectations you will be judged on.",
            },
            actions: {
                stripeConnected: "Connect",
                bio: "Write",
                products: "Add",
                responseTime: "Set",
                displayName: "Set",
                avatar: "Add",
                expertises: "Complete",
            },
        },
        queue: {
            title: "What is waiting for you",
            hint: "Sorted by the closest deadline",
            tabs: {
                all: "All",
                respond: "Respond",
                deliver: "Deliver",
                capture: "Capture",
                awaiting: "Awaiting approval",
                cancellation: "Cancellation asked",
            },
            kind: {
                respond: "Respond",
                deliver: "Deliver",
                capture: "Capture",
                awaiting: "Awaiting approval",
                cancellation: "Cancellation asked",
            },
            empty: {
                all: "Nothing waiting for you right now.",
                respond: "No request waiting for an answer.",
                deliver: "No service left to deliver.",
                capture: "No payment left to capture.",
                awaiting: "No delivery waiting for a client decision.",
                cancellation: "No cancellation request to decide on.",
            },
            /* Compteur d'onglet, pour les lecteurs d'écran : « Deliver, 2 waiting ». */
            tabCount: "{count} waiting",
            noDeadlineDecision: "Waiting on you",
            revision: "Revision requested",
            capture: "Capture",
            openChat: "Open the conversation with {client}",
            page: "Page {page} of {totalPages}",
            previous: "Previous page",
            next: "Next page",
            noDeadline: "No deadline",
        },
        countdown: {
            lateHours: "{hours}h late",
            lateDays: "{days}d late",
            lateMinutes: "{minutes}m late",
            leftMinutes: "{minutes}m left",
            leftHours: "{hours}h left",
            leftDays: "{days}d left",
        },
        risks: {
            title: "Needs your attention",
            responseDueSoon: "{count} answer{plural} due shortly",
            deliveryOverdue: "{count} overdue service{plural} to deliver",
            authorizationExpiringSoon: "{count} payment hold{plural} expiring",
            cta: "Handle now",
        },
        commitments: {
            title: "Your commitments",
            responseTime: "Median response time",
            responseTimeInfo: "Half your answers are faster than this, half slower. A median rather than an average: one two-week absence should not darken a year of quick replies. Measured on accepted requests only.",
            responseTimeMark: "You announce {hours}h",
            hoursValue: "{hours}h",
            minutesValue: "{minutes}min",
            responseTimeUnder: "Faster than you announce",
            responseTimeOver: "Slower than you announce",
            onTimeDelivery: "Delivered on time",
            onTimeDeliveryInfo: "Share of deliveries sent before their deadline over this period. It is the promise your clients paid for, and the one they remember.",
            onTimeDeliveryMeta: "{late} late out of {total} delivered service{plural}",
            acceptance: "Payment-held requests accepted",
            acceptanceInfo: "Accepted requests divided by accepted plus declined requests among payments held during the selected period. This is a decided subset of the Payment held step below; unanswered requests are not counted in the rate.",
            acceptanceMeta: "{accepted} accepted and {declined} declined — {decided} answered among {authorized} payments held",
            noData: "—",
            noResponse: "No accepted request over this period",
            noDelivery: "No delivery over this period",
            noDecision: "No request answered over this period",
        },
        funnel: {
            title: "From visitor to payment",
            hint: "Events in the selected period",
            info: "Each step counts events in the same selected period, not a group of clients followed one by one. Payment held is also the pool from which answered requests are measured in Your commitments; a service captured this month may have been requested last month.",
            requests: "Requests",
            // Paliers de la continuité commerciale (UIUX-1B)
            verified: "Email verified",
            authorized: "Payment held",
            accepted: "Accepted",
            delivered: "Delivered",
            captured: "Captured",
            rate: "{percent}% of the step above",
            empty: "No request over this period",
            reminders: "{sent} reminder{plural} sent over the period — {converted} followed by a funds reservation.",
        },
        products: {
            title: "What sells",
            name: "Service",
            sales: "Sales",
            captured: "Captured",
            info: "Your five best sellers over the period. Services still online but unsold appear too — knowing what finds no buyer is worth as much as knowing what sells.",
            noSale: "No sale yet",
            empty: "No service online yet.",
        },
        reviews: {
            title: "What your clients say",
            outOf: "out of 5",
            /* Deux clés plutôt qu'un token {plural} : « avis » est invariable en
               français, le modèle supprime alors le token et la traduction est
               rejetée. Même motif que `overview.pendingRequestsOne/Many`. */
            totalOne: "1 review",
            totalMany: "{count} reviews",
            info: "Ratings and the latest review cover your whole history, not the selected period: an average over thirty days says little when the volume is small.",
            empty: "No review yet. They arrive once a client approves a delivery.",
            latest: "Latest review",
        },
        start: {
            eyebrow: "Your store is online",
            title: "All that is missing is a first client.",
            body: "Nobody finds you by chance: on ExpertLoop, your links bring your clients. Put this one where your audience already follows you.",
            copy: "Copy",
            copied: "Copied!",
            shareLabel: "Where this link belongs",
            share: {
                video: "Video description",
                bio: "Bio link",
                pinned: "Pinned post",
                qr: "QR code",
            },
            howTitle: "How it will work",
            steps: {
                oneTitle: "A client writes to you",
                oneBody: "You receive a request with their need already framed.",
                twoTitle: "Their payment is held",
                twoBody: "Nothing is charged until the service is approved.",
                threeTitle: "You deliver, then you get paid",
                threeBody: "The funds move to your account on the next payout.",
            },
        },
    },
    // ─── Panneau Actions (client et expert) ──────────────────────────────────
    actions: {
        title: "Actions",
        openPanel: "Open actions panel",
        noActionNeeded: "No action needed from you right now.",
        cancelDeliveryDraft: "Cancel delivery draft",
        captured: "Captured",
        refundPending: "Refund pending",
        refunded: "Refunded",
        holdReleased: "Hold released",
        heldInEscrow: "Held in escrow",
        // Mission gratuite (REFERRAL-4) : aucun montant en jeu
        freeMission: "Free mission",
        freeAmount: "Free",
        freeProposedAmountLabel: "Proposed price:",
        minutesLeft: "{minutes}m left",
        daysHoursLeft: "{days}d {hours}h left",
        clientCanCancelFreely: "The client can still cancel freely for",
        cancellationsBlocked: "Cancellation requests are blocked while you work on this order.",
        guidance: {
            pending: "A client placed a hold on their payment and is waiting for your answer. Declining releases the hold automatically.",
            confirmed: "The client accepted your delivery. Capture the payment to get paid.",
            inProgress: "The payment is secured. Deliver your service before the deadline.",
            revision: "The client requested changes. Send a new delivery.",
            delivered: "Waiting for the client to review your delivery.",
            cancellation: "The client asked to cancel this order. Review their request below.",
            mediation: "Support is reviewing this order. Actions are locked until a decision is made.",
            // Le litige est clos : l'expert ne doit plus lire que son dossier est
            // à l'étude, ni attendre un geste de sa part.
            mediationResolved: "Support has settled this case. Nothing left to do on this order.",
            completed: "Payment captured. Nothing left to do on this order.",
            canceled: "The payment hold expired and was released. You can offer the client a second chance with new terms.",
            refundPending: "The cancellation was approved and the refund is being processed by Stripe.",
            refunded: "The payment was refunded to the client. Nothing else is required.",
        },
        left: "left",
        subtitle: "Your workspace — the client doesn't see this panel",
        // Côté client
        cancelOrder: "Cancel the order",
        requestCancellation: "Request cancellation",
        requestRefund: "Ask for a refund",
        /* Après un remboursement partiel, la somme mise en avant est ce qui reste
           acquis, et la sous-ligne rappelle d'où elle vient. */
        capturedAfterRefund: "Kept after refund",
        refundedOutOf: "{refunded} refunded out of {captured} collected",
        requestMediation: "Ask support to step in",
        contactSupport: "Contact support",
        viewInvoice: "View the invoice",
        youCanCancelFreely: "You can still cancel freely for",
        cancellationsBlockedClient: "The expert has locked cancellation requests while working on this order.",
        requesting: "Requesting…",
    },
    // ─── Liste des chats (expert) ────────────────────────────────────────────
    chatsPage: {
        subtitle: "Manage your client conversations",
        expired: "Expired",
        loading: "Loading chats...",
        loadFailed: "Failed to load chats",
        emptyAll: "No chat requests yet",
        emptyFiltered: "No {filter} chats",
        actionRequired: "Action Required ({count})",
        otherConversations: "Other Conversations",
        searchPlaceholder: "Search by name or chat ID...",
        anonymousClient: "Anonymous Client",
        respondBy: "Respond by:",
        deliveryDeadline: "Delivery Deadline:",
        lastActivity: "Last activity: {date}",
        view: "View",
        accept: "Accept",
        decline: "Decline",
        declineTitle: "Decline Chat",
        declineMessage: "Provide a reason for declining (optional):",
        declinePlaceholder: "Reason for declining...",
        openChat: "Open Chat",
        reason: "Reason: {reason}",
        // UIUX-1B — demande vérifiée en attente de réservation des fonds :
        // rien à décider, une seule relance possible, horodatée par le serveur
        awaitingFundsHint: "The client verified their email but hasn't reserved the funds yet — nothing to do until then",
        // Mission d'invitation (REFERRAL-3) : l'expert a déjà accepté
        invitationAwaitingFundsHint: "You accepted this mission. It starts as soon as the client's payment is reserved — nothing to do until then",
        // Le compte de paiement de l'expert ne peut pas encore encaisser : c'est
        // lui, et non le client, qui retient la réservation
        invitationAwaitingSetupHint: "You accepted this mission. To start it, finish setting up your payment account: the client's payment will then be reserved automatically.",
        finishPaymentSetup: "Set up payments",
        remindClient: "Send a reminder",
        reminderSentOn: "Reminder sent on {date}",
        // Entrées dérivées des états détaillés, définies dans @expertloop/shared.
        // « Actifs » réunit ce qui vit encore : en attente, en cours et litige.
        filter: {
            all: "All",
            active: "Active",
            waiting: "Waiting",
            in_progress: "In progress",
            dispute: "Dispute",
            completed: "Completed",
            canceled: "Canceled",
        },
        badge: {
            newRequest: "New Request",
            readyToCapture: "Ready to Capture",
            deliverService: "Deliver Service",
            awaitingConfirmation: "Awaiting Confirmation",
            revisionNeeded: "Revision Needed",
            underMediation: "Under Mediation",
            cancellationRequested: "Cancellation Requested",
        },
        // Surcouche mobile : les filtres et la taille de page quittent la barre
        // de recherche pour un panneau ancré en bas d'écran (lot 4, revue lot 1)
        filtersSheet: {
            open: "Filter the list",
            title: "Filters",
            status: "Status",
            pageSize: "Rows per page",
            done: "Show results",
        },
    },
    // ─── Liste des chats (client) ────────────────────────────────────────────
    clientChats: {
        // Page « Demandes envoyées » : des personnes et des missions, pas des
        // enregistrements — hiérarchie éditoriale de la maquette UIUX-1
        eyebrow: "Your client space",
        title: "Requests sent",
        subtitle: "Find your requests and pick up each mission where you left it.",
        followUp: "To follow",
        finished: "Finished",
        // Préfixe d'adressage devant le nom de l'expert : une demande est un
        // message adressé, comme un email (maquette corrections UI mobile)
        toPrefix: "To:",
        // Onglet vide sur téléphone : l'autre onglet peut, lui, contenir des demandes
        emptyGroup: "Nothing here for now.",
        // Libellés de statut en perspective client : qui a fait quoi. Les clés
        // partagées chatStatus.* restent neutres pour les autres écrans.
        status: {
            // Avant réservation, rien n'est parti : le focus est le brouillon,
            // pas le paiement
            draftNotSent: "Draft not sent",
            // UIUX-1B : email vérifié, la demande est enregistrée, reste la réservation
            awaitingFunds: "Funds to reserve",
            declinedByExpert: "Declined by the expert",
            // Couvre aussi le brouillon expiré de lui-même après 7 jours : la
            // demande n'a jamais été transmise, personne d'autre n'est impliqué
            abandonedByYou: "Abandoned by you",
        },
        reserved: "{amount} reserved",
        sentOn: "Sent {date}",
        // Un brouillon n'a jamais été envoyé : sa date est celle de sa création
        createdOn: "Created {date}",
        finishedOn: "Finished {date}",
        searchPlaceholder: "Search by expert name or chat ID...",
        defaultExpert: "Expert",
        emptyAll: "You haven't sent any request yet.",
        emptyHint: "When you contact an expert, you will find every step of your mission here.",
        badge: {
            newMessage: "New Message",
            awaitingResponse: "Awaiting Response",
            reviewDelivery: "Review Delivery",
            inProgress: "In Progress",
            paymentPending: "Payment Pending",
            beingRevised: "Being Revised",
            cancellationPending: "Cancellation Pending",
        },
    },
    // ─── Fil de discussion : entrées de journal ──────────────────────────────
    timeline: {
        theExpert: "The expert",
        theClient: "The client",
        reasonLabel: "Reason:",
        abandoned: {
            title: "Request Abandoned",
            body: "This request was abandoned before payment was completed.",
        },
        declined: {
            title: "Request Declined",
        },
        accepted: {
            title: "Request Accepted",
            expertBody: "You accepted this request. The client's funds are held.",
            clientBody: "Your request was accepted! The expert is now working on your service.",
        },
        deliveryAutoAccepted: {
            title: "Delivery Auto-Accepted",
            expertBody: "The delivery was automatically accepted after the review period expired. Payment has been captured.",
            clientBody: "The delivery was automatically accepted because you didn't respond in time. Payment has been processed.",
        },
        videoSessionEnded: {
            title: "Video session ended",
            body: "Duration: {duration}",
            bodyUnknown: "The video session has ended.",
        },
        deliveryViewed: {
            title: "Delivery Opened",
            expertBody: "The client opened your delivery and is reviewing it.",
            clientBody: "You opened the delivery. Please review and accept or reject.",
        },
        deliveryAccepted: {
            title: "Delivery Accepted",
            expertBody: "The client accepted your delivery.",
            clientBody: "You accepted the delivery. Thank you!",
        },
        paymentForm: {
            // Vocabulaire de réservation (UIUX-1) : jamais « blocage » ni « hold »
            title: "Reserve the funds",
            serviceAmount: "the service amount",
            holdBody: "We'll place a temporary hold of {amount} on your card. This is not a charge.",
            chargeNote: "You will only be charged when the expert delivers the service.",
        },
        cancellationDenied: {
            title: "Cancellation Declined",
            expertBody: "You declined the client's cancellation request. The order stays active and your delivery is still expected.",
            // Le client a payé, demandé à sortir, et s'est vu opposer un refus. Lui
            // annoncer le refus sans dire ce qui se passe ensuite est le moment où
            // la confiance se perd : la phrase dit la décision, l'état de la
            // commande, et ce qu'il lui reste à faire.
            clientBody: "The expert declined your cancellation request. Your order stays active and the expert is still expected to deliver.",
            clientNext: "If you think this decision is unfair, you can ask support to step in.",
            requestMediation: "Ask support to step in",
        },
        mediationRequested: {
            title: "Mediation Requested",
            underReview: "Under Review",
            body: "{requestedBy} requested support mediation.",
            notice: "All delivery actions are locked while support reviews this case. You can still send messages.",
        },
        mediationReopened: {
            title: "Mediation Reopened",
            underReview: "Under Review",
            body: "Support has reopened this case.",
            notice: "Delivery and payment actions stay locked until support decides again. You can still send messages.",
        },
        cancellation: {
            expertBody: "The client has requested to cancel this order. Please review and respond.",
            clientBody: "You requested cancellation. Waiting for expert response...",
            waiting: "Waiting for expert response",
        },
        deliveryCanceled: {
            title: "Delivery Canceled",
            expertBody: "You canceled the delivery to submit a new one.",
            clientBody: "The expert canceled their delivery to submit a new one.",
            waiting: "Waiting for the expert to submit a new delivery...",
        },
        deliveryRejected: {
            title: "Delivery Rejected",
            body: "The client rejected the delivery.",
            waiting: "Waiting for the expert to redeliver...",
        },
        chatClosed: {
            title: "Chat Closed",
            expertBody: "You closed this conversation.",
            clientBody: "This conversation has been closed by the expert.",
            reopening: "Reopening...",
            reopen: "Reopen Chat",
        },
        paymentCaptured: {
            title: "Payment Captured",
            invoicePdf: "Invoice PDF",
            viewInvoice: "View Invoice",
        },
        paymentRefunded: {
            title: "Payment Refunded",
            pendingTitle: "Refund in Progress",
        },
        supportDecision: {
            title: "Support Decision",
            translatedFrom: "Translated from {language} · indicative",
            viewOriginal: "View original",
            viewTranslation: "View translation",
            refunded: "Refunded",
            paymentReleased: "Payment Released",
            favorClient: "Support has ruled in favor of the client. The payment has been refunded.",
            /* Fonds seulement réservés : rien n'a été débité, il n'y a donc rien à
               rembourser. Annoncer un remboursement ferait attendre un virement. */
            favorClientReleased: "Support has ruled in favor of the client. The hold on the card has been released, so nothing was charged.",
            // Mission gratuite (REFERRAL-4)
            freeMission: "Free mission",
            freeMissionText: "This mission was free of charge: no payment was involved.",
            favorExpert: "Support has ruled in favor of the expert. The payment has been released.",
            footer: "This mediation case has been resolved by the ExpertLoop support team.",
            // Recours après une décision : seul le support peut rouvrir un dossier
            contestHint: "New information about this case? Tell the support team, who can reopen it.",
            contestLink: "Contact support",
            /*
             * Issues distinctes d'une décision (BL-G4). Les trois textes ci-dessus
             * étaient choisis sur le seul état de la commande : une vente encaissée
             * puis remboursée en partie reste « encaissée », si bien que le fil
             * annonçait au client que rien ne lui avait été facturé alors qu'il
             * venait d'être remboursé.
             */
            partiallyRefunded: "Partially refunded",
            noRefund: "No refund",
            captured: "Payment captured",
            favorClientPartial: "Support has ruled in favor of the client. {refunded} of the {captured} charged has been refunded, leaving {net} with the expert.",
            favorClientFull: "Support has ruled in favor of the client. The full {captured} charged has been refunded.",
            favorExpertKept: "Support has ruled in favor of the expert. No refund was issued, and the {net} already charged stays with the expert.",
            favorExpertCaptured: "Support has ruled in favor of the expert. {captured} has been charged and will be paid out to the expert.",
            releasedNote: "Separately, {released} of the original hold was never charged and has been released.",
        },
        orderDeclined: {
            autoExpiredExpert: "The delivery deadline passed. The payment has been automatically released back to the client.",
            autoExpiredClient: "The delivery deadline passed. Your payment has been automatically refunded.",
            declinedByExpertForClient: "The expert declined this order. Your payment hold has been released.",
            declinedByExpertForExpert: "You declined this order. The payment hold has been released.",
            canceledByClientForClient: "You canceled this order. Your payment hold has been released.",
            canceledByClientForExpert: "The client canceled this order. The payment hold has been released.",
            expiredTitle: "Order Expired",
            declinedTitle: "Order Declined",
            canceledTitle: "Order Canceled",
            released: "Released",
        },
        pendingPayment: {
            title: "Hold Funds to Submit Request",
            holdBody: "To submit your request, we'll place a temporary hold of {amount} on your card. This is not a charge — your funds are simply reserved.",
            chargeNote: "You will only be charged if the expert accepts and delivers the service. If the expert declines, the hold will be released automatically.",
            respondsWithin: "Expert typically responds within {time}",
            canceling: "Canceling...",
            cancelRequest: "Cancel Request",
        },
        declineFeedback: {
            submittedTitle: "Feedback Submitted",
            indicated: "You indicated this decline was",
            professional: "handled professionally",
            unprofessional: "not handled professionally",
            thanks: "Thank you for your feedback.",
            question: "Was this decline handled professionally?",
            helpNote: "Your feedback helps us maintain platform quality.",
            submitFailed: "Failed to submit feedback",
        },
        rating: {
            clientRating: "Client's Rating",
            yourRating: "Your Rating",
            title: "Rating",
            waitingForClient: "Waiting for client to submit their rating...",
            rateExperience: "Rate Your Experience",
            howWasExperience: "How was your experience with this expert?",
            commentPlaceholder: "Leave a comment (optional)",
            submitting: "Submitting...",
            submit: "Submit Rating",
            // Note évolutive (WP A) : une seule note courante, modifiable pendant la fenêtre
            change: "Change my rating",
            save: "Save my rating",
            cancelChange: "Keep my rating",
            updatedFrom: "Changed from {previous} out of 5",
            locked: "This rating can no longer be changed.",
            // Évaluation progressive (WP B) : tout est facultatif sauf la note
            facetsTitle: "Optional details",
            facetsHint: "Rate only what you were able to judge, and skip the rest.",
            clearFacet: "Clear",
            testimonialHint: "Your words can help future clients choose this expert.",
            publishLabel: "Show my testimonial on the expert's profile",
            displayNameLabel: "Name shown with your testimonial",
            displayNameHint: "Choose the name you want to appear under. Your email address is never shown.",
            displayNameRequired: "Choose a name to continue",
            publishedAs: "Shown on the expert's profile as {name}",
            privateTestimonial: "Your testimonial is private",
            stopPublishing: "Stop showing it",
            facetsGiven: "Details you rated",
            // Aide à la rédaction (WP C) : au clic, jamais d'office, jamais publiée par l'assistant
            assist: {
                open: "Writing help",
                draft: "Suggest a draft",
                rewrite: "Reword my text",
                needRating: "Choose your overall rating first, so the draft matches how you feel.",
                working: "Preparing a suggestion...",
                proposalTitle: "Suggested text",
                notice: "You stay in control: nothing changes until you choose to use this text, and you can edit it freely.",
                use: "Use this text",
                dismiss: "Dismiss",
                unavailable: "Writing help is not available right now. Your text is unchanged.",
            }
        },
        paymentAuthorized: {
            title: "Funds Held",
            reserved: "Reserved on card",
            youCanCancel: "You can cancel freely for",
            clientCanCancel: "Client can cancel freely for",
            windowEnded: "Free cancellation window ended",
            deliverBy: "Deliver by {date}",
            deadlinePassed: "Deadline passed",
        },
        pending: {
            title: "Awaiting Expert Review",
            expertBody: "A client has placed a hold on their payment and is requesting your services.",
            expertNote: "If you decline, the hold on their card will be released automatically.",
            clientBody: "Your funds are held securely — you have not been charged yet.",
            clientNote: "You will only be charged if the expert accepts and delivers the service. If the expert declines, the hold will be released automatically.",
            expectedResponse: "Expected response within {time}",
            responseExceeded: "Response time exceeded",
        },
        request: {
            files: "Attached to this request",
            // Mission gratuite (REFERRAL-4) : à côté du prix barré
            free: "Free",
            title: "Service Request",
            updated: "Updated {time}",
            message: "Message:",
            editPlaceholder: "Describe what you need help with...",
            updateFailed: "Failed to update",
            // « Modifier », pas « Terminer » : la demande est déjà envoyée, le
            // client la retouche (maquette wizard, corrige une traduction trompeuse)
            completeRequest: "Edit my request",
            editHint: "You can edit and complete your request until the expert delivers. The expert is notified of every change.",
        },
        retryOffer: {
            accepted: "Retry Accepted",
            declined: "Retry Declined",
            expiredTitle: "Retry Offer Expired",
            sentTitle: "Retry Offer Sent",
            secondChanceOffered: "Second Chance Offered",
            amountLabel: "Amount:",
            deliveryTimeLabel: "Delivery time:",
            expertWaiting: "Waiting for client response...",
            expertAccepted: "The client accepted! A new hold is being placed on their card.",
            expertDeclined: "The client declined the retry offer.",
            expiredNoResponse: "The retry offer expired without a response.",
            clientOfferedBody: "The expert has offered a second chance with updated terms:",
            accepting: "Accepting...",
            acceptAndPay: "Accept & Pay",
            clientAccepted: "You accepted the retry offer. Please complete the payment to proceed.",
            clientDeclined: "You declined the retry offer.",
            hasExpired: "The retry offer has expired.",
            awaitingExpert: "Awaiting Expert Response",
            awaitingExpertBody: "The delivery deadline passed. The expert may offer a second chance with updated terms.",
        },
    },
    chatInputState: {
        awaitingAuthClient: "Please authorize payment to submit your request...",
        awaitingAuthExpert: "Waiting for client to authorize payment...",
        // Mission d'invitation : l'expert a déjà accepté, et la carte du client est enregistrée
        invitationAwaitingSetup: "Finish setting up your payment account: the client's payment will then be reserved and the mission will start.",
        invitationAwaitingReservation: "The mission starts as soon as the client's payment is reserved.",
        abandoned: "This request was abandoned",
        awaitingAcceptance: "Waiting for the expert to accept your request...",
        paymentProcessing: "Payment is being processed...",
        awaitingPaymentAuth: "Waiting for payment authorization...",
    },
    chatPage: {
        authenticating: "Authenticating...",
        loading: "Loading chat...",
        notFound: "Chat not found",
        error: {
            accept: "Failed to accept",
            // Course entre le clic « Accepter » de l'expert et l'annulation libre du
            // client : la demande n'existe plus, l'écran se resynchronise ensuite.
            acceptCanceledOrder: "This request can no longer be accepted: the client canceled their order in the meantime, and the hold on their card has been released.",
            decline: "Failed to decline",
            abandon: "Failed to abandon chat",
            confirmDelivery: "Failed to confirm delivery",
            capturePayment: "Failed to capture payment",
            approveCancellation: "Failed to approve cancellation",
            requestRefund: "Failed to send the refund request",
            denyCancellation: "Failed to deny cancellation",
            cancelOrder: "Failed to cancel order",
            submitRating: "Failed to submit rating",
            acknowledgeDelivery: "Failed to confirm receipt",
            appreciateDelivery: "Failed to send your feedback",
            createComplement: "Failed to share the additional delivery",
            reopenChat: "Failed to reopen chat",
            closeChat: "Failed to close chat",
            rejectDelivery: "Failed to reject delivery",
            viewDelivery: "Failed to view delivery",
            acceptDelivery: "Failed to accept delivery",
            cancelDelivery: "Failed to cancel delivery",
            requestMediation: "Failed to request mediation",
            offerRetry: "Failed to offer retry",
            acceptRetry: "Failed to accept retry",
            declineRetry: "Failed to decline retry",
            updateCancellationSetting: "Failed to update cancellation setting",
            verifyPayment: "Failed to verify payment",
        },
    },
    // Proposition assistée de connaissance métier et d'intake (AI-1B4A). L'IA propose,
    // l'expert valide : aucune de ces chaînes ne doit laisser croire à une publication
    // automatique.
    productKnowledge: {
        title: "Business knowledge",
        subtitle: "What this service covers, and what the client must provide.",
        generate: "Draft with AI",
        regenerate: "Draft again",
        generating: "Drafting…",
        generateHelp: "The assistant reads this saved listing and drafts a proposal. Nothing is saved to your service until you review and apply it.",
        generateFailed: "The proposal could not be generated. Nothing was changed.",
        disabled: "AI drafting is not available yet on this environment.",
        empty: "No proposal yet.",
        proposalTitle: "Proposal — not applied",
        proposalNotice: "Review and edit this before applying. Nothing here is active.",
        builtWithDialogue: "Built from your saved sheet and your workshop dialogue of {date}.",
        // ─── Conduite de l'assistance client (CONV-3, AI-D054) ───────────────
        conduct: {
            title: "Client assistance conduct",
            subtitle: "Two settings, under your sole authority, that shape how the AI assists your clients for this service.",
            depthLabel: "How much context do you expect in a request?",
            depthMinimal: "A short request is enough",
            depthStandard: "A reasonably complete request",
            depthDetailed: "A rich, detailed context is generally needed",
            intensityLabel: "How much should the AI assist your clients?",
            intensityNone: "No assistance — clients write freely",
            intensityLight: "Light — brief, high-value clarifications",
            intensityActive: "Active — guide clients to provide what you need",
            noneNote: "The request funnel will no longer offer AI assistance for this service.",
            save: "Save these settings",
            saving: "Saving…",
            saveFailed: "The settings could not be saved. Please try again.",
        },
        validatedTitle: "Currently active",
        validatedEmpty: "Nothing is active for this service yet.",
        knowledgeSection: "Knowledge",
        intakeSection: "Intake",
        scope: "Scope",
        audience: "Audience",
        problemsAddressed: "Problems addressed",
        deliverables: "Deliverables",
        exclusions: "Out of scope",
        assistantGuidance: "Private guidance to your assistants",
        requirementRequired: "Required",
        requirementOptional: "Optional",
        requirementPrivatePurpose: "Why you need it (private)",
        requirementSensitive: "Sensitive",
        staleProposal: "This listing changed after the proposal was drafted. You can still apply it — it will be recorded as describing the earlier version — or draft again.",
        staleValidated: "This listing changed after this was validated. Review it when you can.",
        apply: "Apply",
        applyKnowledge: "Apply the knowledge",
        applyIntake: "Apply the intake",
        applyBoth: "Apply both",
        applying: "Applying…",
        applyFailed: "The proposal could not be applied. Nothing was changed.",
        applied: "Applied. This is now the active version.",
        appliedAt: "Applied on {date}",
        reject: "Discard the proposal",
        rejectFailed: "The proposal could not be discarded.",
        selectAtLeastOne: "Choose at least one block to apply.",
    },
    productsPage: {
        // Changement volontaire de slug (spec accès client §4.5)
        slugLabel: "Service link",
        slugHelp: "The address of this service in your store. Changing it keeps the old link working as a redirect.",
        slugSave: "Change the link",
        slugSaving: "Saving…",
        slugSaved: "Service link updated — the old one now redirects.",
        slugTaken: "Another of your services already uses this link.",
        slugInvalid: "This link could not be saved. Use letters, digits and hyphens.",
        title: "Products",
        loading: "Loading products...",
        reorderTitle: "Reorder Products",
        saving: "Saving...",
        saveOrder: "Save Order",
        reorder: "Reorder",
        addProduct: "Add Product",
        editProduct: "Edit Product",
        newProduct: "New Product",
        name: "Name",
        maxDelivery: "Max Delivery",
        deliveryUnit: "Delivery unit",
        unitHours: "hours",
        unitDays: "days",
        description: "Description",
        descriptionPlaceholder: "Describe your product or service...",
        generateWithAi: "Generate with AI",
        aiSheetApplied: "The AI-generated sheet has been applied — review it, then save.",
        prerequisites: "Prerequisites (optional)",
        prerequisitesHelp: "List what the client should provide so you can answer quickly and accurately. Shown as a checklist when they write their request.",
        // Fiche dotée d'un intake : la liste devient une projection en lecture seule
        // (AI-D032). Elle n'est plus une entrée indépendante, et n'est plus envoyée.
        prerequisitesDerived: "Prerequisites (from your intake)",
        prerequisitesDerivedHelp: "These come from the intake you validated for this service. Edit the intake to change them — editing them here would create a second, conflicting source.",
        prerequisitePlaceholder: "Prerequisite {number} — e.g. \"Describe your current situation\"",
        addPrerequisite: "Add prerequisite",
        price: "Price",
        currency: "Currency",
        update: "Update",
        create: "Create",
        backToProducts: "Back to products",
        previewTitle: "Product preview",
        previewHelp: "This is how the current form values will be presented to the client.",
        previewUntitled: "Untitled product",
        empty: "No products yet. Create your first product to start receiving chat requests.",
        active: "Active",
        inactive: "Inactive",
        private: "Private",
        privateHint: "Created by ExpertLoop for a specific client. It does not appear in your store until you publish it.",
        deactivate: "Deactivate",
        activate: "Activate",
        // Prestation privée née d'une invitation, à publier (REFERRAL-3B)
        publishPrivate: "Publish in my store",
        privateEditHint: "You can edit this service before or after publishing it.",
        editShort: "Edit",
        deleteTitle: "Delete Product",
        deleteConfirm: "Are you sure you want to delete \"{name}\"?",
        createFailed: "Failed to create product",
        updateFailed: "Failed to update product",
        invalidPrice: "Please enter a valid price",
        invalidDeliveryTime: "Delivery time must be between 1 hour and 7 days",
        invalidDescription: "Please enter a description",
    },
    // ─── Emails transactionnels (docs/email/email-i18n-spec.md §7.3, lots 1 et 2) ──
    // Résolues côté API par destinataire (resolveEmailLocalization). Les URLs
    // ne sont jamais dans le catalogue. Les valeurs interpolées porteuses de
    // balisage (<strong>…</strong>) sont fournies par les templates ; seule
    // exception : un gras qui couvre une proposition entière de la phrase
    // (ex. deliveryUrgent.intro) reste dans le message, une phrase fragmentée
    // en plusieurs clés se traduirait plus mal qu'une balise inline.
    email: {
        common: {
            greeting: "Hi {name},",
            footer: "© {year} ExpertLoop. All rights reserved.",
        },
        verification: {
            subject: "Verify Your Email - ExpertLoop",
            title: "Verify Your Email",
            intro: "Thank you for creating an account with ExpertLoop. Please verify your email address by clicking the button below:",
            button: "Verify Email Address",
            expiry: "This link will expire in {count} hour{plural}.",
            ignore: "If you didn't create an account with ExpertLoop, you can safely ignore this email.",
            fallbackLink: "If the button doesn't work, copy and paste this link into your browser:",
        },
        welcome: {
            subject: "Welcome to ExpertLoop!",
            title: "Welcome, {firstName}!",
            thanks: "Thank you for joining ExpertLoop. We're excited to have you on board!",
            intro: "ExpertLoop connects experts with clients who need their help. Whether you're here to offer your expertise or to get help from professionals, we've got you covered.",
            button: "Go to Dashboard",
            support: "If you have any questions, feel free to reach out to our support team.",
        },
        newChatRequest: {
            title: "New Chat Request",
            intro: "You have a new chat request for your product {productName}.",
            clientLabel: "Client:",
            messageLabel: "Message:",
            button: "View Request",
            respondSoon: "Please respond to this request as soon as possible.",
        },
        chatAccepted: {
            title: "Good News!",
            intro: "Great news! {expertName} has accepted your request for {productName}.",
            next: "You can now continue the conversation and work together on your project.",
            button: "Continue to Chat",
        },
        chatDeclined: {
            title: "Request Update",
            intro: "Unfortunately, {expertName} was unable to accept your request for {productName} at this time.",
            reasonLabel: "Reason:",
            encouragement: "Don't worry! There are many other experts available who might be able to help you.",
            button: "Browse Other Experts",
            historyLink: "View conversation history",
        },
        deliveryAvailable: {
            title: "Your Delivery is Ready!",
            intro: "Great news! {expertName} has delivered their work for {productName}.",
            review: "Please review the delivery and confirm that everything is to your satisfaction. You have {count} hour{plural} to review before the delivery is automatically accepted.",
            button: "Review Delivery",
        },
        // Livraison complémentaire (WP A) : ne demande aucune validation et n'annonce
        // aucune acceptation automatique, d'où un gabarit distinct de deliveryAvailable.
        // Invitation facultative à évaluer (WP C) : écrite par la plateforme seule, sans rien
        // reprendre de l'expert, et sans jamais évoquer une médiation ni sa décision.
        ratingInvitation: {
            title: "How did your mission go?",
            intro: "Your mission \"{productName}\" is complete. If you would like to, you can rate it and tell others how it went.",
            reminderTitle: "A quick reminder to rate your mission",
            reminderIntro: "You have not rated your mission \"{productName}\" yet. If you would like to, it only takes a moment.",
            optional: "This is entirely optional. Your rating and your words stay yours: nothing is published without your permission.",
            button: "Rate my mission",
            unsubscribe: "Stop receiving these invitations",
        },
        deliveryComplement: {
            title: "An additional delivery is available",
            intro: "{expertName} has shared an additional delivery for {productName}, after your earlier delivery was accepted.",
            messageLabel: "Message from {expertName}:",
            messageTruncated: "The message is cut here. Open the conversation to read it in full.",
            note: "This is an extra from your expert. It does not change your order or your payment, and there is nothing to approve. You can open it whenever you like.",
            button: "Open the additional delivery",
        },
        deliveryConfirmed: {
            title: "Delivery Confirmed!",
            intro: "Great news! {clientName} has confirmed the delivery for {productName}.",
            payment: "The payment has been automatically captured and will be transferred to your connected Stripe account.",
            button: "View Chat",
        },
        // UIUX-1B — email de sécurisation après vérification de l'adresse :
        // il porte les trois rendus du même secret de reprise (bouton, QR, code)
        guestAccess: {
            title: "Your request is saved — here is your access",
            intro: "Your email address is verified and your request to {expertName} for {productName} is safely stored. You can now finish it whenever you want, from any device.",
            keepEmail: "Keep this email: it is your way back to your request.",
            button: "Resume my request",
            qrAlt: "QR code to open your request on another device",
            qrHint: "Scan this code to open your request on your phone or another device.",
            codeLabel: "If you ever need it, your backup recovery code:",
            codeHint: "Enter this code at {resumeUrl} to get back to your request.",
            securityNote: "This access is personal to your request. Don't share this email — anyone with the link or code can open your request.",
        },
        // UIUX-1B — relance expert unique d'une demande en attente de fonds :
        // strictement transactionnelle, un seul appel à l'action
        fundsReminder: {
            title: "Your request is waiting for you",
            intro: "Your request to {expertName} for {productName} is saved, and {expertName} has seen it. It hasn't been sent yet: the funds still need to be reserved.",
            next: "Reserving the funds sends your request. Nothing is charged until the work is delivered.",
            button: "Finish my request",
            note: "You received this email because you started a request on ExpertLoop. If you no longer want it, you can simply ignore this message.",
        },
        // ── Lot 2 — le reste des templates (sauf boîte support, non traduite) ──
        labels: {
            product: "Product:",
            client: "Client:",
            expert: "Expert:",
            amount: "Amount:",
            amountRefunded: "Amount refunded:",
            amountReleased: "Amount released:",
            deadline: "Deadline:",
        },
        // Durées composées côté API (rappels, urgences)
        duration: {
            days: "{count} day{plural}",
            hours: "{count} hour{plural}",
            minutes: "{count} minute{plural}",
            hoursAndMinutes: "{hours} and {minutes}",
        },
        shared: {
            viewChat: "View Chat",
            goToDashboard: "Go to Dashboard",
            historyLink: "View conversation history",
            refundDelay: "The refund should appear in your account within 5-10 business days, depending on your bank.",
        },
        passwordReset: {
            subject: "Reset Your Password - ExpertLoop",
            title: "Reset Your Password",
            intro: "We received a request to reset your password. Click the button below to create a new password:",
            button: "Reset Password",
            expiry: "This link will expire in {count} hour{plural} for security reasons.",
            ignore: "If you didn't request a password reset, you can safely ignore this email. Your password will remain unchanged.",
            fallbackLink: "If the button doesn't work, copy and paste this link into your browser:",
        },
        adminPasswordReset: {
            subject: "Reset Your Administration Password - ExpertLoop",
            title: "Reset Your Administration Password",
            intro: "We received a request to reset the password of your ExpertLoop administration account. Click the button below to choose a new password:",
            button: "Choose a New Password",
            expiry: "This link can be used once and will expire in {count} minute{plural} for security reasons.",
            ignore: "If you didn't request this, do not click the link: your password remains unchanged. Please tell another administrator, as someone may be trying to access your account.",
            fallbackLink: "If the button doesn't work, copy and paste this link into your browser:",
        },
        expertInvitation: {
            subject: "{clientName} would like to work with you - ExpertLoop",
            title: "{clientName} would like to work with you",
            greetingAnonymous: "Hello,",
            intro: "{clientName} found you and would like to entrust you with a request on ExpertLoop, for {price}. Here is what they wrote:",
            button: "Read the request",
            // Adresse du bouton écrite en clair : l'expert voit que le lien est authentique
            linkInClear: "The button opens this address on ExpertLoop:",
            noCommitment: "Reading the request does not create an account and commits you to nothing. You will decide afterwards whether you accept it.",
            expiry: "This invitation remains open for {count} day{plural}.",
            ignore: "If you do not know this person or are not interested, you can ignore this email.",
        },
        expertInvitationDeclined: {
            subject: "Your invitation was declined - ExpertLoop",
            title: "Your invitation was declined",
            intro: "The expert you invited has declined your request.",
            introNamed: "{expertName} has declined your request.",
            reasonLabel: "Their message:",
            next: "Nothing has been reserved or charged. You can invite someone else or look for an expert already on ExpertLoop.",
            button: "See my invitations",
        },
        // L'expert invité propose plutôt une de ses prestations (REFERRAL-3B)
        expertInvitationRedirected: {
            subject: "The expert you invited suggests one of their services - ExpertLoop",
            title: "{expertName} suggests one of their services",
            intro: "{expertName} read your request and suggests their service {productName}, at {price}.",
            messageLabel: "Their message:",
            button: "See the service",
            store: "You can also browse all their services: {link}.",
            storeLink: "their store",
            nothingCharged: "Nothing has been reserved or charged. Your request text will be ready to send on the service page.",
        },
        // Réservation sans le client impossible (PAY-REFERRAL-2). Le motif reste
        // général : aucun code bancaire n'est cité.
        reservationActionRequired: {
            subject: "Please confirm your payment - ExpertLoop",
            title: "Your confirmation is needed",
            intro: "{expertName} has accepted your request. To start the mission, {amount} must be reserved on your card, and we could not do it without you.",
            bankConfirmation: "Your bank asks you to confirm this payment yourself. It only takes a moment.",
            cardDeclined: "Your bank declined the card you prepared. You can use another card.",
            cardUnavailable: "The card you prepared can no longer be used. You can use another card.",
            button: "Confirm the reservation",
            noCharge: "The amount is only reserved, not charged. You are charged once the mission is delivered and accepted. The expert does not need to do anything again.",
            expiry: "This link is valid for {count} day{plural}.",
        },
        // Mission acceptée mais pas encore financée (PAY-REFERRAL-2) : l'expert
        // n'a rien à refaire, et ne reçoit aucun détail sur le refus bancaire.
        reservationPendingExpert: {
            subject: "Your mission is waiting for the client's payment - ExpertLoop",
            title: "The payment is not reserved yet",
            intro: "You accepted the request from {clientName}. The {amount} could not be reserved automatically, so we have asked the client to confirm the payment.",
            nothingToDo: "Your acceptance is kept and you have nothing to do again: the mission starts as soon as the amount is reserved.",
            button: "Go to my dashboard",
            noWork: "We recommend waiting for the reservation before starting to work.",
        },
        // Mission d'invitation jamais financée, close d'office : le client seul
        // relance l'expert s'il le souhaite (arbitrage du 1er octobre 2026)
        invitationMissionExpired: {
            subject: "Your mission was closed without starting - ExpertLoop",
            title: "Your mission did not start",
            intro: "{expertName} accepted your request, but the {amount} could not be reserved within {count} days, so we have closed the mission.",
            followUp: "If you still want to work with {expertName}, get in touch with them: they may need to finish setting up their payment account. You can then send them a new invitation.",
            button: "See my invitations",
            nothingCharged: "Nothing was charged to your card.",
        },
        // Fin d'une mission gratuite (REFERRAL-4) : aucun montant, rien d'encaissé
        freeMissionEnded: {
            completedTitle: "Your free mission is complete - ExpertLoop",
            completedIntro: "The mission {productName} with {name} is complete: the delivery has been accepted.",
            expiredTitle: "Your free mission has ended - ExpertLoop",
            expiredIntro: "The mission {productName} with {name} has ended because the delivery deadline has passed.",
            canceledTitle: "Your free mission has been canceled - ExpertLoop",
            canceledIntro: "The mission {productName} with {name} has been canceled.",
            noPayment: "This mission was carried out free of charge: no amount was reserved, charged or refunded.",
        },
        // Mise en route d'une mission d'invitation (REFERRAL-3) : les fonds sont
        // réservés, rien n'est débité, et l'expert n'a rien à accepter de nouveau.
        invitationMissionStartedClient: {
            subject: "Your mission has started - ExpertLoop",
            title: "Your mission has started",
            intro: "{expertName} accepted your request, and the {amount} you proposed is now reserved on your card.",
            deadline: "The delivery is expected by {date}.",
            button: "Open the conversation",
            notCharged: "Nothing has been charged yet: you are only charged once you accept the delivery.",
            // Mission gratuite (REFERRAL-4)
            introFree: "{expertName} accepted your request and will carry it out free of charge.",
            freeNote: "This mission is free of charge: nothing will be reserved or charged.",
        },
        invitationMissionStartedExpert: {
            subject: "Your mission is funded and starts now - ExpertLoop",
            subjectFree: "Your free mission starts now - ExpertLoop",
            title: "Your mission starts now",
            intro: "The {amount} proposed by {clientName} is now reserved. You can start working.",
            introFree: "You accepted to carry out the request from {clientName} free of charge. You can start working.",
            deadline: "Please deliver by {date}.",
            maximum: "The reserved amount is a maximum: when you deliver, you can charge less if the work turned out lighter.",
            button: "Open the conversation",
            nothingToAccept: "You already accepted this request: there is nothing to accept again.",
        },
        clientVerificationCode: {
            subject: "Your Verification Code - ExpertLoop",
            title: "Your Verification Code",
            intro: "Use this code to confirm your email address:",
            expiry: "This code will expire in {count} minute{plural}.",
            ignore: "If you didn't request this code, you can safely ignore this email.",
        },
        paymentCaptured: {
            expertTitle: "Payment Received!",
            expertIntro: "Great news! Your client has confirmed the delivery and your payment has been captured.",
            expertTransfer: "The payment has been captured and will be transferred to your connected Stripe account.",
            viewOrders: "View Orders",
            clientTitle: "Delivery Accepted - Payment Complete",
            clientIntro: "Thank you for confirming the delivery! Your payment has been processed and the expert has been paid.",
            clientReview: "We hope you had a great experience! If you have a moment, please consider leaving a review for {expertName} to help other clients.",
            clientThanks: "Thank you for your trust in ExpertLoop!",
            // Variante capture automatique (fin de fenêtre de relecture sans réponse)
            expertIntroAuto: "The review period ended without a response from your client: the delivery was confirmed automatically and your payment has been captured.",
            clientIntroAuto: "The review period for this delivery has ended. It was confirmed automatically and your payment has been processed.",
        },
        orderExpired: {
            expertTitle: "Order Expired",
            expertIntro: "Unfortunately, the delivery deadline for an order has passed, and the payment has been automatically released back to the client.",
            expertAdvice: "To avoid this in the future, make sure to deliver your services and capture payments within the specified delivery timeframe.",
            clientTitle: "Payment Refunded",
            clientIntro: "The delivery deadline for your order has passed, and your payment has been automatically refunded.",
            clientBrowse: "We apologize for any inconvenience. You're welcome to browse other experts on ExpertLoop to find the help you need.",
        },
        paymentAuthorizationExpired: {
            expertTitle: "Payment Authorization Expired",
            expertIntro: "Your client's bank canceled the payment authorization for this order before you captured it, faster than the delivery deadline you had. This is a limit set by the client's bank or card issuer, not something you missed — the order has been automatically canceled and the client's payment has been released.",
            expertRetryAdvice: "You can offer the client a retry with a shorter delivery time from the chat, so the payment can be captured before a new authorization expires.",
            clientTitle: "Payment Refunded",
            clientIntro: "Your bank's payment authorization for this order expired before the expert captured the payment, so your payment has been automatically refunded.",
        },
        authorizationExpiringWarning: {
            title: "Capture Payment Soon",
            intro: "It's been {count} hour{plural} since this order was authorized. Some banks cancel a payment authorization earlier than the delivery deadline you were given — to avoid losing this payment, we recommend delivering and capturing it as soon as possible, rather than waiting for the full delivery window.",
            button: "Go to Chat",
        },
        deliveryUrgent: {
            title: "Delivery Deadline Approaching!",
            intro: "<strong>You have {timeRemaining} left</strong> to deliver your service and capture the payment for this order. If you don't capture the payment by the deadline, the order will be <strong>automatically canceled</strong> and the client will be refunded.",
            button: "Deliver Now",
            captureNote: "Once you've delivered your service, click \"Capture Payment\" in the chat to complete the order and receive your payment.",
        },
        cancellationRequested: {
            title: "Cancellation Requested",
            intro: "{clientName} has requested to cancel their order. Please review and respond at your earliest convenience.",
            button: "Review Request",
            note: "You can either approve the cancellation or deny it with a reason explaining why you've already started work.",
        },
        /* Après l'encaissement, il n'y a plus rien à annuler : le client demande
           qu'on lui rende de l'argent. Les deux situations n'appellent ni le même
           mot, ni la même décision. */
        refundRequested: {
            title: "Refund Requested",
            intro: "{clientName} has asked for a refund on a service you already delivered. You decide how much to give back, in full or in part.",
            button: "Review Request",
            note: "You can refund all or part of the amount collected, or decline with a reason. The ExpertLoop commission is returned in the same proportion.",
            reasonLabel: "What the client says:",
        },
        // Décision de l'expert sur la demande d'annulation — email au client
        cancellationApproved: {
            title: "Cancellation Approved",
            intro: "{expertName} has approved your cancellation request for {productName}.",
            authorizationReleased: "The payment authorization of {amount} has been released. Depending on your bank, it can take a few days to disappear from your statement.",
            refundPending: "The refund of {amount} has been initiated. It will appear in your account according to your bank's processing time.",
            refunded: "The payment of {amount} has been refunded. It will appear in your account according to your bank's processing time.",
            button: "View Chat",
        },
        refundApproved: {
            title: "Refund Approved",
            intro: "{expertName} has agreed to refund you for {productName}.",
            partial: "{refunded} of the {captured} you paid has been refunded. It will appear in your account according to your bank's processing time.",
            full: "The full {refunded} you paid has been refunded. It will appear in your account according to your bank's processing time.",
            pending: "A refund of {refunded} has been initiated. It will appear in your account according to your bank's processing time.",
            button: "View Chat",
        },
        refundDenied: {
            title: "Refund Request Declined",
            intro: "{expertName} has declined your refund request for {productName}.",
            reasonLabel: "Reason:",
            note: "If you believe this decision is unfair, you can request mediation from the chat.",
            button: "View Chat",
        },
        cancellationDenied: {
            title: "Cancellation Request Declined",
            intro: "{expertName} has declined your cancellation request for {productName}. The order remains active and the expert will deliver as planned.",
            reasonLabel: "Reason:",
            note: "If you believe this decision is unfair, you can request mediation from the chat once the delivery is available.",
            button: "View Chat",
        },
        // Médiation : accusé de réception au demandeur, avis à la partie adverse
        mediationAcknowledged: {
            title: "Mediation Request Received",
            intro: "We have received your mediation request for {productName}.",
            next: "Our support team will review the conversation and the delivery history, then make a decision. Both parties will be notified of the outcome.",
            button: "View Chat",
        },
        // Réouverture d'un litige tranché par le support (MEDIATION-1) : reçu par l'expert et le client
        mediationReopened: {
            title: "Mediation Reopened",
            intro: "Our support team has reopened the mediation regarding the order for {productName}.",
            next: "The case is under review again. Delivery and payment actions stay locked until support makes a new decision, and both parties will be notified of the outcome. You can still send messages in the conversation.",
            button: "View Chat",
        },
        mediationOpened: {
            title: "Mediation Requested",
            intro: "A mediation has been requested regarding the order for {productName}.",
            next: "Our support team will review the conversation and the delivery history, then make a decision. Both parties will be notified of the outcome.",
            button: "View Chat",
        },
        // Sécurité du compte : confirmation de changement de mot de passe
        passwordChanged: {
            subject: "Your ExpertLoop password was changed",
            title: "Password Changed",
            intro: "The password of your ExpertLoop account has just been changed.",
            warning: "If you did not make this change, reset your password immediately — your account may be compromised.",
            button: "Reset Password",
        },
        // Le client a modifié sa demande avant livraison
        requestUpdated: {
            title: "Request Updated",
            intro: "{clientName} has updated their request for {productName}. Review the changes before continuing.",
            messageLabel: "Updated request:",
            button: "Review Request",
        },
        // L'expert a reçu une note
        ratingReceived: {
            title: "You Received a Rating",
            intro: "{clientName} has rated your service {productName}: {rating}/5.",
            commentLabel: "Comment:",
            button: "View Chat",
        },
        // Le client a décliné l'offre de nouvelle tentative
        retryDeclined: {
            title: "Retry Offer Declined",
            intro: "{clientName} has declined your revised delivery offer for {productName}.",
            note: "The order remains closed. No further action is required on your side.",
            button: "View Chat",
        },
        // L'expert a clôturé la conversation
        chatClosed: {
            title: "Conversation Closed",
            intro: "{expertName} has closed the conversation about {productName}.",
            note: "You can still read the full history, but new messages can no longer be sent.",
            button: "View Conversation",
        },
        deliveryViewed: {
            title: "Delivery Viewed",
            intro: "{clientName} has viewed your delivery for {productName}.",
            review: "They are now reviewing your work and will either confirm satisfaction or request revisions.",
        },
        deliveryRejected: {
            title: "Revision Requested",
            intro: "{clientName} has requested revisions for {productName}.",
            reasonTitle: "Reason for revision request:",
            advice: "Please review their feedback and submit an updated delivery.",
            button: "View Chat & Redeliver",
        },
        paymentAuthorized: {
            title: "Your funds have been securely reserved",
            intro: "Great news! {amount} has been securely reserved on your card for {productName}. You have not been charged yet.",
            whatNextTitle: "What happens next?",
            step1: "{expertName} will review your request and accept it",
            step2: "Your funds are held safely - they are <strong>not</strong> charged yet",
            step3: "Payment is only released when the service is delivered and confirmed",
            step4: "You can cancel anytime before delivery starts for a full refund",
            track: "You can track your request and communicate with {expertName} in your chat:",
            button: "View Your Request",
            note: "<strong>Note:</strong> If {expertName} doesn't respond within {count} day{plural}, your authorization will automatically expire and your funds will be released back to you.",
            // Variante relance : rien à accepter, l'expert livre déjà
            retryStep1: "{expertName} is already working on your delivery",
            retryNote: "<strong>Note:</strong> The delivery is due within {count} hour{plural}. If it isn't delivered and confirmed by the deadline, your payment will be automatically refunded.",
        },
        mediationResolved: {
            // Mission gratuite (REFERRAL-4) : aucune somme n'est en jeu
            freeMissionText: "This mission was carried out free of charge: no payment was involved, and none will be.",
            intro: "The mediation case for {productName} with {name} has been resolved by our support team.",
            decisionLabel: "Support Decision:",
            questions: "If you have questions about this decision, please contact our support team.",
            expertFavorTitle: "Resolved in Your Favor",
            expertAgainstTitle: "Resolved in Client's Favor",
            expertFavorText: "The payment of {amount} has been captured.",
            expertAgainstText: "The payment of {amount} has been refunded to the client.",
            clientFavorTitle: "Resolved in Your Favor",
            clientAgainstTitle: "Resolved in Expert's Favor",
            clientFavorText: "You have been refunded {amount}. The refund will appear in your account within 5-10 business days.",
            clientAgainstText: "The payment of {amount} has been released to the expert.",
            /*
             * Issues distinctes d'une même décision (BL-G4). Les quatre textes
             * ci-dessus confondaient une réservation levée, un encaissement et un
             * remboursement sous le même mot « payment of {amount} », où le montant
             * était celui autorisé et non celui réellement déplacé.
             */
            expertRefundPartialText: "{refunded} has been refunded to the client. The remaining {net} of the {captured} charged stays with you.",
            expertRefundFullText: "The full {captured} charged has been refunded to the client.",
            expertReleaseText: "The payment authorization of {released} has been released. Nothing was ever charged.",
            expertNoRefundText: "No refund was issued. The {net} already charged remains with you.",
            clientRefundPartialText: "You have been refunded {refunded} of the {captured} you were charged. The refund will appear in your account within 5-10 business days.",
            clientRefundFullText: "You have been refunded the full {captured} you were charged. The refund will appear in your account within 5-10 business days.",
            clientReleaseText: "The payment authorization of {released} has been released. You were never charged, so there is nothing to refund.",
            clientNoRefundText: "No refund was issued. The {net} you were charged remains with the expert.",
            releasedNote: "Separately, {released} of the original authorization was never charged and has already been released.",
        },
        responseReminder: {
            halfwayTitle: "Reminder: Pending Chat Request",
            halfwayMessage: "A client has been waiting for <strong>{timeWaiting}</strong> for your response. You set your expected response time to <strong>{expectedResponseTime}</strong>, so you're about halfway through that window.",
            urgentTitle: "Urgent: Client Waiting for Response",
            urgentMessage: "A client has been waiting for <strong>{timeWaiting}</strong> for your response - that's approaching your expected response time of <strong>{expectedResponseTime}</strong>. Please respond soon to maintain a great client experience.",
            waitingLabel: "Waiting since:",
            advice: "You can accept to start working on the request, or decline if you can't help with this one.",
            footerNote: "You're receiving this email because you have a pending chat request on ExpertLoop.",
            button: "View Request",
        },
        subscription: {
            manageNote: "You can manage your subscription at any time from your dashboard settings.",
            changedMindCancel: "Changed your mind? You can reactivate your subscription at any time before the cancellation date.",
            reactivateButton: "Reactivate Subscription",
            confirmedSubjectUpgrade: "You've upgraded to {tierName}!",
            confirmedSubjectNew: "Welcome to {tierName}!",
            confirmedIntroUpgrade: "Your plan has been upgraded to {tierName}. The changes are effective immediately.",
            confirmedIntroNew: "Thank you for subscribing to the {tierName} plan. You now have access to all the benefits of your new plan.",
            planHeading: "{tierName} Plan — €{price}/month",
            downgradeSubject: "Plan Change Scheduled",
            downgradeIntro: "Your plan change has been scheduled. Here's a summary:",
            currentPlanLabel: "Current Plan",
            switchingToLabel: "Switching to",
            effectiveLabel: "Effective: {date}",
            downgradeBody: "You'll continue to enjoy all {currentTierName} benefits until {date}. After that, your plan will automatically switch to {newTierName}.",
            downgradeChangedMind: "Changed your mind? You can cancel this change from your dashboard at any time before the switch date.",
            manageButton: "Manage Subscription",
            canceledSubject: "Subscription Cancellation Confirmed",
            canceledIntro: "Your {tierName} subscription has been set to cancel. You'll continue to have full access to all your plan benefits until {date}.",
            canceledLoseTitle: "After {date}, you'll lose access to:",
            reactivatedSubject: "Welcome Back! {tierName} Subscription Reactivated",
            reactivatedTitle: "Welcome Back!",
            reactivatedIntro: "Great news! Your {tierName} subscription has been reactivated. Your plan will continue uninterrupted.",
            reactivatedBenefitsTitle: "Your {tierName} benefits:",
            reactivatedThanks: "Thank you for staying with us!",
            changeCanceledSubject: "Plan Change Canceled",
            changeCanceledIntro: "Your scheduled plan change has been canceled. You'll continue on your current {tierName} plan with no changes.",
            activePlanLabel: "Active Plan: {tierName}",
            noUpcomingChanges: "No upcoming changes scheduled.",
            reminder7daySubject: "Your {tierName} plan ends in 7 days",
            reminder7dayTitle: "Your {tierName} Plan Ends in 7 Days",
            reminder7dayIntro: "This is a friendly reminder that your {tierName} subscription is set to end on {date}.",
            reminder7dayLoseTitle: "Here's what you'll lose:",
            reminderFreePlan: "After cancellation, you'll be moved to the Free plan with a {rate}% platform commission on your sales.",
            reminderKeep: "Want to keep your benefits? Reactivate your subscription before it expires.",
            reminder24hSubject: "Last chance: Your {tierName} plan ends tomorrow",
            reminder24hTitle: "Last Chance: Your Plan Ends Tomorrow",
            reminder24hIntro: "Your {tierName} subscription expires on {date} — that's less than 24 hours away.",
            reminder24hLoseTitle: "You're about to lose:",
            reminder24hCommission: "Once your plan ends, your commission rate will increase from your current rate to {rate}% on the Free plan. Don't miss out on your lower commission rate and premium features.",
            reminder24hButton: "Reactivate Now",
            endedSubject: "Your {tierName} subscription has ended",
            endedTitle: "Your {tierName} Subscription Has Ended",
            endedIntro: "We're sad to see you go. Your {tierName} subscription has ended and your account has been moved to the Free plan.",
            endedChangedTitle: "What's changed:",
            endedCommissionItem: "Your commission rate is now {rate}% (Free plan)",
            endedBenefitsGone: "The following benefits are no longer available:",
            endedComeBack: "We'd love to have you back. You can resubscribe at any time to get your lower commission rate and premium features.",
            endedButton: "View Plans",
            endedThanks: "Thank you for being part of ExpertLoop. We hope to see you again soon!",
        },
        // Période Boost d'un nouvel expert (lot SUB-1). Les chiffres viennent de
        // la configuration, jamais du texte.
        // Parrainage entre experts (lot E5). Aucun chiffre écrit ici : tout montant,
        // pourcentage ou durée vient des réglages du programme.
        expertReferral: {
            welcomeEarnedSubject: "{name} made a first sale: your referral bonus is confirmed",
            welcomeEarnedTitle: "Your referral bonus is confirmed",
            welcomeEarnedIntro: "{name}, whom you referred to ExpertLoop, made a first sale. Your welcome bonus is now confirmed.",
            rewardDiscount: "Your reward: {amount} off your subscription.",
            rewardProMonths: "Your reward: {count} free month{plural} of Pro.",
            recordedNotice: "It is recorded in your referral dashboard, which shows when it is applied to your subscription.",
            keptNotice: "If you do not pay for a subscription yet, your rewards are kept until your first subscription payment.",
            dashboardButton: "See my referrals",
            summarySubject: "Your referral rewards",
            summaryTitle: "Your referral rewards",
            summaryIntro: "Here is what your referrals earned you between {from} and {to}.",
            summaryCredit: "{amount} of subscription credit",
            summaryProMonths: "{count} free month{plural} of Pro",
            refereeWelcomeSubject: "Welcome to ExpertLoop: you were referred by {name}",
            refereeWelcomeTitle: "Welcome to ExpertLoop",
            refereeWelcomeIntro: "You joined ExpertLoop through the referral link of {name}.",
            refereeDiscount: "Your referral offer: {percent}% off your first {count} month{plural} of {plans}.",
            refereeRewarded: "{name} will be rewarded as you succeed on ExpertLoop.",
            refereeButton: "Open my dashboard",
            planSeparator: " or ",
        },
        boost: {
            endingSubject: "Your Boost period ends on {date}",
            endingTitle: "Your Boost period ends soon",
            endingIntro: "Your Boost period, with no subscription fee and a {rate}% commission, ends on {date}.",
            endedSubject: "Your Boost period has ended",
            endedTitle: "Your Boost period has ended",
            endedIntro: "Your Boost period is over. You are now on the Free plan, with a {rate}% commission on your sales.",
            choosePlan: "You can choose the plan that suits you at any time, from Plus to Business.",
            button: "See the plans",
        },
        // Offre de nouvelle tentative après expiration (client), et son
        // acceptation payée (expert)
        retryOffer: {
            title: "New Delivery Offer",
            intro: "{expertName} was unable to deliver {productName} before the deadline, and your payment was refunded. They are now offering a new attempt with a shorter delivery time:",
            amountLabel: "Amount:",
            deliveryLabel: "Delivery within:",
            deliveryValue: "{count} hour{plural}",
            expiry: "This offer expires in {count} hour{plural}.",
            payment: "Your previous payment has already been refunded — accepting this offer will authorize a new payment, held securely until delivery.",
            button: "Review the Offer",
        },
        retryAccepted: {
            title: "New Attempt Accepted!",
            intro: "{clientName} has accepted your new delivery offer for {productName} and the payment has been authorized.",
            deadline: "The clock is ticking: you have {count} hour{plural} to deliver, until {deadline}.",
            button: "Deliver Now",
        },
        retryReminder: {
            title: "Last Chance: Offer Expires Soon",
            intro: "The new delivery offer from {expertName} for {productName} expires in about {count} hour{plural}. After that, it will no longer be possible to accept it.",
            button: "Review the Offer",
        },
        retryExpired: {
            title: "Offer Expired Without a Response",
            intro: "Your new delivery offer for {productName} has expired without a response from {clientName}.",
            nextStep: "You can send another offer from the chat if you would like to give it one more try.",
            button: "Open the Chat",
        },
        // Boîte support (destinataire interne) : langue fixée par
        // SUPPORT_EMAIL_LOCALE côté API, pas par un destinataire utilisateur
        mediationRequested: {
            title: "Mediation Requested",
            intro: "A delivery dispute requires support intervention.",
            chatIdLabel: "Chat ID:",
            orderIdLabel: "Order ID:",
            requestedByLabel: "Requested by:",
            reasonLabel: "Reason:",
            actorExpert: "Expert",
            actorClient: "Client",
            historyTitle: "Delivery History",
            historyEmpty: "No delivery history recorded.",
            actionDelivered: "marked as delivered",
            actionRejected: "rejected delivery",
            actionRedelivered: "redelivered",
            actionConfirmed: "confirmed delivery",
            actionMediationRequested: "requested mediation",
        },
        supportMessage: {
            title: "Support request",
            fromLabel: "From:",
            userIdLabel: "User ID:",
            conversationLabel: "Conversation:",
            orderLabel: "order",
            referenceNote: "Reference given by the sender and not verified. The link opens the case in the administration.",
            subjectLabel: "Subject:",
            footer: "Sent from the /support contact form.",
        },
    },
    // ─── Assistant IA de fiche produit ──────────────────────────────────────
    aiAssistant: {
        title: "AI sheet assistant",
        subtitle: "Answer a few questions and get a polished product sheet.",
        back: "Back to products",
        inputPlaceholder: "Your answer...",
        send: "Send",
        thinking: "The assistant is writing...",
        proposalTitle: "Proposed sheet",
        proposalPrerequisites: "Client prerequisites",
        proposalPrice: "Max price",
        proposalDelivery: "Response within",
        proposalDeliveryHours: "{count} hours",
        useSheet: "Use this sheet",
        abandon: "Abandon",
        abandonConfirmTitle: "Abandon this conversation?",
        abandonConfirmMessage: "The conversation will be closed. You can start a new one from the product form at any time.",
        error: "The assistant is unavailable right now. Your conversation is saved — please try again.",
        rateLimited: "Too many messages in a short time. Please wait a moment before continuing.",
        turnLimit: "This conversation has reached its limit. Use the proposed sheet or start a new conversation.",
        // Résolues côté API : premier message de la session et titre de section
        // imposé au modèle. Traduites comme le reste du catalogue, dans la locale
        // de l'expert.
        introNew: "Hello! I'm your sheet-writing assistant. I'll ask you a few questions so we can write a presentation of your service that makes clients want to reach out. Describe what you offer in a few words — it doesn't have to be perfect, one sentence is enough.",
        introExisting: "Hello! Here is your current sheet — what would you like to change? You can ask me to rewrite it entirely, to emphasise a particular point, to adjust the price or the turnaround time, or to revisit the prerequisites.",
        deliverablesTitle: "What you'll get",
    },
    // ─── Atelier conversationnel de l'expert (CONV-UX1 / CONV-UX2) ──────────
    // Boucle privée de l'expert sur sa fiche produit ou son profil : dialogue,
    // synthèse en cours, relecture et édition directe. « Privé » est une
    // promesse produit : rien n'est publié sans validation explicite. Les clés
    // communes aux deux parcours vivent à la racine ; celles propres à une
    // déclinaison vivent sous `product` et `expert` (contrat WorkshopTextKeys).
    assistantWorkshop: {
        title: "Expert assistant",
        privateBadge: "Private",
        privateFootnote: "Private workshop · nothing is published without your validation",
        breadcrumbAssistant: "Assistant",
        breadcrumbSynthesis: "Synthesis",
        entryOpen: "Open the assistant",
        // Introduction de la boucle privée
        introPrivacy: "Nothing you share here is published without your validation.",
        howItWorksTitle: "How it works",
        howStep1Title: "I ask you targeted questions",
        howStep2Title: "You provide the details",
        howStep2Body: "In a few minutes, at your own pace.",
        howStep3Title: "You validate before any publication",
        howStep3Body: "You keep control at every step.",
        start: "Start",
        // Dialogue
        suggestionsHint: "You can answer freely, for example: 1, 3 and 4, or in your own words.",
        synthesisInProgress: "Synthesis in progress",
        // États de la compréhension par tour (CONV-0)
        statusConfirmed: "Confirmed",
        statusToClarify: "To clarify",
        // Fin de dialogue et transition vers la relecture
        completeTitle: "I now have enough information.",
        reviewCta: "Review the synthesis",
        // Relecture de la synthèse
        synthesisTitle: "Synthesis",
        readyTitle: "This private version is ready.",
        editItem: "Edit {label}",
        useVersion: "Use this version",
        continueWithAi: "Continue with AI",
        // Édition directe
        editFreely: "You can edit this text freely.",
        improveWithAi: "Improve with AI",
        // ─── Déclinaison produit (CONV-UX1) ───────────────────────────────────
        product: {
            titleContext: "Product",
            contextLabel: "Product",
            back: "Back to the product",
            subtitle: "Product · private version",
            entryTitle: "Improve this product with AI",
            entryBody: "This AI assistant is your private workshop. It helps you refine your offer, clarify your positioning and improve your content before publication. No client can access it.",
            introTitle: "I'll help you clarify this service.",
            introBody: "I ask you the right questions to identify the information your clients should provide, and to structure a clear, complete and professional service.",
            howStep1Body: "To understand your service and your needs.",
            itemsIdentified: "{count} piece{plural} of information identified",
            understandingSummary: "{count} piece{plural} of information identified · {confirmed} confirmed · {toClarify} to clarify",
            previewLabel: "The proposed sheet",
            completeBody: "I have structured a first version of your service and of the information to ask your client. You will be able to review, correct and validate each element.",
            readyBody: "The service is now clearer and more complete. You can use it or ask for further adjustments.",
            resultSection: "The service",
            resultRow: "Service",
            itemsSection: "Information to ask the client",
            editResultHint: "Describe precisely what you offer",
            editItemHint: "Wording of the information to ask",
        },
        // ─── Déclinaison demande client (CONV-4) ──────────────────────────────
        // « Privé » change de promesse ici : rien n'est ENVOYÉ sans validation.
        client: {
            title: "Client assistant · Request",
            titleContext: "Request",
            contextLabel: "Service",
            back: "Back to my request",
            subtitle: "Request · private version",
            entryTitle: "Get help phrasing your request",
            entryBody: "This AI assistant is your private workshop. It asks a few useful questions, structures your request as you talk, and nothing is sent to the expert without your validation.",
            introTitle: "I'll help you phrase your request",
            introBody: "I ask a few targeted questions to understand your need, clarify the key elements and prepare a complete request to send to the expert.",
            introPrivacy: "Nothing you share here is sent without your validation.",
            howStep1Body: "To understand your need and its context.",
            howStep2Title: "I structure your request as we talk",
            howStep2Body: "The assistant organises the key information.",
            howStep3Title: "You validate before sending to the expert",
            howStep3Body: "You keep control at every step.",
            introMessage: "Hello! Simply describe what you need for “{service}”. What would you like to clarify, decide or unblock?",
            itemsIdentified: "{count} piece{plural} of information identified",
            understandingSummary: "{count} piece{plural} of information identified · {confirmed} confirmed · {toClarify} to clarify",
            previewLabel: "The proposed request",
            completeBody: "I have structured a complete version of your request. You will be able to review, correct and validate each element before anything is sent.",
            readyBody: "Your need is now clearer and more complete. You can use it as is or ask for further adjustments.",
            resultSection: "The request",
            resultRow: "Request",
            itemsSection: "Key information",
            editResultHint: "Your request, in your own words",
            editItemHint: "Wording of this information",
            useRequest: "Use this request",
            notOffered: "This expert prefers requests written in your own words — simply describe your need.",
            // Contrairement à l'atelier expert, rien n'est « sauvegardé » côté
            // serveur : la conversation reste dans la page, le dire honnêtement.
            errorGeneric: "The assistant is unavailable right now. Your conversation stays on this page — please try again.",
            footnote: "Private workshop · nothing is sent without your validation",
        },
        // ─── Déclinaison profil expert (CONV-UX2) ─────────────────────────────
        expert: {
            titleContext: "Profile",
            contextLabel: "Expert profile",
            back: "Back to my profile",
            subtitle: "Profile · private version",
            entryTitle: "Improve my profile with AI",
            entryBody: "This AI assistant is your private workshop. It helps you present your expertise, clarify your positioning and improve your profile before publication. No client can access it.",
            introTitle: "I'll help you present your expertise.",
            introBody: "I ask you the right questions to understand your background, and to structure a clear, authentic and professional profile.",
            howStep1Body: "To understand your expertise and how you work.",
            itemsIdentified: "{count} area{plural} of expertise identified",
            understandingSummary: "{count} element{plural} identified · {confirmed} confirmed · {toClarify} to clarify",
            previewLabel: "The proposed profile",
            completeBody: "I have structured a first version of your public profile and of your areas of expertise. You will be able to review, correct and validate each element.",
            readyBody: "Your profile is now clearer and more complete. You can use it or ask for further adjustments.",
            resultSection: "The profile",
            resultRow: "Bio",
            itemsSection: "Areas of expertise",
            editResultHint: "Describe your background, your expertise and what sets you apart",
            editItemHint: "Wording of the area of expertise",
        },
    },
    // ─── Ma fiche expert ────────────────────────────────────────────────────
    expertProfile: {
        title: "My expert profile",
        subtitle: "This is what clients see before contacting you.",
        photo: "Profile photo",
        changePhoto: "Change photo",
        comingSoon: "Coming soon",
        bio: "Bio / Introduction",
        bioPlaceholder: "Describe your background, your expertise and what sets you apart...",
        expertises: "Areas of expertise",
        expertisesHelp: "Add up to {max} keywords. Press Enter or comma to add one.",
        expertisesPlaceholder: "e.g. Technical SEO",
        save: "Save",
        saving: "Saving...",
        saved: "Your expert profile has been saved.",
        saveError: "Your profile could not be saved. Please try again.",
        loadError: "Your profile could not be loaded. Please refresh the page.",
        aiApplied: "The AI-generated profile has been applied — review it, then save.",
        previewTitle: "Public preview",
        viewPublicPage: "View my public page",
        emptyBio: "Your bio will appear here once written.",
        respondsWithin: "Usually responds within {time}",
        // ─── Alias public (/expert/:handle) — outil de promotion ─────────────
        handle: {
            label: "Public alias",
            help: "A short, memorable address for your store: {min} to {max} characters, letters, digits and hyphens. You can change it once every 30 days.",
            placeholder: "e.g. marie-durand",
            currentUrl: "Your store address",
            available: "This alias is available.",
            checking: "Checking availability…",
            suggestionsTitle: "Available alternatives",
            save: "Claim this alias",
            saving: "Saving…",
            saved: "Your public alias has been saved.",
            confirmTitle: "Confirm your public alias",
            confirmFirst: "You are about to reserve “{next}”. It will immediately become the public address to share for your store. This choice is reversible, but you will not be able to change it again for {days} days.",
            confirmChange: "You are about to replace “{current}” with “{next}”. The new address will become the one to share immediately. This change is reversible, but you will not be able to choose another alias for {days} days. Depending on how long you held the old alias, its address may redirect to your profile or may stop being reserved, so do not rely on it as your main address.",
            confirmAction: "Reserve this alias",
            // ─── Réservation différée (lot HANDLE-R2) ─────────────────────────
            // Ces libellés existent parce que l'interface annonçait « devient
            // immédiatement » y compris pendant le délai de changement, où le
            // serveur ne peut que réserver. Ils décrivent l'action réelle.
            availableReserve: "This alias is available. It is yours to lock in now, and it goes live on {date}, in {days} day{plural}.",
            availableReplace: "This alias is available. Locking it in replaces your current reservation, on the same date: {date}, in {days} day{plural}.",
            ownActive: "This is already your public alias.",
            ownPending: "This is already your reserved alias.",
            pendingTitle: "Reserved alias",
            pendingNotice: "“{handle}” goes live on {date}, in {days} day{plural}. Until then, {current} stays the address to share.",
            pendingReplaceHint: "Choosing another alias now replaces this reservation without pushing the date back.",
            cancelPending: "Cancel this reservation",
            cancelPendingTitle: "Cancel your reserved alias",
            cancelPendingMessage: "“{handle}” will be released and anyone will be able to claim it. Your current alias does not change.",
            cancelPendingAction: "Cancel the reservation",
            canceledPending: "Your reservation has been canceled.",
            confirmReserve: "“{next}” will be locked in for you right away, and will become your public address on {date}, in {days} day{plural}. Until then, “{current}” stays the address to share.",
            confirmReplace: "“{next}” replaces your reservation of “{pending}”, which is released immediately. The switch date does not move: {date}, in {days} day{plural}.",
            confirmReserveAction: "Reserve for {date}",
            savedReserved: "Your alias has been reserved.",
            savedReplaced: "Your reservation has been replaced.",
            errorConflict: "Someone claimed this alias while you were confirming. Nothing was changed — pick another one.",
            // Motifs de refus, alignés sur les codes de l'API
            reason: {
                tooShort: "Too short: at least {min} characters.",
                tooLong: "Too long: at most {max} characters.",
                invalidCharacters: "Only lowercase letters, digits and hyphens are allowed.",
                invalidEdges: "It must start and end with a letter or a digit.",
                consecutiveHyphens: "Consecutive hyphens are not allowed.",
                objectidPattern: "This format is reserved for technical identifiers.",
                reserved: "This alias is reserved.",
                taken: "This alias is already taken.",
            },
            // Ne concerne plus le changement — pendant le délai, le serveur réserve.
            // Ne reste que la limitation de débit de la vérification.
            errorTooFrequent: "Too many checks in a row. Wait a moment before trying again.",
            errorGeneric: "Your alias could not be saved. Please try again.",
        },
    },
    // ─── Connaissance métier de l'expert (AI-1B5A) ──────────────────────────
    // Les libellés nomment le RÉSULTAT, jamais la technologie, et jamais de la même
    // façon que l'assistant de rédaction de fiche : celui-ci aide à écrire la fiche,
    // ceci en structure la connaissance métier une fois la fiche enregistrée.
    expertKnowledge: {
        title: "Your business knowledge",
        subtitle: "A structured summary of what you do, derived from your saved profile. It guides ExpertLoop assistants — it is not shown on your public page.",
        generate: "Structure it from my profile",
        regenerate: "Start over from my profile",
        generating: "Reading your profile…",
        generateHelp: "This reads your saved profile and drafts a proposal. Nothing is recorded until you review and apply it.",
        generateFailed: "The proposal could not be drafted. Nothing was changed.",
        disabled: "Structuring from your profile is not available yet on this environment.",
        needsProfile: "Write and save your bio first — there is nothing to derive a summary from yet.",
        proposalTitle: "Proposal — not applied",
        proposalNotice: "Review and edit this before applying. Nothing here is recorded yet.",
        builtWithDialogue: "Built from your saved profile and your workshop dialogue of {date}.",
        applied: "Applied. This is now your recorded knowledge.",
        validatedTitle: "Currently recorded",
        validatedEmpty: "Nothing is recorded yet.",
        expertiseAreas: "Areas of expertise",
        targetAudiences: "Who you work with",
        typicalProblems: "Problems you resolve",
        boundaries: "What you do not do",
        boundariesPrivate: "Private — never shown to a client. It keeps assistants from accepting work you would refuse.",
        communicationLanguages: "Working languages",
        emptyList: "Nothing proposed here.",
        staleProposal: "Your profile changed after this was drafted. You can still apply it — it will be recorded as describing the earlier version — or start over.",
        staleValidated: "Your profile changed after this was recorded. Review it when you can.",
        apply: "Apply",
        applying: "Applying…",
        applyFailed: "The proposal could not be applied. Nothing was changed.",
        reject: "Discard the proposal",
        rejectFailed: "The proposal could not be discarded.",
        removeItem: "Remove this entry",
    },
    tagInput: {
        remove: "Remove {tag}",
        count: "{count}/{max}",
    },
    // ─── Assistant IA de fiche expert ───────────────────────────────────────
    aiExpertAssistant: {
        title: "AI profile assistant",
        subtitle: "Answer a few questions and get a bio that makes clients want to reach out.",
        back: "Back to my profile",
        proposalTitle: "Proposed profile",
        proposalExpertises: "Areas of expertise",
        useSheet: "Use this profile",
        abandonConfirmMessage: "The conversation will be closed. You can start a new one from your expert profile at any time.",
        // Résolues côté API : premier message de la session, dans la locale de l'expert.
        introNew: "Hello! I'm your profile-writing assistant. I'll ask you a few questions so we can write a bio that makes clients want to reach out. To start: tell me about your background or your expertise in a few words — it doesn't have to be perfect, one sentence is enough.",
        introExisting: "Hello! Here is your current profile — what would you like to change? You can ask me to rewrite it entirely, to emphasise part of your background, or to revisit your areas of expertise.",
    },
    // Saisie d'une carte en trois champs titrés, commune à tous les formulaires
    cardFields: {
        number: "Card number",
        expiry: "Expiry date",
        cvc: "Security code (CVC)",
    },
    paymentForm: {
        cardNotFound: "Card element not found",
        incompleteAddress: "Please complete the billing address",
        failed: "Payment failed",
        unexpectedStatus: "Unexpected payment status: {status}",
        billingAddress: "Billing Address",
        cardDetails: "Card Details",
        processing: "Processing...",
        // Mémoire client (PAY-1) : l'adresse appartient à ExpertLoop, la carte à Stripe.
        // Guest : adresse mémorisable, carte jamais mémorisée. Compte : deux opt-in distincts.
        paymentMethod: "Payment method",
        editAddress: "Edit",
        rememberAddressGuest: "Remember this address for my next requests",
        rememberAddressGuestHint: "You can change it at your next payment.",
        rememberAddressUser: "Remember this address for my next payments",
        rememberAddressReplaceHint: "Checked, this address replaces the one you saved. Unchecked, it is used for this payment only.",
        saveCard: "Save this card for my next payments",
        // Wording de sécurité imposé : exact, sans promesse abusive (PAY-1 §3.3)
        cardSecurityStripe: "Your card details are stored securely by Stripe. ExpertLoop keeps neither your full card number nor your security code.",
        savedCardSecurity: "Sensitive card data stays with Stripe. ExpertLoop only uses the reference and the display details it needs.",
        guestCardNote: "This card is used for this payment only. No card is stored for guests.",
        guestReturnCardNote: "For your security, a new card is requested for each guest payment.",
        savedBadge: "Saved",
        useAnotherCardHint: "Enter a new payment method",
        useSavedCardAndReserve: "Use this card and reserve the funds",
        savedCardFailed: "This saved card could not be used. Please use another card.",
        addressSaveFailed: "Your address could not be saved, but your payment is not affected.",
    },
    // ─── Parcours de demande client avant acceptation (UIUX-1) ──────────────
    // Le wizard, l'écran d'attente et les fins de parcours (refus, abandon).
    // Le texte de référence français de la réservation est imposé par le work
    // package UIUX-1 : après traduction automatique, vérifier le rendu français
    // et le corriger par une correction manuelle dans l'admin si nécessaire.
    requestFunnel: {
        steps: {
            // Le fil d'Ariane du wizard (UIUX-1C) : cinq étapes pour un visiteur,
            // quatre pour un compte connecté (l'étape email n'existe pas pour lui)
            stepOf: "Step {current} of {total}",
            discover: "Discover the service",
            yourRequest: "Your request",
            email: "Your email",
            reserveFunds: "Reserve the funds",
            sent: "Request sent",
        },
        // Étape 1 — découvrir la prestation dans la coquille du wizard
        discover: {
            subtitle: "What {expertName} offers, and what happens next.",
            expectsTitle: "What {expertName} needs to know",
            nothingSent: "Nothing is sent or reserved at this step.",
            cta: "Describe my request",
            backToStore: "Back to the store",
            priceNote: "Reserved at step {step}, charged only after delivery",
            viewProfile: "View profile",
        },
        // Étape 2 — rédiger la demande dans la coquille du wizard
        write: {
            subtitle: "The more details you give, the better {expertName} can help. Your email is only asked when you send.",
            subtitleAccount: "The more details you give, the better {expertName} can help.",
            back: "Back to the service",
            send: "Send my request",
            nothingYetTitle: "Nothing is sent yet",
            nothingYetBody: "Your text is saved on this device. It will be passed on to {expertName} once your email is confirmed.",
            nothingYetBodyAccount: "Your text is saved on this device until you send it.",
        },
        // Étape 3, premier écran — l'adresse où prévenir (visiteurs sans compte)
        email: {
            title: "Where should we reach you?",
            subtitle: "Your request is saved. Your email confirms it and lets us tell you when {expertName} replies.",
            hint: "We'll send you a 6-digit code to confirm this address. No account needed.",
            cta: "Get my code",
            back: "Edit my request",
            name: "Your first name (optional)",
        },
        // Reprise : une demande existe déjà pour cette prestation
        resume: {
            title: "You already have a request in progress for this service",
            body: "Pick it up where you left it — nothing was lost.",
            cta: "Resume my request",
        },
        // Étape 1 : relire et ajuster la demande avant la réservation
        summary: {
            subtitle: "Check and adjust what you are about to send to {expertName}.",
            yourMessage: "Your message",
            edit: "Edit my request",
            editPlaceholder: "Describe what you need help with...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            saveFailed: "Failed to save your changes",
            updated: "Edited at {time}",
            files: "Attached files",
            continue: "Continue",
            // Encadré de réassurance : rien ne part avant la réservation
            // Après vérification de l'email (UIUX-1B), la demande est déjà visible
            // de l'expert : ne plus prétendre qu'elle n'est pas envoyée
            securedTitle: "Your request is saved",
            securedBody: "{expertName} can already see it. It only becomes a mission to accept once the funds are reserved — nothing is charged before delivery.",
        },
        help: "Need help?",
        // UIUX-1B — vérification de l'email entre la rédaction et la réservation.
        // La demande est déjà persistée : fermer l'écran ne la perd pas.
        verify: {
            title: "Confirm your email address",
            subtitle: "One last check before sending your request to {expertName}: confirm the address where we'll keep you posted.",
            savedNote: "Your request is saved. You can close this page and come back anytime.",
            checking: "Checking your identity…",
            failed: "We couldn't confirm your request. Please try again.",
            // Adresse mal saisie : la demande change d'adresse, un nouveau code part
            changeEmail: "Wrong address? Change it",
            changeEmailTitle: "Use another email address",
            changeEmailCta: "Send the code to this address",
            changeEmailFailed: "We couldn't change the address. Please try again.",
            changeEmailAccountExists: "This email belongs to an account. Sign in to continue with it.",
        },
        // Rail informatif commun à toutes les étapes
        rail: {
            reservedNote: "Reserved — charged only after delivery",
        },
        reserve: {
            title: "Reserve the funds",
            subtitle: "A simple reservation to send your request.",
            noChargeTitle: "No debit today",
            // Texte de référence (wording imposé, valeurs dynamiques), scindé en
            // deux : l'engagement de débit s'affiche en gras (maquette 2026-08-30)
            referenceIntro: "Your request is ready to be sent to {expertName}. To confirm your intention, we will simply reserve {amount} on your card.",
            referenceCommitment: "If {expertName} accepts your request, you will be put in touch, and you will only be charged once they have completed the service within the announced timeframe.",
            todayTitle: "Today",
            todayBody: "{amount} reserved on your card — no debit.",
            acceptTitle: "If {expertName} accepts your request",
            acceptBody: "You are put in touch and the delivery time starts.",
            deliveredTitle: "Once the service is delivered",
            deliveredBody: "The payment is then charged and released to {expertName}.",
            declinedTitle: "If {expertName} declines or the request is canceled",
            declinedBody: "The reserved funds are automatically released.",
            respondsWithin: "{expertName} usually replies within {time}.",
            cta: "Reserve {amount} and send my request",
            back: "Back",
            editRequest: "Edit my request",
            abandonRequest: "Cancel my request",
            // État transitoire entre l'accord de la banque et la confirmation
            // serveur : ne jamais annoncer l'envoi avant cette confirmation
            confirmingTitle: "Confirming your reservation…",
            confirmingBody: "Your payment method was accepted. We are confirming the reservation — this usually takes a few seconds.",
            confirmingHint: "You can safely keep this page open.",
        },
        // Mission d'invitation acceptée, pas encore financée (REFERRAL-3)
        invitationFunding: {
            title: "{expertName} accepted your request",
            subtitle: "Your mission starts as soon as the amount is reserved on your card.",
            pendingTitle: "Reservation in progress",
            pendingBody: "{expertName} is completing their payment setup. The {amount} will then be reserved automatically on the card you prepared: you have nothing to do.",
            confirmTitle: "Your bank asks you to confirm",
            confirmBody: "We could not reserve the {amount} without you. We sent you an email with a link to confirm the reservation.",
            // Nouveau lien depuis le compte (REFERRAL-5)
            confirmNow: "Confirm the payment now",
            confirmFailed: "The confirmation page could not be opened. Please try again in a moment.",
            noChargeTitle: "Nothing is charged yet",
            noChargeBody: "The amount is only reserved. You are charged once you accept the delivery.",
        },
        sent: {
            title: "Request sent! 🎉",
            subtitle: "{expertName} has been notified by email.",
            waitingFor: "Waiting for {expertName}",
            respondsWithin: "{expertName} usually replies within {time}.",
            nothingToDo: "You have nothing to do for now.",
            emailNotice: "We will email you as soon as they reply.",
            fundsReserved: "Funds reserved: {amount}",
            noCharge: "No debit has been made yet.",
            canClose: "You can close this page.",
            // Visiteur : retenir son accès au moment où la demande part (UIUX-1C)
            keepAccessTitle: "Find your requests easily",
            keepAccessBody: "Your requests are tied to {email}. Add a passkey on this device, or create an account, to find them anywhere without a code.",
            createAccount: "Create my account with a password",
            saved: "Your request is saved.",
            allRequests: "View my sent requests",
        },
        declined: {
            title: "{expertName} cannot take on this request",
            body: "Your request was declined. This can happen when an expert is unavailable or when the request falls outside their expertise.",
            reasonTitle: "Message from {expertName}",
            fundsReleased: "The reservation on your card has been released. No charge was made.",
            browse: "Discover other experts",
        },
        abandoned: {
            title: "Request canceled",
            body: "This request was canceled before being sent. If funds had been reserved, the reservation has been released.",
            newRequest: "Make a new request",
        },
    },
    // ─── Suivi de la mission (timeline client, UIUX-1) ──────────────────────
    // Six jalons dérivés des faits du dossier — jamais une seconde machine
    // d'état : dates absolues pour le passé, « à venir » pour la suite.
    missionTimeline: {
        title: "Mission tracking",
        sent: "Request sent",
        accepted: "Accepted by {expertName}",
        deliveryTime: "Delivery time",
        deliveryUntil: "By {date}",
        daysLeft: "Days left: {count}",
        hoursLeft: "Hours left: {count}",
        delivered: "Service delivered",
        review: "Your review",
        payment: "Payment released to {expertName}",
        /* Vu de l'expert, ce n'est pas un versement « à quelqu'un » : c'est le
           sien. Les jalons sont les mêmes des deux côtés, les mots non. */
        paymentExpert: "Payment released to you",
        // Mission gratuite (REFERRAL-4) : aucun paiement versé
        freeCompleted: "Mission completed (free of charge)",
        refundRequested: "Refund requested",
        refundDenied: "Refund declined",
        refundGranted: "Refunded {amount}",
        upcoming: "Upcoming",
    },
    // ─── Accès client par secret de reprise (spec accès client §5) ──────────
    chatAccess: {
        // Écran de sécurisation post-demande (§5.4) — bloquant, acquittement explicite
        screen: {
            title: "Keep your access safe",
            // La demande est enregistrée mais ne part chez l'expert qu'à la
            // réservation des fonds (UIUX-1)
            sent: "Your request is saved. It will be sent to the expert once you reserve the funds — no money has been taken.",
            codeLabel: "Your access code",
            codeHint: "This code is THE way back to this conversation — it works even if you lose access to your inbox or change devices.",
            copyCode: "Copy the code",
            linkLabel: "The same access, as a link",
            copyLink: "Copy the link",
            copied: "Copied!",
            qrHint: "Scan to open this conversation on your phone.",
            emailOneShot: "The links in the emails we send you work only once — your code above is what you keep.",
            ackLabel: "I have saved my access code or link",
            ackButton: "Open my conversation",
        },
        // « Mes accès » dans le fil (réouverture + régénération)
        manage: {
            menuItem: "My access",
            title: "My access to this conversation",
            currentCode: "Your current access code",
            noLocalSecret: "Your access code is not stored on this device. Regenerate it to get a new one — the old code will stop working.",
            regenerate: "Regenerate my access",
            regenerateWarning: "Regenerating revokes the previous code and any email links. Use it if you shared your access by mistake.",
            regenerated: "New access code generated — the previous one no longer works.",
            close: "Close",
        },
        // Page /resume — saisie manuelle du secret
        resume: {
            title: "Resume a conversation",
            help: "Enter the access code you saved when sending your request (like EL-XXXX-XXXX-XXXX). Lowercase, hyphens and small mix-ups are fine.",
            placeholder: "EL-XXXX-XXXX-XXXX",
            submit: "Open my conversation",
            checking: "Checking…",
            errorInvalid: "This code doesn't match any conversation. Check it and try again.",
            errorLocked: "This access is locked after too many attempts. Open the link from your confirmation email to unlock it.",
            errorRate: "Too many attempts. Please wait a moment and try again.",
        },
        // Page /m/:token — lien e-mail à usage unique
        magicLink: {
            opening: "Opening your conversation…",
            consumedTitle: "This link has already been used",
            consumedBody: "Email links work only once. Use your access code to come back to your conversation.",
            invalidTitle: "This link is not valid",
            invalidBody: "It may have expired. Use your access code to resume your conversation.",
            goResume: "Enter my access code",
        },
        // Page /a — lien du secret persistant
        accessLink: {
            opening: "Opening your conversation…",
            invalidTitle: "This access link is not valid",
            invalidBody: "The code it carries doesn't match any conversation. It may have been regenerated.",
        },
        // Passkey — compte client silencieux (spec §6)
        passkey: {
            title: "Come back without any code",
            description: "Add a passkey to this device (Face ID, fingerprint or PIN) and reopen your conversations without email or password.",
            enroll: "Add a passkey",
            deviceLabelPlaceholder: "Name this device (optional, e.g. My phone)",
            enrolling: "Follow your device's instructions…",
            enrolled: "Passkey added — you can now come back from this device without a code.",
            error: "The passkey could not be created. Your access code above still works.",
            loginButton: "Sign in with a passkey",
            loginError: "Passkey sign-in failed. You can use your access code instead.",
        },
        // Passage du visiteur au client depuis la conversation (UIUX-1C)
        account: {
            title: "Find your requests easily",
            intro: "Your requests are tied to {email}. Create your access to find them from any device, without a code.",
            registerTitle: "Or create an account with a password",
            registerHint: "Once your email is verified, the requests sent with this address are attached to your account.",
            registerLink: "Create my account",
            menuItem: "Create my account",
        },
        // Gestion des passkeys du compte (page Paramètres)
        passkeyManager: {
            title: "My passkeys",
            description: "Devices that can open your account without email or password.",
            unnamedDevice: "Unnamed device",
            addedOn: "Added on {date}",
            lastUsed: "Last used {date}",
            revoke: "Revoke",
            revokeTitle: "Revoke this passkey?",
            revokeBody: "\u201c{device}\u201d will no longer be able to sign in to your account.",
        },
    },
    // ─── Page /my-experts — historique personnel, jamais une vitrine (spec §7) ──
    myExperts: {
        title: "My experts",
        subtitle: "The experts you have already worked with. Nothing else — this is your history, not a directory.",
        lastInteraction: "Last interaction: {date}",
        viewStore: "View their store",
        unknownExpert: "Expert",
        emptyTitle: "No experts yet",
        emptyBody: "Once you send a request to an expert, they will appear here.",
        // Visiteur : ses demandes vivent dans ce navigateur, ou se retrouvent par le code
        guestEmptyHint: "Sent a request from another device? Get it back with your recovery code.",
        guestResume: "Use my recovery code",
        // Les demandes par expert, pour rouvrir le fil sans passer par la boutique
        requests: "Your requests",
        openRequest: "Open",
        // Visiteur : proposer de passer client pour tout retrouver depuis un compte
        guestAccountTitle: "Find all your requests from any device",
        guestAccountBody: "Create your account with the email you used: your requests will be attached to it.",
        guestAccountCta: "Create my account",
    },
    // Pièces jointes d'une demande. Les fichiers ne partent qu'après la création
    // de la conversation : aucun fichier orphelin n'existe donc jamais.
    requestAttachments: {
        add: "Attach a file",
        hint: "Up to {max} files, {size} MB each. Documents, images and audio.",
        remove: "Remove this file",
        errorType: "This file type is not accepted.",
        errorSize: "Too large — {max} MB maximum.",
        errorCount: "Limit of {max} files reached.",
        uploading: "Attaching your files...",
        uploadFailed: "Your request was sent, but some files could not be attached. You can add them from the conversation.",
    },
    // ─── Espace client : Paiements & factures (BL-F3) ───────────────────────
    // Centre documentaire des règlements du client. Aucun vocabulaire de solde,
    // de portefeuille ou de crédit : la page recense ce qui a été payé et donne
    // accès aux documents correspondants.
    // Invitation d'un expert qui n'est pas encore sur ExpertLoop (REFERRAL-2)
    // ─── Parrainage entre experts (lot E5) ──────────────────────────────────
    /* Aucun chiffre écrit ici : pourcentages, montants et durées viennent des
       réglages du programme, par interpolation. Aucun exemple chiffré n'est
       affiché (arbitrage E8-Q5), et le filleul n'apprend jamais ce que gagne son
       parrain (E8-Q6). */
    expertReferral: {
        page: {
            title: "Refer an expert",
            intro: "Share your link with experts who could join ExpertLoop. You are rewarded as they succeed.",
            notEligible: "Your referral link appears once your shop is published: an active public handle and at least one public service.",
            loadFailed: "Your referral information could not be loaded.",
        },
        link: {
            title: "Your referral link",
            code: "Your code: {code}",
            copy: "Copy the link",
            copied: "Link copied",
            shareTitle: "Ready-to-post message",
            shareText: "I sell my expertise on ExpertLoop. If you are an expert too, join through my link: {link}",
            copyShare: "Copy the message",
            shareCopied: "Message copied",
        },
        offer: {
            title: "What you earn for each expert you refer",
            welcomeBoth: "At their first sale: {amount} off your subscription, or {count} free month{plural} of Pro if you do not pay for a subscription.",
            welcomeDiscountOnly: "At their first sale: {amount} off your subscription, if you pay for one.",
            welcomeProMonthsOnly: "At their first sale: {count} free month{plural} of Pro, if you do not pay for a subscription.",
            sales: "On their sales: {percent}% of what they bring in to ExpertLoop, for {count} month{plural}.",
            salesUnlimited: "On their sales: {percent}% of what they bring in to ExpertLoop, with no time limit.",
            salesAfterBoost: "This period starts when their launch period ends.",
            subBonus: "If they subscribe to {plans}: the equivalent of {percent}% of their first month, once they have paid {count} month{plural}.",
            subShare: "On their {plans} subscription: {percent}% of each payment, for {count} month{plural}.",
            subShareUnlimited: "On their {plans} subscription: {percent}% of each payment, with no time limit.",
            creditUse: "Your rewards reduce your subscription. If you do not pay for a subscription, they are kept until your first payment.",
            referee: "The expert you refer gets {percent}% off their first {count} month{plural} of {plans}.",
            planSeparator: " or ",
        },
        referees: {
            title: "Experts you referred",
            empty: "Nobody has joined through your link yet.",
            joined: "Joined on {date}",
            stageRegistered: "Signed up",
            stagePublished: "Shop published",
            stageFirstSale: "First sale made",
            stageSubscribed: "Subscribed",
        },
        rewards: {
            title: "Your rewards by month",
            empty: "No reward yet.",
            hint: "A reward waits until the refund period of the sale or payment is over, then it is earned. It is applied to your subscription afterwards.",
            pending: "Waiting",
            earned: "Earned",
            applied: "Applied",
            proMonths: "{count} free month{plural} of Pro",
        },
        landing: {
            title: "{name} invites you to ExpertLoop",
            intro: "ExpertLoop lets experts sell their expertise to clients, with payment and invoicing handled for them.",
            offer: "Your offer: {percent}% off your first {count} month{plural} of {plans}.",
            rewarded: "{name} will be rewarded as you succeed on ExpertLoop.",
            cta: "Create my expert account",
            unavailable: "This referral link is no longer available. You can still create your account.",
            genericTitle: "Join ExpertLoop",
            loading: "Opening the invitation…",
        },
    },
    referral: {
        errors: {
            unavailable: "This invitation is not available. It may have expired, been withdrawn, or the link may be incomplete.",
            alreadyDecided: "This invitation has already been answered.",
            alreadyClaimed: "This invitation is already linked to another account.",
            accountExists: "An account already exists with this email address. Sign in to accept the invitation.",
            accountRequired: "Sign in or create an account to continue.",
            emailNotVerified: "Confirm your email address first, using the link we sent you.",
            ownInvitation: "You cannot answer your own invitation.",
            paymentMethodNotPrepared: "Your card has not been saved yet. Please enter it again.",
            paymentMethodAlreadyUsed: "This card preparation has already been used for another invitation. Please start again.",
            tooManyRequests: "Too many attempts. Please try again in a few minutes.",
            generic: "Something went wrong. Please try again.",
            // Invitation ouverte (REFERRAL-3B)
            productNotProposable: "Only one of your public services can be proposed. Publish it first from your services.",
            linkEmailLimit: "This link has already been emailed the maximum number of times.",
            // Plafond d'invitations par client (REFERRAL-5)
            invitationLimit: "You have sent many invitations in the last 24 hours. Please try again later.",
            priceBelowMinimum: "The price you propose is below the minimum for an invitation.",
        },
        status: {
            pending: "Awaiting answer",
            accepted: "Accepted",
            declined: "Declined",
            redirected: "Another service proposed",
            expired: "Expired",
            revoked: "Withdrawn",
        },
        mode: {
            paid: "Paid",
            free: "Free of charge",
        },
        invite: {
            title: "Invite an expert",
            intro: "Send your request to a professional who is not on ExpertLoop yet. They can read it without creating an account and decide whether to take it on.",
            messageLabel: "Your request",
            messagePlaceholder: "Describe what you need, the context and what you expect as a result.",
            messageCount: "{current} / {max}",
            priceLabel: "Price you propose",
            priceHint: "The price is fixed: it is not negotiated. You can discuss the details in the conversation once the expert accepts.",
            // Prix minimum configurable : une invitation part vers quelqu'un qui n'a rien demandé
            minPriceHint: "Minimum price for an invitation: {amount}.",
            currencyLabel: "Currency",
            recipientNameLabel: "Expert's name (optional)",
            // Le lien est créé d'abord, l'envoi par email vient ensuite (REFERRAL-3B)
            linkFirstHint: "You will get a link to share with the expert, wherever you found them: their website, video channel or social network. You can also have it emailed if you know their address.",
            submit: "Continue",
            nothingReserved: "Nothing is reserved or charged at this stage.",
        },
        card: {
            title: "Save your card",
            explain: "Your card is saved and checked with your bank now, so that the expert can start without waiting for you. Nothing is reserved or charged at this stage.",
            ifPaid: "If the expert chooses to be paid, the proposed amount will be reserved on this card when they accept. If they work free of charge, the card will not be used.",
            bankMayAsk: "Your bank may still ask you to confirm when the amount is reserved.",
            preparing: "Preparing the secure card form…",
            ready: "A card is already saved for your next invitation. You can send it now.",
            failed: "Your card could not be saved. Check its details or try another card.",
            unavailable: "Card entry is unavailable right now. Please try again in a moment.",
            send: "Save the card and send the invitation",
            sendReady: "Send the invitation",
            sending: "Sending…",
            back: "Back to the request",
        },
        link: {
            title: "Your invitation is ready",
            emailSent: "We have sent the invitation to {email}.",
            shareIt: "Send this link to the expert, by the channel of your choice.",
            emailLabel: "Or have it emailed to the expert",
            emailSubmit: "Send by email",
            onlyOnce: "Copy it now: for security reasons, this link is shown only once.",
            copy: "Copy the link",
            copied: "Link copied",
            another: "Invite another expert",
        },
        list: {
            title: "Your invitations",
            empty: "You have not invited anyone yet.",
            sentOn: "Sent on {date}",
            expiresOn: "Open until {date}",
            viewed: "Opened by the expert",
            notViewed: "Not opened yet",
            emailedTo: "Emailed to {email}",
            linkOnly: "Shared by link",
            declineReason: "Their message: {reason}",
            revoke: "Withdraw",
            revokeTitle: "Withdraw this invitation?",
            revokeBody: "The link will stop working immediately. The expert will no longer be able to read or accept your request.",
            revokeConfirm: "Withdraw",
            // Mission issue de l'invitation (REFERRAL-3)
            followMission: "Follow the mission",
            openMission: "Open the mission",
            // L'expert propose plutôt une de ses prestations (REFERRAL-3B)
            redirectedTo: "The expert suggests their service {productName}, at {price}.",
            seeService: "See the service",
            seeStore: "Browse their store",
        },
        // Éléments qui rassurent l'expert invité, écrits par la plateforme et
        // jamais par l'assistant (REFERRAL-3C, AI-D059). Repris à l'identique dans
        // l'email d'invitation, sur la page de l'invitation et dans l'aperçu du client.
        trust: {
            title: "Why you can trust this request",
            signedBy: "Guaranteed by ExpertLoop",
            verifiedClient: "{clientName} has an ExpertLoop account with a verified email address.",
            cardSaved: "Their card is already saved: if you choose to be paid, the amount is reserved before you start working.",
            noCommitment: "Reading the request creates no account and commits you to nothing.",
            noBankDetails: "ExpertLoop never asks for your bank details by email. Payment setup only happens on Stripe, from your ExpertLoop space.",
            officialLink: "The link in this invitation leads to the ExpertLoop website ({domain}).",
        },
        // Présentation d'ExpertLoop à l'expert invité, qui ne connaît pas la
        // plateforme : pourquoi il reçoit ce message et comment cela se passe
        // (REFERRAL-3C, retour de recette du 01/10/2026). Repris dans l'email,
        // sur la page de l'invitation et dans l'aperçu du client.
        about: {
            title: "Why you are receiving this message",
            why: "{clientName} would like to work with you and used ExpertLoop to send you this request.",
            platform: "ExpertLoop is a platform where clients entrust requests to independent professionals. The request, the conversation, the delivery and the payment all happen in one secure space, so neither of you has to chase the other.",
            stepsTitle: "How it works",
            stepRead: "Read the request: no account needed, no commitment.",
            stepAnswer: "Accept it, decline it, or — if you already have an ExpertLoop store — propose one of your services instead.",
            stepAccount: "If you accept, you create a free account in a minute (email, password, first name). No store or profile to build first.",
            stepPaid: "If you choose to be paid, the amount is reserved on the client's card before you start, and paid to you once the client accepts your delivery. The proposed price is a maximum: you can charge less when you deliver.",
            commission: "A platform commission is deducted from paid missions. You can also choose to do the work free of charge.",
        },
        // Rédaction accompagnée de la demande (REFERRAL-3C)
        compose: {
            needLabel: "Your need, in a few words",
            needPlaceholder: "What do you need, in what context, and what result do you expect?",
            guideTitle: "How to write your request",
            guideNeed: "Focus on your need: what you expect from the expert, the context, and the result you hope for.",
            guideTrust: "No need to introduce yourself or to reassure the expert: ExpertLoop adds the guarantees that make your request credible.",
            guideReview: "You will review and correct the complete message before sending it.",
            prepare: "Prepare my message",
            preparing: "Preparing your message…",
            prepareAgain: "Prepare again from my need",
            previewTitle: "What the expert will receive",
            previewEmpty: "Write your need, then click « Prepare my message » to see the complete message.",
            requestLabel: "Your request (you can correct it)",
            fromAssistant: "Rewritten by our assistant from your need. Check it: nothing should be added that you did not say.",
            unchanged: "Your text is used as written.",
            useMyText: "Use my own text instead",
            outdated: "You changed your need after preparing the message. Prepare it again, or correct the request directly.",
            reviewRequired: "Prepare and review the message before continuing.",
            priceFirst: "Enter the price you propose first: it appears in the message the expert receives.",
        },
        keep: {
            saved: "This invitation is saved in your received invitations: you can come back to it from any device. This commits you to nothing.",
            seeReceived: "See my received invitations",
            emailLabel: "Not ready to answer? Email yourself this link so you don't lose it",
            emailSubmit: "Email me the link",
            emailSent: "Sent. Check your inbox.",
        },
        redirect: {
            title: "Propose one of your services",
            explanation: "Already on ExpertLoop? Point the client to one of your public services. They will also get a link to your store, and this invitation will be closed without anything being charged.",
            signInFirst: "Sign in to your ExpertLoop account to choose one of your services.",
            loading: "Loading your services…",
            noPublicService: "You have no public service yet. A private service must be published before you can propose it.",
            manageServices: "Manage my services",
            chooseLabel: "Which service do you propose?",
            messageLabel: "A word for the client (optional)",
            storeNotice: "The client will also receive a link to your store, to choose freely.",
            submit: "Propose this service",
        },
        received: {
            title: "Received invitations",
            intro: "Invitations you kept in your account. You can come back to them without the original link.",
            empty: "You have not kept any invitation yet.",
            from: "From {clientName}",
            open: "Open the invitation",
        },
        // Prestation créée pour la mission de l'expert invité (REFERRAL-3)
        product: {
            title: "Your service for this request",
            explanation: "On ExpertLoop, every mission is attached to one of your services. We create it for you from this request, so you have nothing to set up.",
            preparing: "Preparing a title and description…",
            nameLabel: "Service title",
            descriptionLabel: "Description",
            fromAssistant: "Suggested by our assistant from the request. You can change it: accepting applies what you see.",
            fromRequest: "Taken from the request. You can change it: accepting applies what you see.",
            privateNotice: "This service stays private: it does not appear in your public store. You will find it among your services, where you can edit it and, if you wish, publish it in your store.",
        },
        page: {
            loading: "Opening the invitation…",
            unavailableTitle: "Invitation unavailable",
            title: "{clientName} would like to work with you",
            intro: "{clientName} found you and would like to entrust you with this request on ExpertLoop.",
            priceLabel: "Proposed price",
            expiresOn: "This invitation is open until {date}.",
            noCommitment: "Reading this request creates no account and commits you to nothing.",
        },
        decide: {
            title: "Your answer",
            modeLabel: "How do you want to do it?",
            modePaid: "I charge the proposed price ({price})",
            modePaidHint: "The amount is reserved on the client's card as soon as your payment account can receive payments; if you don't have one yet, we guide you through it after you accept. It is paid to you when your work is delivered.",
            modeFree: "I do it free of charge",
            modeFreeHint: "No payment, no invoice. The request keeps its proposed price as a reference.",
            // Le prix proposé est un plafond : l'expert peut demander moins à la livraison
            modePaidMaximum: "This price is a maximum. When you deliver, you can charge less if the work turned out lighter: the client is only charged the final amount.",
            // Expert déjà sur ExpertLoop : orienter vers une prestation (REFERRAL-3B)
            proposeService: "Propose one of my services instead",
            acceptAs: "Accept as {email}",
            createAccountTitle: "Create your ExpertLoop account to accept",
            createAccountHint: "Use the address where you received this invitation and your account is ready right away. With another address, we first ask you to confirm it.",
            firstName: "First name",
            lastName: "Last name (optional)",
            email: "Email",
            password: "Password (8 characters minimum)",
            haveAccount: "Already have an account?",
            signIn: "Sign in to accept",
            accept: "Accept the request",
            accepting: "Accepting…",
            decline: "Decline",
            declineTitle: "Decline this request",
            declineReasonLabel: "A word for the client (optional)",
            declineConfirm: "Decline the request",
            declining: "Sending…",
            cancel: "Cancel",
        },
        outcome: {
            acceptedTitle: "You have accepted this request",
            acceptedBody: "Your account is linked to this invitation. We will guide you through the next steps with the client.",
            goDashboard: "Go to my dashboard",
            missionCreatedBody: "Your mission has been created. It starts as soon as the client's payment is reserved: you have nothing to accept again.",
            // Compte de paiement pas encore prêt : la réservation attend l'expert
            missionCreatedSetupBody: "Your mission has been created. To start it, finish setting up your payment account: the client's payment will then be reserved automatically, with nothing else to accept.",
            finishPaymentSetup: "Set up my payment account",
            // Mission gratuite (REFERRAL-4) : elle démarre tout de suite
            missionCreatedFreeBody: "Your free mission has been created and starts now. No payment is involved.",
            redirectedTitle: "You proposed one of your services",
            redirectedBody: "The client has been told about your service {productName} and can order it from your store.",
            answeredBody: "This invitation has already been answered.",
            openMission: "Open the mission",
            verifyTitle: "Confirm your email address",
            verifyBody: "We have sent a confirmation link to {email}. As soon as you confirm, your acceptance is recorded: you will not have to decide again.",
            declinedTitle: "You have declined this request",
            declinedBody: "The client has been told. Thank you for answering.",
            claimedByOther: "This invitation has already been accepted with another account.",
            signInToContinueTitle: "This invitation has been accepted",
            signInToContinueBody: "If you accepted it, sign in to continue.",
            signIn: "Sign in",
            goHome: "Back to ExpertLoop",
        },
    },
    clientFinances: {
        eyebrow: "Your client space",
        title: "Payments & invoices",
        subtitle: "Find your payments and your billing documents.",
        // Résumé de la période
        summaryLabel: "Summary for the selected period",
        period: "Period",
        period12m: "Last 12 months",
        period24m: "Last 24 months",
        periodAll: "All time",
        summaryPayments: "payments",
        summaryPaid: "paid",
        summaryInvoices: "invoices available",
        multiCurrencyNote: "Totals are shown per currency; amounts in different currencies are never added together.",
        // Filtres et recherche
        typeFilterLabel: "Payment type",
        typeAll: "All",
        typeService: "Services",
        typeMasterclass: "Masterclass",
        typeSubscription: "Subscriptions",
        searchLabel: "Search a payment",
        searchPlaceholder: "Expert, service or request reference",
        clearSearch: "Clear the search",
        searching: "Searching...",
        // Tableau
        columnDate: "Date",
        columnExpert: "Expert",
        columnProduct: "Service",
        columnType: "Type",
        columnAmount: "Amount",
        columnStatus: "Status",
        columnInvoice: "Invoice",
        statusPaid: "Paid",
        unknownExpert: "Expert",
        unknownProduct: "Service",
        total: "{count} payment{plural} in total",
        // Tri des colonnes : le libellé annonce ce que fera l'activation. La
        // formulation reste neutre pour convenir à une date, à un nom et à un
        // montant, que « du plus petit au plus grand » décrirait mal.
        sortAscending: "Sort by {column} in ascending order",
        sortDescending: "Sort by {column} in descending order",
        // Facture
        downloadInvoice: "Download the invoice",
        downloadInvoiceFor: "Download the invoice for {product}",
        downloadFailed: "The invoice could not be downloaded.",
        downloading: "Downloading...",
        viewInvoice: "View the invoice",
        viewInvoiceFor: "View the invoice for {product}",
        getInvoice: "Get the invoice",
        getInvoiceFor: "Get the invoice for {product}",
        preparingInvoice: "Preparing...",
        preparingInvoiceNote: "Your invoice is being prepared. It will be available in a few moments.",
        invoiceUnavailable: "No invoice can be issued for this payment.",
        invoiceFailed: "The invoice could not be prepared.",
        retryInvoice: "Try again",
        // Liens vers le parcours
        moreActions: "More actions",
        moreActionsFor: "More actions for {product}",
        viewRequest: "View the request",
        viewExpert: "View the expert",
        viewProduct: "View the service",
        // États
        loading: "Loading your payments...",
        loadFailed: "Your payments could not be loaded.",
        retry: "Try again",
        emptyTitle: "No payment yet",
        emptyBody: "Your payments will appear here once a service has been charged.",
        emptyTypeTitle: "No payment of this type",
        emptyTypeBody: "No payment of this type over the selected period.",
        emptySearchTitle: "No matching payment",
        emptySearchBody: "No payment matches your search.",
        // Deux lectures de la même page (BL-F7) : les règlements, sommes
        // réellement parties du compte, et les prestations, quel que soit leur
        // état. Le résumé chiffré n'appartient qu'à la première.
        viewLabel: "What to show",
        viewPayments: "Payments",
        viewServices: "Services",
        services: {
            intro: "Every service you ordered, whatever its current state. Amounts here are never added up.",
            filterLabel: "Filter by state",
            searchLabel: "Search a service",
            searchPlaceholder: "Expert, service or request reference",
            columnOrderedAt: "Ordered on",
            columnExpert: "Expert",
            columnProduct: "Service",
            columnAmount: "Amount",
            columnStatus: "State",
            columnActions: "Actions",
            // Ce que le montant représente : la même somme peut être bloquée,
            // prélevée ou rendue, et le lecteur doit le savoir sans ouvrir la demande.
            amountCharged: "charged",
            amountReserved: "reserved, not charged",
            amountReleased: "reservation released",
            amountNone: "not reserved",
            // Mission gratuite (REFERRAL-4)
            amountFree: "Free of charge",
            viewRequest: "Open the request",
            viewRequestFor: "Open the request for {product}",
            total: "{count} service{plural} in total",
            loading: "Loading your services...",
            loadFailed: "Your services could not be loaded.",
            emptyTitle: "No service yet",
            emptyBody: "Your services will appear here once you have sent a request to an expert.",
            emptyFilterTitle: "No service in this state",
            emptyFilterBody: "No service matches the selected state.",
            emptySearchTitle: "No matching service",
            emptySearchBody: "No service matches your search.",
        },
    },
    // Paramètres > Paiement (PAY-1) — comptes authentifiés uniquement
    paymentSettings: {
        title: "Payment methods",
        intro: "Your saved billing address and payment methods for your next requests.",
        loading: "Loading your payment settings...",
        loadFailed: "Your payment settings could not be loaded.",
        memoryUnavailable: "Payment preferences become available once your email address is verified.",
        billingAddress: "Billing address",
        noAddress: "No saved address.",
        addAddress: "Add an address",
        edit: "Edit",
        deleteAddress: "Delete the address",
        deleteAddressTitle: "Delete your saved address?",
        deleteAddressBody: "You will be asked for a billing address at your next payment.",
        addressSaved: "Address saved.",
        addressDeleted: "Address deleted.",
        addressIncomplete: "Please complete the address.",
        saveAddress: "Save the address",
        saving: "Saving...",
        saveFailed: "The address could not be saved. Please try again.",
        paymentMethods: "Payment methods",
        noCards: "No saved card. You can save a card during your next payment.",
        deleteCard: "Delete",
        deleteCardTitle: "Delete this card?",
        deleteCardBody: "{brand} •••• {last4} will be removed from your saved payment methods.",
        cardDeleted: "Card deleted.",
        deleteFailed: "This card could not be deleted. Please try again.",
        securityNote: "Sensitive card data stays with Stripe. ExpertLoop only shows the details needed to recognize your saved cards.",
    },
    paymentModal: {
        // Vocabulaire de réservation (UIUX-1) : réservé, aucun débit avant réalisation
        holdNote: "The amount is only reserved on your card. You are charged once the service has been delivered.",
        createFailed: "Failed to create payment",
        // Capacité d'encaissement lue chez Stripe (PAY-REFERRAL-2) : rendu avant
        // toute saisie de carte, sans rien réserver
        expertCannotAcceptPayments: "This expert cannot receive payments yet. Nothing has been reserved on your card. Please try again later.",
        continueToHold: "Continue to reserve the funds",
        loadingForm: "Loading payment form...",
    },
    // ─── Reprise d'une réservation par le client (PAY-REFERRAL-2) ───────────
    // Page ouverte depuis le lien reçu quand la réservation n'a pas pu se faire
    // sans le client. L'expert a déjà accepté : ne jamais laisser croire qu'il
    // doit refaire un geste, ni que le montant est débité.
    reservationResume: {
        title: "Confirm the reservation",
        loading: "Loading your mission...",
        intro: "{expertName} has accepted your request. To start the mission, {amount} must be reserved on your card.",
        bankConfirmation: "Your bank asks you to confirm this payment yourself.",
        cardDeclined: "Your bank declined the card you prepared. Please use another card.",
        cardUnavailable: "The card you prepared can no longer be used. Please use another card.",
        preparedCard: "We will use the card you prepared. Your bank may ask you to confirm.",
        newCard: "Card details",
        holdNote: "The amount is only reserved, not charged. You are charged once the mission is delivered and accepted.",
        confirm: "Confirm the reservation",
        confirming: "Confirming...",
        useAnotherCard: "Use another card",
        authorizedTitle: "The amount is reserved",
        authorizedBody: "Thank you. The mission can start: the expert has nothing to do again on this side.",
        unavailableTitle: "This link is no longer valid",
        unavailableBody: "It may have expired or already been used. If you have already confirmed the reservation, there is nothing more to do.",
        expertNotReady: "This expert cannot receive payments yet. Nothing has been reserved on your card. Please try again later.",
        failed: "The reservation did not go through. Please try again or use another card.",
        backToDashboard: "Go to my dashboard",
    },
    // ─── Identité client vérifiée (code à 6 chiffres au paiement) ───────────
    // « Compte » ici = identité client reconnue par email vérifié, distincte du
    // compte User avec mot de passe (auth.*) : garder des libellés qui ne
    // promettent pas de connexion classique.
    clientIdentity: {
        accountQuestion: "Have you already ordered on ExpertLoop?",
        accountYes: "Yes, with this email",
        accountNo: "No, first time",
        emailLabel: "Email address",
        emailHint: "We'll send you a 6-digit confirmation code.",
        sendCode: "Send code",
        sendingCode: "Sending...",
        codeSentTo: "Enter the 6-digit code sent to {email}.",
        codeLabel: "Verification code",
        verify: "Verify",
        verifying: "Verifying...",
        verified: "Email verified",
        verifyToPay: "Verify your email to confirm the payment.",
        codeInvalid: "Incorrect code. Please try again.",
        codeExpired: "This code has expired. Request a new one.",
        tooManyAttempts: "Too many attempts. Please try again later.",
        sendFailed: "The code could not be sent. Please try again.",
        resendCode: "Resend code",
        changeEmail: "Change email",
        channelEmail: "Email",
        channelPhone: "Text message",
        channelPhoneSoon: "Coming soon",
        savedCards: "Your saved cards",
        savedCardExpiry: "Expires {month}/{year}",
        useSavedCard: "Pay with this card",
        useAnotherCard: "Use another card",
        welcomeBack: "Good to see you again! Your details have been filled in.",
        prepareFailed: "Your card could not be prepared. Please try again.",
        // Accueil PWA client (icône tapée directement, sans lien)
        homeGreetingNamed: "Good to see you again, {name}!",
        homeGreeting: "Good to see you again!",
        homeChatsTitle: "Your conversations",
        homeNoChats: "No conversations on this device yet.",
        homeOpenChat: "Open",
        homeOffline: "You appear to be offline — showing saved information.",
        // Reconnexion (jeton local absent ou expiré) : même mécanisme email + code
        reconnectTitle: "Welcome back",
        reconnectSubtitle: "Enter your email address to receive a 6-digit code.",
    },
    // Page Abonnement du tableau de bord expert. Ces clés étaient appelées sans
    // exister au catalogue : la page affichait ses libellés bruts. Copie à
    // relire par le produit.
    subscription: {
        subtitle: "Choose the plan that matches your activity.",
        loading: "Loading your subscription...",
        free: "Free",
        perMonth: "/month",
        commissionLabel: "commission per mission",
        // Palier de commission d'une formule (lot SUB-1), valeurs de la grille
        commissionAbove: "{rate}% beyond {threshold} of sales per month",
        // Période Boost d'un nouvel expert : un état, jamais un choix
        boostName: "Boost",
        boostUntil: "Boost period until {date}: no subscription fee and a reduced commission.",
        // Mois de Pro offerts par le parrainage, et remise d'un filleul (lot E4a)
        referralGiftUntil: "Pro offered by your referrals until {date}, with no subscription fee.",
        refereeDiscount: "Your referral offer: {percent}% off your first {count} month{plural} of {plans}, applied when you subscribe.",
        recommended: "Recommended",
        currentPlan: "Current plan",
        upcomingPlan: "Upcoming plan",
        upcoming: "Upcoming",
        nextMonth: "Next month",
        scheduled: "Scheduled",
        upgrade: "Upgrade",
        downgrade: "Downgrade",
        switchPlan: "Switch plan",
        renews: "Renews on",
        planChangesOn: "Plan changes on",
        accessUntil: "Access until",
        cancelsAtPeriodEnd: "Cancels at the end of the period",
        cancel: "Cancel subscription",
        canceling: "Canceling...",
        cancelChange: "Cancel the scheduled change",
        cancelConfirmBody: "Your subscription stays active until the end of the current period, then switches to the Free plan. You can reactivate it at any time.",
        reactivate: "Reactivate",
        reactivating: "Reactivating...",
        savingsInfo: "A higher plan lowers your commission on every mission. From a few missions a month, the saving usually covers the subscription.",
        invoiceHistory: "Invoice history",
        // Une facture de changement de formule mêle un avoir sur l'ancienne et le
        // prorata de la nouvelle : aucune de ses lignes ne décrit ce qui a été payé.
        invoicePlanChange: "Plan change",
        // Statuts de facture rendus par Stripe, traduits ici plutôt qu'affichés
        // tels quels en anglais.
        invoiceStatusPaid: "Paid",
        invoiceStatusOpen: "Due",
        invoiceStatusDraft: "Draft",
        invoiceStatusVoid: "Voided",
        invoiceStatusUncollectible: "Uncollectible",
    },
    subscriptionModal: {
        switchPlan: "Switch Plan",
        subscribeTo: "Subscribe to {name}",
        newPlan: "New Plan",
        // Suffixe de périodicité : forme courte pour les lignes compactes de
        // comparaison de plans, forme longue pour les blocs tarifaires.
        perMonthShort: "/mo",
        perMonth: "/month",
        chargeToday: "Charge today",
        noCharge: "No charge",
        nextBilling: "Next billing ({date})",
        endOfPeriod: "end of period",
        downgradeNote: "You will keep your {plan} benefits until {date}, then switch to {newPlan}.",
        endOfBillingPeriod: "the end of your billing period",
        calculatingProration: "Calculating proration...",
        proratedToday: "Prorated charge today",
        futureBilling: "Future billing",
        chargedImmediately: "Your card on file will be charged immediately for the prorated amount.",
        changeFailed: "Failed to change plan",
        back: "Back",
        scheduleDowngrade: "Schedule Downgrade",
        confirmSwitch: "Confirm Switch",
        planName: "{name} Plan",
        cancelAnytime: "You can cancel anytime.",
        createFailed: "Failed to create subscription",
        reviewChange: "Review Change",
        continueToPayment: "Continue to Payment",
        confirmFailed: "Failed to confirm subscription",
        subscribe: "Subscribe",
    },
    captureOrder: {
        captured: "Payment captured",
        confirmMessage: "Capture {amount}{client}? The funds will be transferred to your Stripe account.",
        fromClient: " from {name}",
    },
    guestLogin: {
        accessFailed: "Failed to access chat. Please check your email.",
        title: "Access Chat",
        subtitle: "Enter your email to access this chat",
        emailPlaceholder: "Your email",
        accessing: "Accessing...",
        // Porte de sortie pour un titulaire de compte (expert venu d'un email)
        accountSignIn: "Have an account? Sign in",
    },
    // ─── Page de contact du support (hors litige — lot 8 v3) ────────────────
    support: {
        // Retour vers la page d'où le client est venu (aide du tunnel, menu…)
        back: "Back",
        title: "Contact support",
        intro: "A question about your account, a bug, a suggestion? Write to us — we answer every message.",
        mediationNotice: "For a dispute about an order (delivery, refund), use mediation from the conversation concerned:",
        mediationLink: "open my conversations",
        emailLabel: "You can also reach us directly at",
        // Demande rattachée à une conversation
        aboutConversation: "This message will mention conversation {ref}, so we can find your case.",
        decisionSubject: "About a mediation decision",
        form: {
            name: "Your name",
            email: "Your email",
            subject: "Subject",
            message: "Your message",
            messagePlaceholder: "Describe your request…",
            // La longueur minimale est aussi imposée par le serveur : l'annoncer
            // évite un bouton qui refuse sans dire pourquoi.
            messageHint: "At least {min} characters.",
            missingFields: "Please fill in every field before sending.",
            invalidEmail: "Please enter a valid email address.",
            messageTooShort: "Your message must be at least {min} characters long.",
            submit: "Send message",
            sending: "Sending…",
            sent: "Message sent — we will get back to you by email.",
            error: "The message could not be sent. Please try again or email us directly.",
        },
    },
    chatHeader: {
        menu: "More options",
        invalidEmail: "Please enter a valid email address",
        updateEmailFailed: "Failed to update email",
        registeredAccountEmail: "This email belongs to a registered account. Please sign in instead.",
        reported: "Successfully reported",
        reportUser: "Report user",
        editEmail: "Edit email",
        reopen: "Reopen",
    },
    notificationsPanel: {
        justNow: "Just now",
        minutesAgo: "{count}m ago",
        hoursAgo: "{count}h ago",
        daysAgo: "{count}d ago",
        markAllRead: "Mark all as read",
        clearRead: "Clear read",
        empty: "No notifications yet",
        loadMore: "Load More",
    },
    // Titres et textes des notifications du panneau. Le serveur enregistre la clé
    // et ses variables ; la traduction se fait à l'affichage, ce qui permet de
    // changer de langue sans perdre l'historique.
    notifications: {
        newPaidRequest: {
            title: "New paid request",
            body: "{clientName} wants \"{productName}\" and has authorized payment. Review and accept or decline.",
        },
        retryOfferAccepted: {
            title: "Revised delivery accepted",
            body: "{clientName} accepted your new delivery offer for \"{productName}\". The delivery countdown is running.",
        },
        requestAwaitingFunds: {
            title: "Request awaiting funds",
            body: "{clientName} verified their email for \"{productName}\". The request becomes active once funds are reserved — nothing to do yet.",
        },
        requestUpdated: {
            title: "Request updated",
            body: "The client has updated their request. Review the changes.",
        },
        chatAccepted: {
            title: "Request accepted",
            body: "{expertName} accepted your request.",
        },
        // Fin d'une mission gratuite (REFERRAL-4) : jamais de montant
        freeMission: {
            completedTitle: "Free mission complete",
            completedBody: "The free mission is complete. No payment was involved.",
            expiredTitle: "Free mission ended",
            expiredBody: "The free mission ended because the delivery deadline passed. No payment was involved.",
            canceledTitle: "Free mission canceled",
            canceledBody: "The free mission was canceled. No payment was involved.",
        },
        // Mise en route d'une mission d'invitation (REFERRAL-3)
        invitationMissionStartedClient: {
            title: "Your mission has started",
            body: "{expertName} accepted your request and the amount is reserved.",
            bodyFree: "{expertName} accepted your request and will carry it out free of charge.",
        },
        invitationMissionStartedExpert: {
            title: "Your mission starts now",
            body: "The amount proposed by {clientName} is reserved. You can start working.",
            bodyFree: "Your free mission for {clientName} has started. You can start working.",
        },
        chatDeclined: {
            title: "Request declined",
            body: "{expertName} declined your request.",
            bodyWithReason: "{expertName} declined your request. Reason: {reason}",
        },
        newMessage: {
            title: "New message",
            body: "{senderName}: {preview}",
        },
        translationAllowance: {
            title: "Translation allowance: {percent}% used",
            body: "You have used {percent}% of this period's translation allowance. Upgrade your plan to keep every conversation translated.",
        },
        serviceDelivered: {
            title: "Service delivered",
            body: "Your expert has delivered the service. Please review and confirm.",
        },
        deliveryCanceled: {
            title: "Delivery canceled",
            body: "The expert has canceled their delivery. A new delivery will be submitted soon.",
        },
        deliveryRejected: {
            title: "Delivery rejected",
            body: "Your client rejected the delivery: {reason}",
        },
        deliveryAutoAccepted: {
            title: "Delivery automatically accepted",
            bodyClient: "Your delivery was automatically accepted after the review period expired.",
            bodyExpert: "Your delivery was automatically accepted and payment has been captured.",
        },
        deliveryComplement: {
            title: "Additional delivery",
            body: "Your expert has shared an additional delivery. It does not change your order or payment.",
        },
        ratingUpdated: {
            title: "Rating updated",
            body: "Your client updated their rating to {rating} out of 5.",
        },
        mediationRequested: {
            title: "Mediation requested",
            body: "{requesterName} has requested mediation for this order.",
        },
        mediationReopened: {
            title: "Mediation reopened",
            body: "Support has reopened the mediation for this order. Delivery and payment actions stay locked until it decides again.",
        },
        paymentReceived: {
            title: "Payment received",
            body: "{clientName} paid {amount}.",
        },
        paymentCaptured: {
            title: "Payment charged",
            body: "{amount} has been charged for your service.",
        },
        deliveryDeadlineWarning: {
            title: "Delivery deadline approaching",
            body: "You have {remainingTime} left to deliver \"{productName}\" and capture the payment.",
            bodyFree: "You have {remainingTime} left to deliver the free mission \"{productName}\".",
        },
        captureSoon: {
            title: "Capture the payment soon",
            body: "Some banks cancel authorizations earlier than the delivery deadline. Deliver and capture \"{productName}\" soon to avoid losing this payment.",
        },
        orderExpired: {
            title: "Order expired",
            body: "Your order for \"{productName}\" has expired. The {amount} payment was released back to the client.",
        },
        paymentRefundedDeadline: {
            title: "Payment refunded",
            body: "Your {amount} payment for \"{productName}\" has been refunded because the delivery deadline expired.",
        },
        authorizationExpired: {
            title: "Payment authorization expired",
            body: "The client's bank canceled the authorization for \"{productName}\" before you captured it. The {amount} payment was released back to the client.",
        },
        paymentRefundedAuthorization: {
            title: "Payment refunded",
            body: "Your {amount} payment for \"{productName}\" has been refunded because your bank's authorization expired before the expert captured it.",
        },
        cancellationRequested: {
            title: "Cancellation requested",
            body: "{clientName} requested to cancel their order for \"{productName}\". Please review and respond.",
        },
        refundRequested: {
            title: "Refund requested",
            body: "{clientName} asked for a refund on \"{productName}\". You decide how much to give back.",
        },
        cancellationDenied: {
            title: "Cancellation denied",
            body: "The expert denied your cancellation request. Reason: {reason}",
        },
        cancellationApproved: {
            title: "Cancellation approved",
            bodyAuthorizationReleased: "Your cancellation was approved. The payment authorization has been released.",
            bodyRefunded: "Your cancellation was approved and the payment has been refunded.",
            bodyRefundPending: "Your cancellation was approved and the refund is being processed.",
        },
        refundApproved: {
            title: "Refund approved",
            bodyPartial: "{refunded} of the {captured} you paid has been refunded.",
            bodyFull: "The full {refunded} you paid has been refunded.",
            bodyPending: "A refund of {refunded} has been initiated.",
        },
        refundDenied: {
            title: "Refund declined",
            body: "The expert declined your refund request. Reason: {reason}",
        },
        revisedDeliveryOffered: {
            title: "Revised delivery offered",
            body: "The expert has offered a revised delivery for {amount} with {hours}h delivery.",
        },
    },
    mediaLibrary: {
        title: "Videos",
        subtitle: "Videos exchanged in your missions, kept and replayable.",
        empty: "No video yet.",
        listLabel: "Available videos",
        columnDate: "Date and time",
        columnInformation: "Video",
        columnDetails: "Details",
        columnActions: "Actions",
        play: "Play",
        openConversation: "Open the conversation",
        playerTitle: "Video from {date}",
        durationUnknown: "Duration unavailable",
        senderExpert: "Expert",
        senderClient: "Client",
        uses: "Uses",
        playbackDisabled: "Playback unavailable",
        purpose: {
            conversation: "Conversation",
            delivery: "Deliverable",
            sessionRecording: "Session recording",
            masterclassReplay: "Masterclass replay",
        },
        status: {
            uploadPending: "Waiting to upload",
            uploading: "Uploading",
            processing: "Processing",
            ready: "Ready",
            failed: "Processing failed",
            expired: "Expired",
            deleted: "Deleted",
        },
        usageTitle: "Media usage — {period}",
        usageRealtime: "Live video usage",
        usageStored: "Stored video usage",
        usageDelivered: "Delivered video usage",
        usageRecording: "Recording usage",
        minutes: "{count} minute{plural}",
        usageInformational: "These figures are informational: no limit is enforced at this stage.",
    },
    dashboardLayout: {
        openMenu: "Open menu",
        closeMenu: "Close menu",
        moreActions: "More actions",
        language: "Language",
        theme: "Theme",
        unreadNotifications: "{count} unread notification{plural}",
        activity: "Activity",
        store: "My store",
        admin: "Admin",
        client: "Client",
        logout: "Logout",
        reconnecting: "Reconnecting...",
        completeYourSetup: "Complete Your Setup",
        stripeOnboardingBody: "You need to complete your Stripe onboarding to start receiving payments from clients.",
        completeOnboarding: "Complete Onboarding",
        disableExpertMode: "Disable Expert Mode",
    },
    adminNav: {
        overview: "Overview",
        finance: "Finance",
        allChats: "All Chats",
        users: "Users",
        reports: "Reports",
        auditLog: "Audit Log",
        adminUsers: "Admin Users",
        securitySettings: "Security Settings",
        translations: "Translations",
        // Parrainage entre experts (lot E6)
        expertReferrals: "Expert referrals",
        signOut: "Sign Out",
    },
    // ─── Admin : vue d'ensemble de la plateforme ────────────────────────────
    /* Les chiffres viennent tous de la fondation analytique ADM-1. Aucun libellé
       ne doit promettre davantage que ce que la métrique mesure : « commission
       marketplace » n'est pas « revenu de la plateforme ». */
    adminOverview: {
        title: "Overview",
        subtitle: "Platform activity and what needs attention.",
        periodLabel: "Period",
        period7d: "7 days",
        period30d: "30 days",
        periodMonth: "This month",
        period12m: "12 months",
        loading: "Loading…",
        empty: "Nothing recorded over this period.",
        loadFailed: "This data could not be loaded.",
        retry: "Try again",
        forbidden: "You do not have permission to view this data.",
        // Zone « à traiter »
        attentionTitle: "Needs attention",
        attentionAllClear: "Nothing is waiting for you right now.",
        // Bloc compact des médiations à régler
        mediationBlockTitle: "Mediations to settle",
        mediationBlockSince: "since {date}",
        mediationBlockOpen: "Open case",
        mediationBlockMore: "See all {count} mediations",
        openReports: "Open reports",
        refundsPending: "Refunds in progress",
        refundsPendingHint: "No administration screen lists these orders yet.",
        review: "Review",
        // Zone activité
        activityTitle: "Activity",
        metricLabel: "Measure",
        metricGmv: "Captured volume",
        metricPlatformFee: "Marketplace commission",
        metricCapturedOrders: "Captured orders",
        capturedOrders: "Captured orders",
        versusPrevious: "vs. previous period",
        // Zone business
        businessTitle: "Marketplace",
        experts: "Experts",
        clients: "Clients",
        newUsers: "New accounts",
        ordersCreated: "Orders created",
        gmv: "Captured volume",
        platformFee: "Marketplace commission",
        effectiveFeeRate: "Effective commission rate",
        stripeFee: "Stripe fees",
        authorized: "Funds held, not captured",
        refunded: "Refunded",
        partialData: "{count} of {total} order{plural} in this currency had no recorded value; a fallback was used.",
        marketplaceOnlyNotice: "These figures cover marketplace orders only. Expert subscriptions and AI costs are not measured yet, so none of this is a net result.",
        // Parcours
        funnelTitle: "Journeys",
        clientFunnel: "Client journey",
        expertFunnel: "Expert journey",
        funnelRequestCreated: "Requests created",
        funnelRequestAccepted: "Requests accepted",
        funnelPaymentAuthorized: "Payments authorised",
        funnelDelivered: "Delivered",
        funnelClientConfirmed: "Confirmed by client",
        funnelCaptured: "Captured",
        funnelRegistered: "Experts registered",
        funnelStripeReady: "Stripe onboarding done",
        funnelPublishedProduct: "Published a service",
        funnelFirstRequest: "Received a first request",
        funnelFirstSale: "Made a first sale",
        // Invitation d'experts externes (REFERRAL-5)
        invitationFunnel: "Expert invitations",
        funnelInvitationIssued: "Invitations sent",
        funnelInvitationViewed: "Invitations opened",
        funnelInvitationAccepted: "Invitations accepted",
        funnelInvitationAccountCreated: "Expert accounts created",
        funnelInvitationFundedMission: "Paid missions funded",
        funnelInvitationFreeMission: "Free missions started",
        funnelInvitationCompletedMission: "Missions completed",
        funnelInvitationDeclined: "Invitations declined",
        funnelInvitationRedirected: "Another service proposed",
        funnelInvitationRedirectOrder: "Orders after a proposal",
        // Parrainage entre experts (lot E6)
        expertReferralFunnel: "Expert referrals",
        funnelReferralAttached: "Experts referred",
        funnelReferralStripeReady: "Stripe onboarding done",
        funnelReferralPublished: "Published their shop",
        funnelReferralFirstSale: "Made a first sale",
        funnelReferralSubscribed: "Paid a subscription",
        funnelLimitations: "What these numbers do not show",
    },
    // ─── Admin : finance de la place de marché ──────────────────────────────
    /* Cette page ne couvre que les commandes encaissées. Aucun libellé ne doit
       suggérer une rentabilité : les abonnements et les coûts d'intelligence
       artificielle ne sont pas encore mesurés. */
    // ─── Admin : parrainage entre experts (lot E6) ──────────────────────────
    /* Le revenu perdu ne compte que les crédits versés à un parrain qui paie un
       abonnement ; aucun libellé ne doit le présenter comme le coût complet. */
    adminExpertReferral: {
        title: "Expert referrals",
        subtitle: "Experts who joined through another expert's link, what they became and what their rewards cost.",
        disabled: "The expert referral programme is switched off. Nothing is measured while it stays off.",
        referrerFilter: "Referrer",
        allReferrers: "All referrers",
        cohortTitle: "Referred experts",
        activeReferrers: "Active referrers",
        publishedExperts: "Experts who can refer today",
        attachmentRate: "Referrals per published expert per month",
        notAvailable: "Not available",
        rewardsTitle: "Rewards and what referred experts brought in",
        proMonths: "Free Pro months recorded",
        rewardFaceValue: "Rewards recorded",
        rewardLostRevenue: "Revenue given up",
        refereeRevenue: "Brought in by referred experts",
        costRevenueRatio: "Revenue given up per unit brought in",
        noRewards: "No reward recorded for these referrals yet.",
        topReferrersTitle: "Referrers",
        columnReferrer: "Referrer",
        columnAttached: "Referred",
        columnPublished: "Published",
        columnFirstSale: "First sale",
        columnSubscribed: "Subscribed",
        filterBy: "Show only {name}",
        noReferrals: "No expert was referred in this period.",
        alertsTitle: "Cost alerts",
        alertsOff: "The cost alert is switched off until its threshold is decided.",
        alertsThreshold: "Referred experts whose revenue given up exceeds {threshold} of what they brought in.",
        alertsNone: "No referred expert is above the threshold.",
        alertNoRevenue: "nothing brought in yet",
        applicationTitle: "Applying rewards",
        applicationDryRun: "Simulation: these amounts show what would be applied; nothing is written to Stripe or to accounts.",
        applicationLive: "Rewards are applied: credits are written to Stripe before each renewal, free Pro months are granted.",
        plannedCredit: "Credit that would be applied",
        plannedProMonths: "Free Pro months that would be granted",
        appliedCredit: "Credit applied",
        appliedProMonths: "Free Pro months granted",
        discrepancies: "Differences with Stripe",
        retentionTitle: "Data retention",
        retentionDryRun: "Deletion is switched off: these counts show what it would remove, nothing has been deleted.",
        retentionEnabled: "Deletion is on: these records are removed at the next hourly run.",
        retentionReferrals: "Referrals past their retention period",
        retentionJournal: "Referral log entries past their retention period",
        retentionInvitationJournal: "Client invitation log entries past their retention period",
        limitations: "What these numbers do not show",
    },
    adminFinance: {
        title: "Finance",
        subtitle: "Captured marketplace orders, commissions and Stripe fees.",
        scopeNotice: "This page covers marketplace orders only. Expert subscription revenue and AI costs are not measured yet, so nothing here is a net result for the platform.",
        // Chiffres par devise
        gmv: "Captured volume",
        platformFee: "Marketplace commission",
        stripeFee: "Stripe fees",
        effectiveFeeRate: "Effective commission rate",
        captureCount: "Captured orders",
        averageOrderValue: "Average order",
        refunded: "Refunded",
        refundCount: "Refunds",
        feeReturned: "Commission returned",
        feeRetained: "Stripe fees kept",
        feeReturnedFallbackNotice: "{count} refund{plural} in this currency had no commission figure from Stripe; it was reconstructed in proportion to the amount refunded.",
        authorized: "Funds held, not captured",
        feeFallbackNotice: "{count} order{plural} in this currency had no recorded commission; the shared fallback rule was applied.",
        stripeFeeMissingNotice: "{count} order{plural} in this currency had no Stripe fee recorded; those orders add nothing to the total and no rate was assumed.",
        // Transactions
        transactionsTitle: "Source transactions",
        transactionsHint: "Every figure above is the sum of these orders.",
        basisLabel: "Show",
        basisCapture: "Sales captured",
        basisRefund: "Refunds completed",
        refundsHint: "Refunds completed during this period. Each line keeps the date of the sale it cancels, which may fall in an earlier period.",
        columnRefundedAt: "Refunded on",
        noRefunds: "No refund was completed over this period.",
        currencyFilter: "Currency",
        allCurrencies: "All currencies",
        columnCapturedAt: "Captured on",
        columnOrder: "Order",
        columnAmount: "Amount",
        columnStripeFee: "Stripe fee",
        fallbackMark: "reconstructed",
        notRecorded: "not recorded",
        /* BL-F8 : une commande occupe une ligne, ses mouvements se déplient sous
           elle. Chaque mouvement garde sa propre date, et la devise devient une
           colonne à part entière. */
        columnCurrency: "Currency",
        columnStatus: "State",
        columnMovements: "Movements",
        expandOrder: "Show the movements of order {order}",
        collapseOrder: "Hide the movements of order {order}",
        movementCount: "{count} movement{plural}",
        movementCapture: "Sale captured",
        movementRefund: "Refund completed",
        movementDate: "Date",
        movementAmount: "Amount",
        movementFee: "Commission",
        movementFeeReturned: "Commission returned",
        orderStatus: {
            captured: "captured",
            refund_pending: "refund in progress",
            refunded: "refunded",
            canceled: "canceled",
            expired: "expired",
            authorized: "funds held",
            pending_authorization: "awaiting authorization",
            cancellation_requested: "cancellation requested",
            settled_free: "free mission completed",
        },
        noTransactions: "No order was captured over this period.",
        previousPage: "Previous",
        nextPage: "Next",
        pageOf: "Page {page} of {totalPages}",
        transactionCount: "{count} captured order{plural}",
        // Contribution suivie (ADM-6)
        /* Aucun de ces libellés ne doit suggérer un bénéfice : la contribution ne
           couvre que les revenus et coûts variables réellement mesurés. */
        contributionTitle: "Tracked contribution",
        contributionIntro: "What remains once the variable costs the platform can measure are removed. This is not a profit, not a net result and not an amount available to withdraw.",
        contributionFormula: "Formula",
        contributionExclusions: "Not included",
        contributionValue: "Tracked contribution",
        contributionUnavailable: "This figure cannot be shown for this period without being misleading.",
        contributionComponents: "Components",
        componentPlatformFee: "Marketplace commission",
        componentSubscriptions: "Subscriptions collected",
        componentStripeFee: "Stripe fees",
        componentFeeReturned: "Commission returned on refunds",
        componentAiCost: "AI cost measured",
        reliabilityTitle: "Reliable from",
        reliabilitySubscriptions: "Subscription ledger",
        reliabilityAi: "AI usage ledger",
        reliabilityNever: "not started yet",
        gapSubscriptionLedgerEmpty: "The subscription ledger holds no entry yet, so collected subscriptions cannot be counted.",
        gapPeriodPrecedesSubscriptionLedger: "This period starts before the subscription ledger did, so part of it is not covered.",
        gapAiLedgerEmpty: "The AI usage ledger holds no entry yet, so AI costs cannot be counted.",
        gapPeriodPrecedesAiLedger: "This period starts before the AI usage ledger did, so part of it is not covered.",
        gapAiCostUnpriced: "{count} AI call{plural} could not be priced, because no rate has been set for the models in use.",
        gapAiCostOtherCurrency: "Some AI costs are in another currency and are never subtracted from this one.",
    },
    // ─── Admin : gestion des traductions ────────────────────────────────────
    adminI18n: {
        title: "Translations",
        subtitle: "Review, correct and regenerate the translated catalogs.",
        loading: "Loading translations...",
        loadFailed: "Failed to load translations",
        sourceLocale: "Source language",
        keysInCatalog: "{count} keys in the source catalog",
        // Colonnes et états
        locale: "Language",
        status: "Status",
        version: "Version",
        translated: "Translated",
        missing: "Missing",
        rejected: "Rejected",
        override: "Corrected",
        stale: "Outdated correction",
        lastTranslated: "Last generated",
        never: "Never",
        outdatedBadge: "Behind the source",
        // Actions sur une locale
        addLocale: "Add a language",
        addLocalePlaceholder: "Language code (e.g. de, es, it)",
        addLocaleSubmit: "Add",
        addLocaleFailed: "Failed to add the language",
        retranslate: "Regenerate what is missing",
        retranslateAll: "Regenerate everything",
        retranslateStarted: "Generation started. This page updates on its own.",
        retranslateRunning: "A generation is already running.",
        retranslateFailed: "Failed to start the generation",
        deleteLocale: "Delete",
        deleteLocaleTitle: "Delete this language?",
        deleteLocaleBody: "The translated catalog for {locale} will be removed for good. The language can be generated again afterwards, at the cost of a full translation.",
        deleteLocaleConfirm: "Delete the language",
        deleteLocaleFailed: "Failed to delete the language",
        superAdminOnly: "Reserved for super admins",
        // Détail d'une locale
        backToLocales: "Back to languages",
        searchPlaceholder: "Search a key or a text...",
        filterAll: "All",
        noKeys: "No key matches this filter.",
        sourceText: "English source",
        translation: "Translation",
        editKey: "Correct",
        saveKey: "Save",
        cancelEdit: "Cancel",
        saveFailed: "Failed to save the correction",
        resetKey: "Restore the automatic translation",
        resetFailed: "Failed to restore the automatic translation",
        retranslateKey: "Regenerate this key",
        correctedBy: "Corrected by {author}",
        staleHint: "The English source changed after this correction: the automatic translation is served again.",
        rejectionHint: "Left in English by the validation: {reason}",
        placeholderMismatch: "The placeholders must be exactly those of the English source: {expected}",
        untranslated: "Not translated",
        pageOf: "Page {page} of {totalPages}",
        keysCount: "{count} keys",
    },
    adminMediations: {
        expert: "Expert",
        client: "Client",
        guestUser: "Guest User",
    },
    adminChatsPage: {
        totalChats: "{count} total chats",
        searchPlaceholder: "Search by name, email or product…",
        search: "Search",
        clearSearch: "Clear search",
        mediationFilterLabel: "Mediation",
        mediationAll: "All mediations",
        mediationPending: "Mediation to settle",
        mediationResolvedFilter: "Mediation resolved",
        mediationNone: "No mediation",
        allStatus: "All Status",
        // Statuts bruts du filtre admin : ces libellés référençaient des clés
        // `chatsPage.filter.*` disparues du catalogue (défaut préexistant révélé
        // par la reconstruction du paquet i18n)
        pending: "Pending",
        accepted: "Accepted",
        declined: "Declined",
        abandoned: "Abandoned",
        emptyTitle: "No Chats Found",
        emptyBody: "Try adjusting your filters or search query.",
        product: "Product",
        status: "Status",
        order: "Order",
        created: "Created",
        actions: "Actions",
        guest: "Guest",
        mediation: "Mediation",
        // Une médiation tranchée n'est plus un litige : la pastille doit le dire,
        // sans quoi le support relit indéfiniment un dossier qu'il a clos.
        mediationResolved: "Mediation resolved",
        noOrder: "No order",
    },
    adminUsersPage: {
        loadFailed: "Failed to load users",
        totalUsers: "{count} total users",
        allRoles: "All Roles",
        experts: "Experts",
        clients: "Clients",
        guests: "Guests",
        loading: "Loading users...",
        emptyTitle: "No Users Found",
        user: "User",
        role: "Role",
        auth: "Auth",
        rating: "Rating",
        joined: "Joined",
        guest: "Guest",
        noAccount: "No account",
        verified: "Verified",
        notVerified: "Not verified",
        chats: "chats",
        noRatings: "No ratings",
        lastActivity: "Last: {date}",
        notAvailable: "N/A",
        store: "Store",
    },
    adminLogin: {
        verificationFailed: "Verification failed",
        twoFactorTitle: "Two-Factor Authentication",
        enterRecoveryCode: "Enter one of your recovery codes",
        enterAuthenticatorCode: "Enter the 6-digit code from your authenticator app",
        recoveryCode: "Recovery Code",
        verificationCode: "Verification Code",
        verifying: "Verifying...",
        verify: "Verify",
        useAuthenticatorInstead: "Use authenticator app instead",
        useRecoveryCode: "Use a recovery code",
        backToLogin: "Back to login",
        forgotPassword: "Forgot your password?",
    },
    adminPasswordReset: {
        forgotTitle: "Forgot your password?",
        forgotIntro: "Enter the email address of your administration account. If it exists, we will send you a link to choose a new password.",
        send: "Send the link",
        sending: "Sending...",
        sentTitle: "Check your inbox",
        sentBody: "If an administration account exists with this address, a reset link is on its way. It can be used once and stays valid for 30 minutes.",
        requestFailed: "The request could not be sent. Please try again in a few minutes.",
        tooManyRequests: "Too many requests. Please try again later.",
        backToLogin: "Back to sign in",
        resetTitle: "Choose a new password",
        resetIntro: "Choose a new password for your administration account. Every open session will be signed out.",
        newPassword: "New password",
        confirmPassword: "Confirm new password",
        passwordTooShort: "The password must contain at least 8 characters.",
        passwordMismatch: "The two passwords do not match.",
        resetSubmit: "Change the password",
        resetting: "Changing...",
        resetSuccessTitle: "Password changed",
        resetSuccessBody: "You can now sign in with your new password. If two-factor authentication is enabled on your account, you will still be asked for your code.",
        invalidLink: "This link is invalid or has expired. Request a new one.",
        missingToken: "This link is incomplete. Request a new one.",
        requestNewLink: "Request a new link",
    },
    adminGuestDetail: {
        loadFailed: "Failed to load guest",
        loading: "Loading guest details...",
        backToUsers: "← Back to Users",
        notFound: "Guest not found",
        basicInfo: "Basic Info",
        authProvider: "Auth Provider",
        emailVerified: "Email Verified",
        memberSince: "Member Since",
        activity: "Activity",
        firstSeen: "First Seen",
        lastSeen: "Last Seen",
        totalChats: "Total Chats",
        orders: "Orders",
        none: "None",
        chatStatusBreakdown: "Chat Status Breakdown",
        chatsCount: "Chats ({count})",
        noChats: "No Chats",
        noChatsBody: "This guest has no chats.",
    },
    adminAudit: {
        loadFailed: "Failed to load audit log",
        subtitle: "{count} entries - GDPR compliance tracking",
        actionLabel: "Action",
        allActions: "All Actions",
        action: {
            login: "Login",
            logout: "Logout",
            viewMediations: "View Mediations",
            viewMediationDetail: "View Mediation Detail",
            resolveMediation: "Resolve Mediation",
            reopenMediation: "Reopen Mediation",
            generateMediationReport: "Generate Mediation Report",
            draftMediationReply: "Draft Mediation Reply",
            viewChatFile: "View Conversation File",
            viewChatVideo: "View Conversation Video",
            viewChats: "View Chats",
            viewChatDetail: "View Chat Detail",
            viewUsers: "View Users",
            viewUserDetail: "View User Detail",
            viewUserChats: "View User Chats",
            exportData: "Export Data",
            impersonateUser: "Impersonate User",
            impersonatedAction: "Impersonated Action",
            grantExpertStatus: "Grant Expert Status",
            revokeExpertStatus: "Revoke Expert Status",
            viewPlatformFinance: "View Platform Finance",
            viewFinanceTransactions: "View Finance Transactions",
            passwordResetRequested: "Admin Password Reset Requested",
            passwordResetCompleted: "Admin Password Reset Completed",
            passwordResetSent: "Admin Password Reset Sent",
            adminCreated: "Admin Created",
            adminUpdated: "Admin Updated",
            sendUserPasswordReset: "User Password Reset Sent",
            sendUserVerificationEmail: "User Verification Email Sent",
        },
        adminResource: "Administration team",
        impersonatedActionsPlural: "Impersonated Actions",
        resourceType: "Resource Type",
        allTypes: "All Types",
        chat: "Chat",
        orderMediation: "Order/Mediation",
        startDate: "Start Date",
        endDate: "End Date",
        clearFilters: "Clear Filters",
        loading: "Loading audit log...",
        emptyTitle: "No Audit Entries",
        emptyBody: "No matching audit log entries found.",
        timestamp: "Timestamp",
        admin: "Admin",
        resource: "Resource",
        ipAddress: "IP Address",
        idPrefix: "ID:",
        asUser: "as {email}",
    },
    adminSecurity: {
        passwordChanged: "Password changed successfully",
        passwordChangeFailed: "Failed to change password",
        setupFailed: "Failed to setup 2FA",
        invalidCode: "Invalid verification code",
        disableFailed: "Failed to disable 2FA",
        sessionsInvalidated: "All other sessions have been invalidated",
        invalidateFailed: "Failed to invalidate sessions",
        changePassword: "Change Password",
        currentPassword: "Current Password",
        newPassword: "New Password",
        confirmNewPassword: "Confirm New Password",
        changing: "Changing...",
        twoFaDescription: "Add an extra layer of security to your account using an authenticator app.",
        twoFaEnabled: "2FA Enabled",
        disable2fa: "Disable 2FA",
        saveRecoveryCodes: "Save Your Recovery Codes",
        saveRecoveryCodesBody: "Store these codes in a safe place. You can use each code once if you lose access to your authenticator app.",
        savedCodes: "I've Saved My Codes",
        qrCodeAlt: "2FA QR Code",
        scanQrCode: "Scan this QR code with your authenticator app",
        enterManually: "Or enter this code manually:",
        enterCodeFromApp: "Enter 6-digit code from your app",
        verifyAndEnable: "Verify and Enable",
        settingUp: "Setting up...",
        setup2fa: "Setup 2FA",
        sessionManagement: "Session Management",
        sessionManagementBody: "Invalidate all other active sessions. You will remain logged in on this device.",
        confirmWithPassword: "Confirm with your password",
        invalidating: "Invalidating...",
        invalidateAllSessions: "Invalidate All Other Sessions",
        disableTwoFaTitle: "Disable Two-Factor Authentication",
        disableTwoFaBody: "Enter your password to confirm disabling 2FA.",
        disabling: "Disabling...",
    },
    adminUserDetail: {
        loadFailed: "Failed to load user",
        expertStatusFailed: "Failed to update expert status",
        loading: "Loading user details...",
        notFound: "User not found",
        revokeExpertConfirm: "Revoke expert status?",
        grantExpertConfirm: "Grant expert status?",
        confirm: "Confirm",
        revokeExpert: "Revoke Expert",
        grantExpert: "Grant Expert",
        opening: "Opening...",
        impersonateUser: "Impersonate User",
        name: "Name",
        yes: "Yes",
        no: "No",
        expertStats: "Expert Stats",
        ratingValue: "{rating} ({count} reviews)",
        chatsExpert: "Chats (Expert)",
        ordersExpert: "Orders (Expert)",
        clientStats: "Client Stats",
        chatsClient: "Chats (Client)",
        ordersClient: "Orders (Client)",
        stripeCustomer: "Stripe Customer",
        stripeAndSubscription: "Stripe & Subscription",
        stripeConnected: "Stripe Connected",
        stripeAccount: "Stripe Account",
        periodEnds: "Period Ends",
        asExpert: "As Expert",
        asClient: "As Client",
        noChatsBody: "This user has no chats in the selected role.",
        accessTitle: "Account access",
        signInMethod: "Sign-in method",
        methodEmail: "Email and password",
        methodGoogle: "Google",
        methodPasskey: "Passkey (no password)",
        sendPasswordReset: "Send password reset link",
        sendPasswordResetTitle: "Send a password reset link?",
        sendPasswordResetBody: "An email with a link to choose a new password will be sent to {email}. The link stays valid for one hour, and you will not see it.",
        resendVerification: "Resend verification email",
        resendVerificationTitle: "Resend the verification email?",
        resendVerificationBody: "A new email asking to confirm the address will be sent to {email}.",
        send: "Send",
        passwordResetSent: "Password reset link sent to {email}.",
        verificationSent: "Verification email sent to {email}.",
        passwordResetUnavailableGoogle: "This account signs in with Google: there is no password to reset.",
        passwordResetUnavailablePasskey: "This account has no password: it signs in with a passkey.",
        actionFailed: "The email could not be sent.",
        resendLimited: "A verification email was sent recently. Please wait before sending another.",
        alreadyVerified: "This address is already verified.",
    },
    adminReports: {
        loadFailed: "Failed to load reports",
        updateFailed: "Failed to update report",
        unknownUser: "Unknown User",
        loading: "Loading reports...",
        title: "User Reports",
        total: "Total",
        reviewed: "Reviewed",
        resolved: "Resolved",
        dismissed: "Dismissed",
        emptyTitle: "No Reports",
        emptyAll: "No user reports have been submitted yet.",
        emptyFiltered: "No {status} reports found.",
        reportedBy: "Reported by",
        reportedUser: "Reported user",
        hideDetails: "Hide details",
        showDetails: "Show details",
        noDescription: "No description provided.",
        resolutionLabel: "Resolution:",
        viewChat: "View Chat",
        markReviewed: "Mark Reviewed",
        resolve: "Resolve",
        dismiss: "Dismiss",
    },
    adminAdmins: {
        groupUsersChats: "Users & Chats",
        permViewUsers: "View Users",
        permViewUsersDesc: "Browse user profiles and activity",
        permManageUsers: "Manage Users",
        permManageUsersDesc: "Edit user accounts (expert status, password reset links, verification emails)",
        permViewChats: "View Chats",
        permViewChatsDesc: "Access chat conversations",
        permViewOrders: "View Orders",
        permViewOrdersDesc: "See order and payment details",
        permImpersonateUsers: "Impersonate Users",
        permImpersonateUsersDesc: "Log in as any user (sensitive)",
        groupMediations: "Mediations",
        permViewMediations: "View Mediations",
        permViewMediationsDesc: "See active dispute cases",
        permResolveMediations: "Resolve Mediations",
        permResolveMediationsDesc: "Make decisions on disputes",
        groupAdministration: "Administration",
        permManageAdmins: "Manage Admins",
        permManageAdminsDesc: "Create and edit admin accounts",
        loadFailed: "Failed to load admins",
        createFailed: "Failed to create admin",
        updateFailed: "Failed to update admin",
        never: "Never",
        sensitive: "SENSITIVE",
        title: "Admin Users",
        accountsCount: "{count} admin accounts",
        createAdmin: "Create Admin",
        loading: "Loading admins...",
        emptyTitle: "No Admin Users",
        emptyBody: "Create your first admin user to get started.",
        admin: "Admin",
        permissions: "Permissions",
        lastLogin: "Last Login",
        allPermissions: "All permissions",
        morePermissions: "+{count} more",
        disabled: "Disabled",
        enable: "Enable",
        disable: "Disable",
        cannotChangeOwnRole: "You cannot change your own role or disable your own account. Ask another super admin to do it.",
        ownRoleLocked: "You cannot change your own role.",
        twoFactor: "2FA",
        twoFactorOn: "On",
        twoFactorOff: "Off",
        sendResetLink: "Reset password",
        resetLinkTitle: "Send a password reset link?",
        resetLinkBody: "An email with a link to choose a new password will be sent to {email}. The link stays valid for 30 minutes. Once it is used, every open session of this account is signed out.",
        resetLinkSend: "Send the link",
        resetLinkSent: "Reset link sent to {email}.",
        resetLinkFailed: "The reset link could not be sent.",
        createAdminUser: "Create Admin User",
        superAdmin: "Super Admin",
        creating: "Creating...",
        editAdmin: "Edit Admin: {name}",
        superAdminNote: "Super admins have all permissions by default.",
        saveChanges: "Save Changes",
    },
    modal: {
        reasonOptional: "Reason (optional)",
        closeChat: {
            title: "Close Chat",
            body: "Are you sure you want to close this chat? The conversation will be marked as completed and no further messages can be sent.",
            closing: "Closing...",
        },
        abandon: {
            body: "Are you sure you want to cancel this request? This action cannot be undone.",
            keep: "Keep Request",
        },
        unilateralCancel: {
            title: "Cancel Order",
            body: "Are you sure you want to cancel this order? The payment hold will be released back to your card.",
            keep: "Keep Order",
        },
        decline: {
            title: "Decline Request",
            body: "Are you sure you want to decline this request?",
            declining: "Declining...",
        },
        denyCancellation: {
            title: "Deny Cancellation Request",
            body: "Please explain why you're denying the cancellation:",
            placeholder: "Reason for denial (required)",
            denying: "Denying...",
        },
        rejectDelivery: {
            title: "Reject Delivery",
            body: "Explain why the delivery doesn't meet your expectations:",
            placeholder: "What's wrong with the delivery? (required)",
            rejecting: "Rejecting...",
        },
        cancelOrder: {
            body: "Are you sure you want to decline this order? The payment hold will be released to the client.",
            reasonPrompt: "Please explain why you're declining:",
            placeholder: "Reason for declining (required)",
            declining: "Declining...",
        },
    },
    chatMessage: {
        download: "Download",
        read: "Read",
        delivered: "Delivered",
        // Message envoyé avant la réception de ses médias (MEDIA-5)
        pending: "Sending",
        mediaUploading: "Sending…",
        mediaUploadingProgress: "Sending… {percent}%",
        mediaProcessing: "Processing…",
        mediaFailed: "This file could not be sent",
        mediaRetry: "Retry",
        mediaRemove: "Remove",
        mediaVideo: "Video",
    },
    fileUploader: {
        addFiles: "Add files ({count} remaining)",
        maxReached: "Maximum {max} files reached",
        attachFiles: "Attach files",
    },
    fileViewer: {
        loadFailed: "Failed to load file",
        urlUnavailable: "File URL not available",
        imageFailed: "Failed to load image",
        videoFailed: "Failed to load video",
        videoUnsupported: "Your browser does not support video playback.",
        audioFailed: "Failed to load audio",
        audioUnsupported: "Your browser does not support audio playback.",
        pdfFailed: "Failed to load PDF",
        loading: "Loading file...",
        previewUnavailable: "Preview not available for this file type",
        downloadToView: "Download to View",
        downloadFile: "Download file",
        downloadInstead: "Download Instead",
        enterFullscreen: "Full screen",
        exitFullscreen: "Exit full screen",
    },
    // Lecteur de PDF intégré (MEDIA-PDF-VIEWER-1).
    pdfViewer: {
        loading: "Loading document…",
        loadFailed: "This document could not be displayed.",
        loadFailedHint: "You can still download it to read it on your device.",
        openFailed: "This document is not available right now.",
        open: "Open {name}",
        pageOf: "{page} / {total}",
        previousPage: "Previous page",
        nextPage: "Next page",
        zoomIn: "Zoom in",
        zoomOut: "Zoom out",
        zoomLevel: "Zoom {percent}%",
        fitWidth: "Fit to screen width",
        search: "Search in document",
        searchPlaceholder: "Search…",
        searchPrevious: "Previous result",
        searchNext: "Next result",
        searchClose: "Close search",
        searching: "Searching…",
        searchNoResult: "No result",
        searchResults: "{current} of {count} result{plural}",
    },
    // Domaine média : vidéos asynchrones et sessions vidéo. Aucune de ces chaînes
    // ne nomme le fournisseur — un changement d'implémentation ne doit pas
    // entraîner de retraduction.
    media: {
        addVideo: "Add a video",
        recordNow: "Record now",
        chooseVideo: "Choose a video",
        preparing: "Preparing…",
        uploading: "Sending… {percent}%",
        processing: "Processing video…",
        processingFailed: "This video could not be processed",
        ready: "Ready to send",
        play: "Play video",
        videoTitle: "Video",
        unavailable: "Video unavailable",
        download: "Download",
        preparingDownload: "Preparing…",
        enableDownload: "Allow download",
        disableDownload: "Disable download",
        sentByExpert: "Sent by the expert",
        sentByClient: "Sent by the client",
        errorTooLarge: "This video is too large",
        errorTooLong: "This video is too long",
        errorUploadFailed: "The video could not be sent",
        errorProviderUnavailable: "The video service is unavailable. Please try again shortly.",
        errorProcessingFailed: "The video could not be processed",
        errorDownloadFailed: "The download could not be started",
        pickerTitle: "Add a video from the conversation",
        pickerEmpty: "You have not sent any video in this conversation yet.",
        // Enregistrement à la webcam, sur les postes de bureau où l'attribut
        // `capture` d'un champ fichier n'ouvre aucune caméra.
        recorderTitle: "Record a video",
        recorderRequesting: "Waiting for camera access…",
        recorderStart: "Start recording",
        recorderStop: "Stop",
        recorderRetake: "Record again",
        recorderUse: "Use this video",
        recorderCamera: "Camera",
        recorderMicrophone: "Microphone",
        recorderNoDeviceOption: "No device found",
        captureLimitsUnavailable: "Recording limits could not be loaded. Please reload before recording. Your existing files are kept.",
        captureLimitSaved: "The recording limit was reached. Your recording has been saved and is ready to send.",
        nativeLimitTitle: "Before recording",
        nativeLimitNotice: "The recording limit is {seconds} second{plural}. The native camera cannot stop automatically. Your file will be kept if it exceeds the limit; further processing will be blocked until your expert’s subscription renews.",
        recoverRecording: "Save my recording",
        recorderMaxDuration: "Recording stops automatically after {minutes} minutes.",
        recorderUnsupported: "This browser cannot record video. Choose a video file instead.",
        recorderPermissionDenied: "Camera and microphone access was denied. Allow it in your browser settings, or choose a video file instead.",
        recorderNoDevice: "No camera was found on this device.",
        recorderFailed: "The recording could not be completed. Please try again.",
        pickerConfirm: "Add to delivery",
        pickerConfirmPreview: "Add to preview",
        attachedVideos: "{count} video{plural}",
        addVideoToDelivery: "Add a video from the conversation",
        addVideoToPreview: "Add a video from the conversation",
        videoSession: "Video session",
        startSession: "Start the session",
        startSessionHint: "Start a live video session with your client.",
        joinSession: "Join the session",
        endSession: "End the session",
        connecting: "Connecting…",
        connectFailed: "Could not join the session.",
        retryConnect: "Try again",
        noActiveSession: "No session is open right now.",
        backToChat: "Back to the conversation",
        timeRemaining: "Time remaining",
        sessionLive: "Session in progress",
        roleExpert: "Expert",
        roleClient: "Client",
        connectionConnected: "Connected",
        connectionReconnecting: "Reconnecting…",
        connectionWeak: "Weak connection",
        tapToStartVideo: "Tap to start the video",
        pipSwapFocus: "Show full screen",
        muteAudio: "Mute microphone",
        unmuteAudio: "Unmute microphone",
        disableCamera: "Turn off camera",
        enableCamera: "Turn on camera",
        switchCamera: "Switch camera",
        cycleLayout: "Change layout",
        hangUp: "Hang up",
        // Enregistrement d'écran (MEDIA-2). Le choix écran/fenêtre/onglet est
        // laissé au navigateur ; rien ne promet le son du système.
        screenRecordNow: "Record my screen",
        screenUnavailable: "Screen recording is not available on this device or browser.",
        screenTitle: "Record my screen",
        screenIntro: "Your browser will ask which screen, window or tab to share. Recording starts once you have chosen.",
        screenMicrophoneOption: "Record my voice with the microphone",
        screenMicrophoneOn: "Microphone on",
        screenMicrophoneOff: "Microphone off",
        screenMuteMicrophone: "Mute microphone",
        screenUnmuteMicrophone: "Unmute microphone",
        screenMicrophoneUnavailable: "The microphone could not be used; the screen is recorded without your voice.",
        screenNoSystemAudio: "Sounds played by your computer are not captured.",
        screenChoose: "Choose what to share",
        screenRequesting: "Waiting for your choice…",
        screenPause: "Pause",
        screenResume: "Resume",
        screenPaused: "Paused",
        screenFinish: "Finish",
        screenDiscard: "Delete",
        screenUse: "Add to the conversation",
        screenRetake: "Record again",
        screenWithVoice: "With your voice",
        screenWithoutVoice: "Without sound",
        screenBadge: "Screen recording",
        screenCancelled: "No screen was shared. Choose what to share to start recording.",
        screenUnsupported: "This browser cannot record the screen. You can still add a video file.",
        screenFailed: "The screen recording could not be completed. Please try again.",
        screenTooShort: "The recording is too short to be sent.",
        screenMaxDuration: "Recording stops automatically after {minutes} minutes.",
        // Capture réduite : la modale se replie, un badge REC dans le composeur
        // rouvre les commandes ; quitter la conversation demande confirmation.
        screenBackgroundHint: "Once recording starts, this window closes and a REC badge in the message box lets you pause, mute or finish.",
        screenBadgeLabel: "Screen recording in progress, {time} elapsed. Open the recording controls.",
        screenBadgeOpen: "Recording controls",
        screenLeaveTitle: "A screen recording is in progress",
        screenLeaveBody: "Leaving this conversation stops the recording and discards it.",
        screenLeaveConfirm: "Stop and leave",
        screenLeaveCancel: "Keep recording",
        leaveConfirmTitle: "Leave the session?",
        leaveConfirmBody: "You can rejoin later if the session is still open.",
        leaveConfirmAction: "Leave",
    },
    // Messages vocaux (MEDIA-1) : enregistrement dans le navigateur, lecteur
    // accessible, erreurs actionnables. Aucune chaîne ne nomme un fournisseur.
    voice: {
        record: "Record a voice message",
        voiceMessage: "Voice message",
        recording: "Recording",
        elapsed: "Recording, {time} elapsed",
        stop: "Stop recording",
        discard: "Delete recording",
        send: "Send voice message",
        addToDelivery: "Add a voice message",
        addToList: "Add this voice message",
        preview: "Voice message preview",
        play: "Play",
        pause: "Pause",
        seek: "Position in the recording",
        position: "{current} of {total}",
        loading: "Loading voice message…",
        loadFailed: "This voice message could not be played.",
        downloadInstead: "Download the audio file",
        level: "Microphone level",
        maxDuration: "Recording stops automatically after {minutes} minutes.",
        sending: "Sending…",
        retry: "Try again",
        unsupported: "This browser cannot record voice messages. You can still attach an audio file.",
        permissionDenied: "Microphone access was denied. Allow it in your browser settings to record a voice message.",
        noDevice: "No microphone was found on this device.",
        interrupted: "The recording was interrupted before anything was captured.",
        failed: "The recording could not be completed. Please try again.",
        tooShort: "The recording is too short to be sent.",
        errorTooLarge: "This voice message is too large.",
        errorTooLong: "This voice message is too long.",
        errorLimitReached: "The voice message limit has been reached.",
        errorUnsupportedType: "This audio format is not supported.",
        errorInvalid: "This voice message could not be processed. Please record it again.",
        errorUploadFailed: "The voice message could not be sent. Check your connection and try again.",
        errorSendFailed: "The voice message was uploaded but the message could not be sent. Try again.",
        attachedCount: "{count} voice message{plural}",
        deliveredVoiceMessages: "Voice messages",
        previewVoiceMessages: "Preview voice messages",
        remove: "Remove",
    },
    chatInput: {
        uploadFailed: "Upload failed",
        guidanceRevision: "Please use the \"Redeliver\" button in the revision request above to submit your updated work.",
        guidanceNormal: "Use the \"Deliver Service\" button above to submit your work. Free chat is for questions and updates only.",
        placeholder: "Type a message...",
        dropToAttach: "Drop files to attach",
    },
    deliveryComposer: {
        unsupportedType: "\"{name}\" has an unsupported file type",
        exceedsLimit: "\"{name}\" exceeds {max}MB limit",
        maxFilesDelivery: "Maximum {max} files allowed per delivery",
        maxFilesPreview: "Maximum {max} preview files allowed",
        createFailed: "Failed to create delivery",
        title: "Deliver Service",
        subtitle: "Attach files and/or write a message for your client",
        previewTab: "Preview",
        fullDeliveryTab: "Full Delivery",
        required: "Required",
        previewExplanation: "The client will only see this preview before accepting or rejecting the delivery. Use it to show samples of your work to reassure the client while protecting your full delivery.",
        dragDropPreview: "Drag & drop preview files here or click to browse",
        maxFilesHint: "Max {max} files, {sizeMb}MB each",
        previewFilesCount: "{count} preview file{plural}",
        slotsRemaining: "{count} slot{plural} remaining",
        previewMessageLabel: "Preview Message (optional)",
        previewMessagePlaceholder: "Add a preview message for the client...",
        deliveryExplanation: "This is the full delivery that will be revealed to the client after they accept your work. It remains hidden until acceptance.",
        dragDropDelivery: "Drag & drop files here or click to browse",
        supportedFormats: "Supported: {formats}",
        filesSelected: "{count} file{plural} selected",
        messageLabel: "Message (optional)",
        messagePlaceholder: "Add a note for your client...",
        mustAddContent: "You must add content to the Full Delivery tab before sending",
        addContentHint: "Add files or a message to the Full Delivery tab",
        sending: "Sending...",
        sendDelivery: "Send Delivery",
        complementTitle: "Additional delivery",
        complementSubtitle: "Share something more now that your delivery was accepted",
        complementExplanation: "The client opens this directly, with nothing to approve. It does not change the order, the payment or any date, and the delivery that was accepted stays as it is.",
        sendComplement: "Share this delivery",
        complementEmailNotice: "The message you write here is also included in the email sent to your client.",
        noPreviewTitle: "No Preview Provided",
        noPreviewBody: "You haven't added a preview. Providing a preview (watermarked samples, partial work, etc.) helps reassure clients and increases acceptance rates. Are you sure you want to send without a preview?",
        sendAnyway: "Send Anyway",
        addPreview: "Add Preview",
        // Montant final annoncé avec la livraison (BL-F1). Le plein tarif est
        // prérempli : le cas nominal ne demande aucune saisie.
        amountTitle: "Amount to charge",
        amountReserved: "Reserved by the client",
        amountCharged: "Charged when the client accepts",
        amountReduce: "Charge less than the reserved amount",
        amountKeepFull: "Charge the full reserved amount",
        amountLabel: "Final amount",
        amountFullNote: "The client will be charged the full amount they reserved.",
        amountReleased: "{amount} will be released back to the client and never leaves their account.",
        amountInvalid: "Enter an amount between {min} and {max}.",
    },
    // Réputation multifacette (WP B) : facettes communes définies par ExpertLoop
    reputation: {
        facet: {
            result_quality: "Quality of the result",
            need_understanding: "Understanding of my need",
            relationship_quality: "Quality of the relationship",
            adaptability: "Adaptability",
        },
        facetsTitle: "What clients rated",
        facetRatings: "{count} rating{plural}",
        measuredTitle: "Measured by ExpertLoop",
        measuredHint: "These are facts counted by the platform, not opinions.",
        deliveryOnTime: "Delivered within the announced time",
        responseOnTime: "Replied within the announced time",
        missions: "{onTime} of {total} mission{plural}",
    },
    // Désinscription des invitations à évaluer (WP C) : sans compte, par le lien du courriel
    unsubscribeRatingInvitations: {
        title: "Stop receiving invitations to rate",
        body: "You will no longer receive emails inviting you to rate a mission. Other emails about your missions are not affected.",
        confirm: "Stop receiving these invitations",
        working: "Saving...",
        doneTitle: "Done",
        done: "You will not receive invitations to rate any more.",
        invalidTitle: "This link is not valid",
        invalid: "The link may be incomplete or damaged. Open it again from the email, or ignore the invitation: nothing is sent without your action.",
        error: "We could not save your choice. Please try again in a moment.",
        home: "Back to ExpertLoop",
    },
    // Appréciation d'une livraison (WP A) : facultative, privée, jamais publique
    deliveryAppreciation: {
        title: "How was this delivery?",
        hint: "Optional. It never affects your decision to accept. Only you and your expert can see it.",
        veryHappy: "Very satisfied",
        partlyHappy: "Partly satisfied",
        disappointed: "Disappointed",
        commentLabel: "Add a comment (optional)",
        commentPlaceholder: "Tell your expert what worked or what was missing",
        send: "Send my feedback",
        sending: "Sending...",
        yourFeedback: "Your feedback on this delivery",
        clientFeedback: "Client feedback on this delivery",
        privateNote: "Private: it is not shown on the expert's public profile.",
    },
    // Livraison complémentaire (WP A) : consultée et confirmée seulement
    complementEntry: {
        title: "Additional delivery",
        clientIntro: "Your expert shared an additional delivery. It does not change your order or your payment, and there is nothing to approve.",
        expertIntro: "You shared an additional delivery. It does not change the order or the payment.",
        open: "Open the additional delivery",
        opening: "Opening...",
        stateDelivered: "Delivered",
        stateSeen: "Seen",
        stateAcknowledged: "Receipt confirmed",
        acknowledge: "Confirm receipt",
        acknowledging: "Confirming...",
        acknowledgeNote: "Confirming receipt is a courtesy. It does not approve the quality of the work.",
        acknowledged: "You confirmed receipt.",
        ratingPrompt: "If your opinion of the whole mission has changed, you can update your overall rating. It is entirely up to you.",
        updateRating: "Update my rating",
    },
    deliveryEntry: {
        hoursMinutesRemaining: "{hours}h {minutes}m remaining",
        minutesSecondsRemaining: "{minutes}m {seconds}s remaining",
        redelivered: "Redelivered",
        serviceDelivered: "Service Delivered",
        underMediation: "Delivery is under mediation review.",
        // Le support a tranché : la livraison n'est plus à l'étude, et le dire
        // évite que le client attende encore une décision déjà rendue.
        mediationSettled: "The support team has settled this case.",
        clientNotViewed: "The expert has delivered your service. The preview is loading...",
        clientPendingDecision: "Please review the delivery and accept or reject it.",
        expertWaitingView: "Waiting for client to open the delivery...",
        legacyClientBody: "The expert marked this service as delivered. Please review and confirm.",
        legacyExpertBody: "Waiting for client to confirm delivery...",
        expertDelivered: "You delivered the service.",
        clientDelivered: "The expert delivered the service.",
        fileCount: "{count} file{plural}",
        autoAcceptIn: "Auto-accept in:",
        opening: "Opening...",
        viewDelivery: "View Delivery",
        previewFiles: "Preview Files",
        previewVideos: "Preview Videos",
        deliveredVideos: "Delivered Videos",
        sample: "Sample",
        previewMessage: "Preview Message",
        previewNotice: "This is a preview of the delivery. Accept to access the full content.",
        noPreviewProvided: "The expert provided no preview for this delivery.",
        deliveredFiles: "Delivered Files",
        messageFromExpert: "Message from Expert",
        expertPreviewHeader: "Preview (what client sees before accepting)",
        accepting: "Accepting...",
        acceptDelivery: "Accept Delivery",
        requestMediation: "Request Mediation",
        confirming: "Confirming...",
        confirmDelivery: "Confirm Delivery",
        // Montant final annoncé par l'expert avec sa livraison (BL-F1). Le client
        // doit voir ce qui sera débité avant d'accepter, pas seulement ce qui a
        // été réservé.
        amountReserved: "Reserved",
        amountCharged: "You will be charged",
        // Mission gratuite (REFERRAL-4)
        freeNoCharge: "Free mission: nothing will be charged.",
        amountReleased: "{amount} will be released back to you.",
    },
    fileUploadConfirm: {
        typeImage: "Image",
        typeVideo: "Video",
        typeAudio: "Audio",
        typePdf: "PDF Document",
        typeWord: "Word Document",
        typeSpreadsheet: "Spreadsheet",
        typeText: "Text File",
        typeFile: "File",
        uploadFile: "Upload File",
        uploadFiles: "Upload {count} Files",
        warning: "Please review before sending. This action cannot be undone.",
        name: "Name",
        type: "Type",
        size: "Size",
        filesCount: "{count} files",
        total: "Total: {size}",
    },
    reportModal: {
        title: "Report User",
        reasonLabel: "Reason for report:",
        harassment: "Harassment",
        spam: "Spam",
        inappropriate: "Inappropriate Content",
        fraud: "Fraud",
        unprofessional: "Unprofessional Behavior",
        other: "Other",
        descriptionLabel: "Description:",
        descriptionPlaceholder: "Please describe the issue in detail...",
        submitting: "Submitting...",
        submit: "Submit Report",
        submitFailed: "Failed to submit report",
    },
    unprofessionalModal: {
        title: "Tell us more",
        body: "What was unprofessional about this decline? (optional)",
        placeholder: "e.g., Rude response, no explanation given...",
        submitting: "Submitting...",
        submit: "Submit Feedback",
    },
    capturePaymentModal: {
        heldAmount: "Held amount:",
        announcedAmount: "Amount announced with the delivery:",
        releasedNote: "{amount} will be released back to the client.",
        capturing: "Capturing...",
        captureFullAmount: "Capture Full Amount",
        orPartial: "Or capture a partial amount:",
        amountPlaceholder: "Amount (max {max})",
        capturePartialAmount: "Capture Partial Amount",
    },
    mediationModal: {
        body: "Requesting mediation will involve ExpertLoop support to review this case. All delivery actions will be locked until support resolves the dispute.",
        explainPrompt: "Explain why you need support to intervene:",
        placeholder: "Describe the issue and why you need mediation (required)",
        requesting: "Requesting...",
    },
    offerRetryModal: {
        body: "The payment hold expired. You can offer the client new terms — they will be asked to authorize the payment again.",
        newDeliveryTime: "New delivery time (hours)",
        hoursHint: "Must be shorter than the original. Max {max} hours (7 days).",
        differentAmount: "Offer a different amount",
        originalAmount: "Original: {amount}",
        sameAmount: "Same amount as before:",
        sending: "Sending Offer...",
        send: "Send Offer",
    },
    // ─── Admin Error Boundary ─────────────────────────────────────────────
    adminError: {
        title: "Something went wrong",
        body: "An unexpected error occurred in the admin panel.",
        details: "Error details",
        reload: "Reload Page",
        goHome: "Go to Dashboard",
    },
    // ─── Admin Chat Detail ────────────────────────────────────────────────
    adminChatDetail: {
        loadFailed: "Failed to load chat",
        backToChats: "← Back to Chats",
        notFound: "Chat not found",
        title: "Chat Details",
        inMediation: "In Mediation",
        mediationRequestedBy: "Mediation requested by",
        chatInfo: "Chat Info",
        id: "ID",
        status: "Status",
        created: "Created",
        accepted: "Accepted",
        completed: "Completed",
        expert: "Expert",
        name: "Name",
        email: "Email",
        rating: "Rating",
        noRatings: "No ratings",
        opening: "Opening...",
        viewAsExpert: "View as Expert",
        client: "Client",
        notProvided: "Not provided",
        account: "Account",
        guest: "Guest",
        viewAsGuest: "View as Guest",
        memberSince: "Member Since",
        viewAsClient: "View as Client",
        product: "Product",
        price: "Price",
        maxDelivery: "Max Delivery",
        hours: "{count} hours",
        orders: "Orders ({count})",
        amount: "Amount",
        authorized: "Authorized",
        captured: "Captured",
        noRating: "No rating",
        deliveryHistory: "Delivery History",
        action: {
            delivered: "delivered",
            redelivered: "redelivered",
            rejected: "rejected",
            confirmed: "confirmed",
            mediation_requested: "mediation requested",
            mediation_resolved: "mediation resolved",
            mediation_reopened: "mediation reopened",
            viewed: "viewed",
            auto_accepted: "auto accepted",
            complement_delivered: "additional delivery shared",
            complement_acknowledged: "additional delivery receipt confirmed (courtesy, not an acceptance)",
            chat_reopened: "conversation reopened",
        },
        translation: {
            title: "Reading language",
            viewReference: "Reference ({language})",
            viewOriginal: "Original",
            viewDossier: "Case language ({language})",
            viewMine: "My language ({language})",
            indicative: "Machine translations are indicative. The original is the reference.",
            translatedFrom: "Translated from {language}",
            alreadyIn: "Already in {language}",
            notTranslated: "Not translated yet",
            unknownLanguage: "unknown language",
            previewButton: "Preview in the parties' languages",
            previewing: "Translating...",
            previewTitle: "What each party will read",
            previewExpert: "Expert ({language})",
            previewClient: "Client ({language})",
            previewUnavailable: "Translation unavailable",
            previewNotNeeded: "Translation not needed: this party already reads this language.",
            previewFailed: "The preview could not be produced.",
        },
        // Pièces de la conversation consultables par le support, sans se connecter en tant qu'une partie
        media: {
            attachments: "Attachments",
            voice: "Voice message {duration}",
            video: "Video",
            screenRecording: "Screen recording",
            previewFile: "Preview",
            openFailed: "This file could not be opened.",
            videoUnavailable: "This video cannot be played right now.",
            videoTitle: "Video",
            close: "Close",
            fileOpening: "Opening…",
        },
        collapse: {
            show: "Expand",
            hide: "Collapse",
        },
        splitter: "Resize the conversation column",
        // Conversation reconstituée, telle que la voient les deux parties, événements compris
        conversation: {
            title: "Conversation as the parties see it",
            subtitle: "Messages, deliveries and events in one thread, in the reading language.",
            request: "Initial request",
            empty: "Nothing to show yet.",
            delivery: "Delivery",
            deliveryAmount: "Amount announced: {amount}",
            rejectionReason: "Reason given by the client",
            mediationReason: "Reason given for the mediation",
            decisionMessage: "Message sent to the parties",
            selected: "Selected from the report",
        },
        // Panneau de décision de la médiation (MEDIATIONUX4)
        decision: {
            title: "Decision",
            humanValidation: "Human validation",
            intro: "Choose an outcome, check the amount available and read the message sent to the parties before confirming.",
            grantRefund: "Grant a refund",
            grantRefundSettled: "Return money to the client",
            grantRefundUnsettled: "Lift the authorisation",
            close: "Close without further refund",
            closeSettled: "The expert keeps what remains",
            closeUnsettled: "Capture the payment",
            simulation: {
                title: "What each outcome would do",
                ifRefund: "If you grant a refund",
                ifClose: "If you close without further refund",
                clientReceives: "Client receives",
                clientReleased: "Authorisation lifted for",
                expertKeeps: "Expert keeps",
                expertReceives: "Expert receives",
                invalidAmount: "Enter an amount between 0 and what remains",
                closeNeedsZero: "To close without a refund, enter 0 in the amount field.",
                feesNote: "Amounts before platform fees.",
            },
        },
        // Dossier chronologique (MEDIATIONUX1) et rapport d'instruction IA (MEDIATIONUX2)
        dossier: {
            moreInfo: "Conversation, parties and orders",
            summaryTitle: "Situation at a glance",
            attachments: "Attachments",
            initialPrice: "Initial price",
            agreedPrice: "Price after concessions",
            exchanges: "Exchanges",
            exchangesValue: "{count} message{plural}",
            price: {
                title: "Price history",
                stepAuthorized: "Amount authorised by the client",
                stepDelivery: "Amount proposed with a delivery",
                stepRefund: "Refund granted by the expert, a commercial gesture",
                netPrice: "Net price after the expert's gestures",
                change: "{amount} vs previous",
                withdrawn: "Withdrawn",
                accepted: "Accepted",
                rejected: "Rejected",
                implicit: "No amount stated: full authorised amount",
                reductionTitle: "Total commercial reduction",
                reductionDetail: "{amount} off the initial price",
                reductionBreakdown: "{discount} discount on the price + {refund} refund granted by the expert",
                flowsTitle: "Money actually moved",
                authorized: "Authorised",
                captured: "Captured",
                released: "Released, never charged",
                refunded: "Refunded",
                refundable: "Still refundable",
                keptByExpert: "Kept by the expert, before fees",
                notCaptured: "Nothing captured yet",
                notRefund: "A commercial reduction is not a refund: the part that was never captured is released by the bank and never leaves the client's account.",
                refundNote: "The order only records the total refunded and the date of the last refund, not each refund.",
            },
            timeline: {
                title: "Timeline",
                all: "All",
                messages: "Messages",
                deliveries: "Deliveries",
                finance: "Finance",
                empty: "No events.",
                sectionBefore: "Before the first delivery",
                sectionDelivery: "Delivery {number} and what followed",
                sectionMediation: "Mediation, round {number}",
                events: "{count} event{plural}",
                open: "Open",
                files: "{count} file{plural}",
                round: "Round {number}",
                actor: {
                    expert: "Expert",
                    client: "Client",
                    system: "System",
                    admin: "Support",
                },
                type: {
                    message: "Message",
                    delivery_published: "Delivery published",
                    delivery_withdrawn: "Delivery withdrawn by the expert",
                    delivery_viewed: "Delivery viewed by the client",
                    delivery_accepted: "Delivery accepted",
                    delivery_rejected: "Delivery rejected",
                    delivery_auto_accepted: "Delivery accepted automatically",
                    delivery_complement_published: "Additional delivery published",
                    delivery_complement_acknowledged: "Additional delivery acknowledged by the client (courtesy, not an acceptance)",
                    delivery_appreciated: "Client appreciation of a delivery",
                    payment_authorized: "Payment authorised",
                    payment_captured: "Payment captured",
                    cancellation_requested: "Cancellation requested",
                    cancellation_approved: "Cancellation approved",
                    cancellation_denied: "Cancellation denied",
                    refund_requested: "Refund requested",
                    refund_executed: "Refund executed",
                    mediation_opened: "Mediation opened",
                    mediation_decided: "Mediation decided",
                    mediation_reopened: "Mediation reopened",
                },
            },
        },
        report: {
            title: "AI instruction report",
            subtitle: "Prepared for the support team when the mediation opened. A human decides: the report never refunds or closes anything.",
            language: "Report language",
            generate: "Generate in {language}",
            regenerate: "Regenerate",
            generating: "Preparing the report…",
            failed: "The report could not be prepared (reason: {code}). It is retried automatically; you can also try again now.",
            stale: "Messages, deliveries or payments have changed since this analysis: the report no longer reflects the whole file.",
            none: "No report exists in this language yet.",
            preparedOn: "Prepared on {date}",
            section: {
                summary: "Summary",
                commitment: "What was promised",
                delivery: "What was delivered",
                expertBehavior: "Expert's conduct",
                clientBehavior: "Client's conduct",
                climate: "Atmosphere of the exchanges",
                finances: "Finances",
                disagreements: "Points of disagreement",
            },
            kind: {
                fact: "Verified fact",
                interpretation: "AI interpretation",
            },
            sources: "Sources",
            source: {
                title: "Message in context",
                close: "Close",
                seeInConversation: "See in the full conversation",
                notFound: "This message is no longer available.",
                marked: "Message cited by the report",
            },
            ref: {
                order: "Order",
                request: "Initial request",
                message: "Message",
                delivery: "Delivery",
                event: "Event",
                unknown: "Source",
            },
            concessions: {
                title: "Concessions found in the messages",
                none: "None found beyond the amounts on the order and deliveries.",
                amount: "Amount stated: {amount}",
            },
            recommendation: {
                title: "Recommendation, to be validated by a human",
                outcome: {
                    refund_additional: "Grant an additional refund",
                    close_without_refund: "Close without further refund",
                    needs_more_information: "More information needed",
                },
                suggestedAmount: "Suggested refund: {amount}",
                alternatives: "Alternatives",
            },
            proposedReply: {
                title: "Opinion for the arbitration",
                note: "An internal note for the support member, to weigh the case. It is never sent to the parties: the message to the parties is drafted from the field below.",
            },
            missing: {
                title: "What the report could not establish",
                unreadFiles: "Files the AI cannot read: {files}",
                consultTitle: "To be consulted by the support member",
                consultBody: "The AI cannot read the contents of these files yet, so it does not judge them. Open them to judge the work, in parallel with this report.",
            },
        },
        mediationCase: {
            title: "Mediation case",
            round: "Round {number}",
            openedBy: {
                expert: "Opened by the expert",
                client: "Opened by the client",
                support: "Reopened by support",
            },
            reopenedBy: "Reopened by {name}",
            reason: "Reason: {reason}",
            noDecisionYet: "No decision yet",
            decision: {
                favor_client: "Decided in favor of the client",
                favor_expert: "Decided in favor of the expert",
            },
            decidedAt: "on {date}",
            lastRead: "You last read this case on {date}.",
            firstRead: "First time you read this case.",
            newBadge: "New",
            newSince: "{count} new since your last visit",
            reopen: "Reopen the case",
            reopenTitle: "Reopen this mediation",
            reopenBody: "The case goes back to open and you can decide again. Payments and deliveries stay locked. Both parties are notified; the reason below is not shown to them.",
            reopenReasonLabel: "Reason for reopening",
            reopenReasonRequired: "A reason is required to reopen the case.",
            reopenConfirm: "Reopen the case",
            reopening: "Reopening...",
            reopenFailed: "The case could not be reopened.",
            blocked: {
                not_in_mediation: "This order is not in mediation.",
                not_resolved: "This case is still open.",
                order_not_captured: "Only an order that has been charged can be reopened.",
                nothing_left: "Nothing is left to refund on this order.",
                already_reopened: "This case has just been reopened.",
                order_not_found: "Order not found.",
            },
        },
        by: "by {name}",
        deliveriesCount: "Deliveries ({count})",
        deliveryOn: "Delivery on {date}",
        accepted2: "Accepted",
        rejected: "Rejected",
        pending: "Pending",
        rejectionReason: "Rejection reason: {reason}",
        supportDecisionMessage: "Support Decision Message",
        draftReply: {
            button: "Draft a reply to the parties",
            generating: "Drafting…",
            needsReport: "Available once the AI report is ready.",
            failed: "The draft could not be written. Try again in a moment.",
            noReport: "The report is not ready in this language yet.",
            confirmTitle: "The message field already has text",
            confirmBody: "You can have the AI take your text into account (a resolution, a wording, remarks) to write a complete, courteous reply, or replace it with a new draft written from the report alone.",
            confirmAdapt: "Adapt using my text",
            confirmReplace: "Replace with a new draft",
            confirmCancel: "Keep my text",
        },
        decisionPlaceholder: "Explain the reasoning for this decision. This message will be visible to both parties...",
        favorClientTitle: "Refund client and cancel the order",
        favorClient: "Grant a refund",
        refund: "Refund",
        favorExpertTitle: "Capture payment for expert",
        favorExpert: "Close without further refund",
        capturePayment: "Capture Payment",
        confirmResolution: "Confirm Resolution",
        favorClientFullRefund: "Grant a refund - full",
        favorClientPartialRefund: "Grant a refund - partial",
        favorClientBody: "The payment will be voided and the client will receive their full refund.",
        // Une vente déjà encaissée ne s'annule pas : elle se rembourse, en tout ou
        // en partie. Une phrase unique promettait un remboursement complet là où
        // l'expert conserve une part de la somme.
        favorClientSettledFullBody: "The client is refunded the whole amount still held, and the expert keeps nothing.",
        favorClientSettledPartialBody: "The client is refunded the amount you chose, and the expert keeps the rest.",
        refundAmountLabel: "Amount returned to the client",
        refundAmountHint: "Leave empty to refund the full refundable amount ({remaining}), or enter 0 to refund nothing.",
        clientReceives: "Client receives:",
        expertReceives: "Expert receives:",
        clientCanReview: "The client will be able to rate the expert after this decision.",
        favorExpertCapture: "Close the case - capture the payment",
        favorExpertBody: "The held funds will be captured from the client.",
        amountCaptured: "Amount captured:",
        favorExpertKeep: "Close the case - nothing returned",
        favorExpertKeepBody: "The sale has already been paid out. Closing the case returns nothing to the client.",
        amountKept: "Amount kept by the expert:",
        /*
         * Résultat d'une décision de médiation (BL-G4). La confirmation n'affichait
         * qu'une ligne, alimentée par le montant autorisé de la commande ; trois
         * lignes distinctes sont nécessaires pour ne pas confondre ce qui est rendu,
         * ce qui reste acquis et ce qui n'a jamais été encaissé.
         */
        amountReleased: "Never charged, released:",
        resolutionNothingMoved: "No money was moved by this decision.",
        resolutionReleasedOnly: "The authorization was released; nothing had been charged.",
        clientCannotReview: "The client will not be able to rate the expert after this decision.",
        messagePreview: "Your message to both parties:",
        processing: "Processing...",
        mediationResolved: "Mediation Resolved",
        clientRefunded: "The client has been refunded.",
        fundsCaptured: "The held funds have been captured.",
        refunded: "Refunded:",
        captured2: "Captured:",
        backToPendingMediations: "Back to pending mediations",
        supportMessageRequired: "Support decision message is required",
        resolveFailed: "Failed to resolve mediation",
        impersonateExpertFailed: "Failed to impersonate user",
        impersonateGuestFailed: "Failed to impersonate guest",
        loading: "Loading chat details...",
    },
    // ─── Admin ──────────────────────────────────────────────────────────────
    admin: {
        login: {
            title: "Admin Login",
            subtitle: "ExpertLoop Administration Panel",
            emailPlaceholder: "admin@expertloop.com",
            submit: "Sign In",
            submitting: "Signing in...",
        },
    },
    // ─── Sélecteur de langue / traduction dynamique ─────────────────────────
    language: {
        selectorAria: "Select language",
        current: "Current language",
        availableOnRequest: "Available on request",
        translationInProgress: "Translation in progress…",
        translationFailed: "Translation unavailable",
        translationRetry: "Failed — retry",
        bannerDetected: "Your browser language is {language}. ExpertLoop is currently displayed in English. Would you like to make the interface available in {language}?",
        bannerTranslate: "Translate to {language}",
        bannerContinue: "Continue in English",
        requestPending: "{language} translation requested.",
        nowAvailable: "{language} is now available.",
        requestFailed: "The {language} translation could not be generated. You can continue using ExpertLoop in English.",
    },
    // ─── Installation PWA ────────────────────────────────────────────────────
    pwa: {
        // Invite CLIENT contextuelle pré-visio (spec identité §5) — clés séparées
        // des invites expert : deux logiques indépendantes, jamais partagées.
        clientVideoTitle: "Better video in the app",
        clientVideoBenefit: "Install ExpertLoop on your home screen for a full-screen video session, without the browser bars.",
        clientVideoInstall: "Install",
        clientVideoContinue: "Continue in the browser",
        // Modale légère (accueil, après un premier signe d'engagement)
        installTitle: "Add ExpertLoop to your home screen",
        installBenefit: "Full-screen video sessions, and you stay signed in.",
        installCta: "Add to home screen",
        installLater: "Later",
        // Une fois les instructions affichées : il n'y a plus rien à déclencher
        installGotIt: "Got it",
        // Écran plein (iOS, juste après une authentification réussie)
        optInTitle: "One more step for the best experience",
        optInBenefit1: "You'll stay signed in, even after installing the app.",
        optInBenefit2: "Video sessions open full-screen, with no browser bar.",
        optInCodeLabel: "Your pairing code: {code}",
        optInCodeHint: "You'll be asked for this code the first time you open the installed app.",
        optInContinue: "Continue without installing",
        // Bandeau persistant (Android)
        androidBannerText: "Install ExpertLoop for the best experience.",
        androidBannerInstall: "Install",
        androidBannerDismiss: "Dismiss",
        // Instructions d'installation par plateforme/navigateur
        instructionsIosSafari: "Tap the Share button, then \"Add to Home Screen\".",
        instructionsIosOther: "Tap the Share button (top right), then \"Add to Home Screen\".",
        instructionsIosBraveUnsupported: "Brave on iOS can't install apps. Open this link in Safari instead.",
        instructionsAndroidOther: "Open the ⋮ menu, then \"Add to Home Screen\".",
        // Samsung Internet fabrique lui-même l'application installée ; Android la
        // bloque parfois comme conçue pour une ancienne version. Chrome n'a pas ce défaut.
        instructionsAndroidSamsung: "Open the ≡ menu, then \"Add page to\" and \"Home screen\". If Android blocks the app as unsafe, open this page in Chrome and install it from there.",
        // Écran d'appairage (/launch, premier lancement de l'app installée)
        launchTitle: "Welcome back",
        launchSubtitle: "Enter the pairing code shown in your browser tab to sign back in.",
        launchCodeLabel: "Pairing code",
        launchCodePlaceholder: "123456",
        launchSubmit: "Continue",
        launchSubmitting: "Verifying…",
        launchError: "This code isn't valid anymore. You can sign in below instead.",
        launchGoToLogin: "Sign in",
        // Sortie permanente : sans code sous la main (session perdue, onglet
        // fermé), la connexion normale reste la seule issue de l'app installée.
        launchNoCode: "No pairing code?",
        // Navigateur intégré (Instagram, TikTok…)
        inAppBrowserWarning: "Open this page in your browser for the best experience.",
        inAppBrowserCopyLink: "Copy link",
        inAppBrowserCopied: "Link copied",
        inAppBrowserOpenInstructions: "Menu ⋮ → \"Open in browser\"",
        inAppBrowserVideoBlockedTitle: "Please open this in your browser",
        inAppBrowserVideoBlockedBody: "Video sessions aren't supported inside this app's built-in browser. Copy the link below and open it in Safari or Chrome.",
        // Avertissement avant une session vidéo hors PWA (§5.3)
        videoStandaloneWarning: "The video will display smaller in your browser.",
        videoStandaloneWarningLink: "See install instructions",
    },
};
/**
 * Catalogue aplati en chemins pointés — la forme que consomment le moteur de
 * traduction, le stockage Mongo et les catalogues servis au client. `messages`
 * reste la source d'écriture, `sourceMessages` la source de lecture.
 */
export const sourceMessages = flattenMessages(messages);
//# sourceMappingURL=messages.js.map